import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Save, Plus, Pencil, Trash2, Globe, Box, Scale, Ruler, MapPin, Code, Copy, Loader2, X, Download, Upload, ArrowUp, ArrowDown, ArrowUpDown, Search, Package, Truck, Waypoints, AlertTriangle, ChevronDown, ChevronRight, Undo2, Weight, Archive} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';
import { PalletLaneCoveragePanel } from '../components/PalletLaneCoverage';

/** The postcode set of a zone in one line: counts first, then a few examples.
 *  Full list and a postcode lookup are on the Zones page (/zones). */
function ZonePostcodeSummary({ zone }) {
  const prefixes = (zone.postal_code_patterns || '').split(',').map(p => p.trim()).filter(Boolean);
  const ranges = zone.range_count || 0;
  const span = zone.postcode_span || 0;
  const parts = [];
  if (prefixes.length) parts.push(`${prefixes.length} prefix${prefixes.length > 1 ? 'es' : ''}`);
  if (ranges) parts.push(`${ranges.toLocaleString()} range${ranges > 1 ? 's' : ''}`);
  if (span) parts.push(`\u2248 ${span.toLocaleString()} postcodes`);
  const sample = prefixes.slice(0, 6);
  const rangeSample = (zone.range_sample || []).map(([a, b]) => (a === b ? `${a}` : `${a}\u2013${b}`));
  return (
    <div className="space-y-0.5">
      <div className="text-muted-foreground">{parts.join(' \u00b7 ') || '\u2014'}</div>
      {(sample.length > 0 || rangeSample.length > 0) && (
        <div className="font-mono text-[11px] truncate" title={zone.postal_code_patterns || ''}>
          {[...sample, ...rangeSample].join(', ')}
          {(prefixes.length > 6 || ranges > 3) ? ', \u2026' : ''}
        </div>
      )}
      {(prefixes.length > 6 || ranges > 0) && (
        <a href={`/zones?zone=${zone.id}`} className="text-[11px] text-sky-700 hover:underline">full list</a>
      )}
    </div>
  );
}

const getCountryFlag = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '';
  const codePoints = iso2.toUpperCase().split('').map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
};

// Brand assets live in public/carrier-logos/. Keyed by the carrier brand
// (logos belong to the brand, not the per-service variant).
const CARRIER_LOGOS = {
  dhl: '/carrier-logos/dhl.svg',
  gls: '/carrier-logos/gls.png',
  geodis: '/carrier-logos/geodis.png',
  bpost: '/carrier-logos/bpost.png',
  chronopost: '/carrier-logos/chronopost.png',
  ups: '/carrier-logos/ups.svg',
};

// Fallback monogram colours when a logo file is missing.
const CARRIER_COLORS = {
  dhl: { bg: '#FFCC00', fg: '#D40511' },
  gls: { bg: '#061AB1', fg: '#FFFFFF' },
  geodis: { bg: '#1A1A52', fg: '#FFFFFF' },
  bpost: { bg: '#E5097F', fg: '#FFFFFF' },
  chronopost: { bg: '#7AB800', fg: '#FFFFFF' },
  ups: { bg: '#351C15', fg: '#FFB500' },
};

// A carrier name can be a brand ("GLS") or a brand + variant ("GLS Belgium",
// "DHL Express Worldwide (Intl)", "UPS Standard"). Logos belong to the brand,
// so match the whole name first, then fall back to the first word.
const brandKey = (name) => {
  const n = (name || '').trim().toLowerCase();
  if (CARRIER_LOGOS[n]) return n;
  return n.split(/\s+/)[0];
};

// Small inline country flags (cross-platform: emoji flags don't render on
// Windows). Keyed by a country word appearing in the carrier name — used to
// tell same-brand entities apart (GLS Belgium vs GLS France).
const CARRIER_FLAGS = {
  belgium: ['#000000', '#FDDA24', '#EF3340'],
  france: ['#002395', '#FFFFFF', '#ED2939'],
  netherlands: ['#AE1C28', '#FFFFFF', '#21468B'],
};
const flagForName = (name) => {
  const n = (name || '').toLowerCase();
  for (const word of Object.keys(CARRIER_FLAGS)) if (n.includes(word)) return { word, bands: CARRIER_FLAGS[word] };
  return null;
};
const CountryFlag = ({ name, className = 'h-4' }) => {
  const f = flagForName(name);
  if (!f) return null;
  const horizontal = f.word === 'netherlands';
  const dir = horizontal ? 'to bottom' : 'to right';
  const stops = f.bands.map((c, i) => `${c} ${(i / f.bands.length) * 100}% ${((i + 1) / f.bands.length) * 100}%`).join(', ');
  return (
    <span
      title={f.word.charAt(0).toUpperCase() + f.word.slice(1)}
      className={`${className} inline-block aspect-[3/2] rounded-[2px] ring-1 ring-black/10 shrink-0`}
      style={{ background: `linear-gradient(${dir}, ${stops})` }}
    />
  );
};

// ISO2 -> emoji flag. Rate lanes already carry iso2_code, so this needs no
// name lookup (unlike countryFlag in the simulator). Returns '' for anything
// that is not two letters, and an unsupported platform just renders nothing
// rather than breaking the cell.
const isoFlag = (iso2) => {
  const c = (iso2 || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return String.fromCodePoint(...[...c].map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65));
};

// One country cell, used by every pallet-rate table. The ISO code lives in the
// title rather than beside the flag on purpose: Windows has no flag glyphs and
// falls back to the two regional-indicator LETTERS, so a visible code next to
// it would read "BE BE" there while looking fine on a Mac.
const CountryCell = ({ iso2, name }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={`${name} (${iso2 || '?'})`}>
    <span className="text-sm leading-none" aria-hidden>{isoFlag(iso2)}</span>
    <span>{name}</span>
  </span>
);

const CarrierLogo = ({ name, className = 'h-6' }) => {
  const key = brandKey(name);
  const src = CARRIER_LOGOS[key];
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        onError={() => setFailed(true)}
        className={`${className} w-auto max-w-[120px] object-contain rounded-sm bg-white p-0.5 shrink-0`}
      />
    );
  }
  const c = CARRIER_COLORS[key] || { bg: '#64748b', fg: '#ffffff' };
  return (
    <span
      className={`${className} inline-flex items-center justify-center rounded-sm px-2 text-xs font-bold tracking-wide shrink-0`}
      style={{ backgroundColor: c.bg, color: c.fg }}
      title={name}
    >
      {(name || '?').slice(0, 3).toUpperCase()}
    </span>
  );
};

// Carrier colour palette for chart lines — stable per carrier id.
const CHART_PALETTE = ['#2563eb', '#16a34a', '#dc2626', '#9333ea', '#ea580c', '#0891b2', '#db2777', '#65a30d', '#0284c7', '#a16207'];
const colorForCarrier = (cid) => CHART_PALETTE[Math.abs(Number(cid) || 0) % CHART_PALETTE.length];

// Lightweight responsive SVG line chart.
//  series = [{ label, color, data: [{x, y}, ...] }]
//  xUnit  = "kg" | "pkg"
const RateLineChart = ({ series, xUnit = 'kg', height = 260, xLabel }) => {
  const valid = (series || []).filter(s => s.data && s.data.length);
  if (!valid.length) {
    return <div className="text-xs text-muted-foreground italic py-8 text-center">No {xUnit === 'kg' ? 'rate' : 'parcel'} data</div>;
  }
  const xs = valid.flatMap(s => s.data.map(d => d.x));
  const ys = valid.flatMap(s => s.data.map(d => d.y));
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = 0;
  const yMax = Math.max(...ys) * 1.1 || 1;
  const W = 720, H = height, padL = 52, padR = 14, padT = 14, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const xScale = (x) => padL + (xMax === xMin ? innerW / 2 : (x - xMin) / (xMax - xMin) * innerW);
  const yScale = (y) => padT + (1 - (y - yMin) / (yMax - yMin)) * innerH;
  const yTickCount = 5;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => yMin + (yMax - yMin) * i / yTickCount);
  const xUnique = [...new Set(xs)].sort((a, b) => a - b);
  const stride = Math.max(1, Math.ceil(xUnique.length / 8));
  const xShown = xUnique.filter((_, i) => i % stride === 0 || i === xUnique.length - 1);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full text-foreground" preserveAspectRatio="xMidYMid meet" role="img">
      {yTicks.map((y, i) => (
        <line key={`yg${i}`} x1={padL} x2={W - padR} y1={yScale(y)} y2={yScale(y)} stroke="currentColor" strokeOpacity="0.08" />
      ))}
      {yTicks.map((y, i) => (
        <text key={`yl${i}`} x={padL - 8} y={yScale(y) + 3} textAnchor="end" fontSize="10" className="fill-muted-foreground">€{y.toFixed(y >= 10 ? 0 : 1)}</text>
      ))}
      {xShown.map((x) => (
        <text key={`xl${x}`} x={xScale(x)} y={H - padB + 14} textAnchor="middle" fontSize="10" className="fill-muted-foreground">{x}</text>
      ))}
      <text x={padL + innerW / 2} y={H - 4} textAnchor="middle" fontSize="10" className="fill-muted-foreground">{xLabel || (xUnit === 'kg' ? 'weight (kg)' : 'packages')}</text>
      <line x1={padL} x2={padL} y1={padT} y2={padT + innerH} stroke="currentColor" strokeOpacity="0.25" />
      <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="currentColor" strokeOpacity="0.25" />
      {valid.map((s, i) => (
        <g key={i}>
          <polyline fill="none" stroke={s.color} strokeWidth={s.width || 2} strokeDasharray={s.dash || undefined}
            points={s.data.map(d => `${xScale(d.x)},${yScale(d.y)}`).join(' ')} />
          {s.data.map((d, j) => (
            <circle key={j} cx={xScale(d.x)} cy={yScale(d.y)} r="2.5" fill={s.color}>
              <title>{s.label}: {d.x}{xUnit === 'kg' ? ' kg' : ' pkg'} → €{d.y.toFixed(2)}</title>
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
};

const tabGroups = [
  { label: 'Destinations', tabs: [
    { id: 0, label: 'Countries', icon: Globe },
    { id: 1, label: 'Zones', icon: MapPin },
  ]},
  { label: 'Packaging', tabs: [
    { id: 2, label: 'Bins', icon: Box },
    { id: 8, label: 'Carriers', icon: Waypoints },
  ]},
  { label: 'Rates', tabs: [
    { id: 3, label: 'Weight', icon: Scale },
    { id: 4, label: 'Volumetric', icon: Ruler },
    { id: 5, label: 'Per-Parcel', icon: Package },
    // The per-size Pallet table is superseded: since 2026-09-16 pallets price on
    // the carrier cards in the Pallet Rates tab (PALLET_BAND_MODE = 'replace').
    // It stays visible because it is still the fallback for a lane with no card,
    // the anchor for the wide-base rule, and the home of the pallet fuel rates.
    { id: 12, label: 'Pallet Rates', icon: Weight },
    // The old per-size table: superseded by the cards above, but still the
    // fallback for a carrier with no card on a lane, so it stays visible and
    // editable (2026-10-02: its rows priced DHL Express Worldwide (Pallet) at
    // EUR 1,000-3,500 per size and nobody could see them).
    { id: 6, label: 'Pallet (old per-size)', icon: Archive },
    { id: 10, label: 'Pickup', icon: MapPin },
    { id: 11, label: 'Returns', icon: Undo2 },
    { id: 9, label: 'Surcharges', icon: AlertTriangle },
  ]},
  { label: 'Developer', tabs: [
    { id: 7, label: 'API', icon: Code },
  ]},
];

const Modal = ({ open, onClose, title, children, footer, size = 'lg' }) => {
  if (!open) return null;
  const sizeClass = size === '5xl' ? 'max-w-5xl' : 'max-w-lg';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative z-50 w-full ${sizeClass} overflow-hidden rounded-xl border border-border/70 bg-card shadow-2xl max-h-[90vh]`}>
        {/* Brand accent strip — ties modal to the rest of the brand language */}
        <div className="h-[3px] bg-gradient-to-r from-brand via-brand-2 to-brand/40" aria-hidden />
        <div className="px-6 py-5 max-h-[calc(90vh-3px)] overflow-y-auto">
          <div className="flex items-start justify-between mb-4 gap-4">
            <h2 className="text-lg font-semibold leading-tight">{title}</h2>
            <button onClick={onClose} className="rounded-md p-1 hover:bg-accent transition-colors -mt-0.5">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border/60">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

// Compact isometric 3D box visualization
const BoxViz = ({ w, h, d, size = 36 }) => {
  const cos30 = Math.cos(Math.PI / 6);
  const sin30 = Math.sin(Math.PI / 6);

  // Compute the bounding extent of a unit isometric box to find the right scale
  // In isometric: width goes up-left, depth goes up-right, height goes up
  const rawW = w || 1, rawH = h || 1, rawD = d || 1;

  // Total horizontal span = w*cos30 + d*cos30
  const totalX = rawW * cos30 + rawD * cos30;
  // Total vertical span = (w + d)*sin30 + h  (bottom rhombus depth + height)
  const totalY = (rawW + rawD) * sin30 + rawH;

  const padding = 2;
  const available = size - padding * 2;
  const scale = Math.min(available / totalX, available / totalY);

  const sw = rawW * scale;
  const sh = rawH * scale;
  const sd = rawD * scale;

  // Origin = front bottom vertex (lowest point), positioned to center the box in the SVG
  const boxW = (sw + sd) * cos30;
  const boxH = (sw + sd) * sin30 + sh;
  const ox = padding + sw * cos30 + (available - boxW) / 2;
  const oy = padding + (available - boxH) / 2 + boxH;

  // 8 corners of the isometric box relative to origin
  // Bottom face: origin, left, back, right (edges recede upward on screen)
  const B0 = [ox, oy];                                                    // front
  const B1 = [ox - sw * cos30, oy - sw * sin30];                          // left
  const B2 = [ox - sw * cos30 + sd * cos30, oy - sw * sin30 - sd * sin30];// back
  const B3 = [ox + sd * cos30, oy - sd * sin30];                          // right
  // Top face: same but shifted up by sh
  const T0 = [ox, oy - sh];
  const T1 = [ox - sw * cos30, oy - sw * sin30 - sh];
  const T2 = [ox - sw * cos30 + sd * cos30, oy - sw * sin30 - sd * sin30 - sh];
  const T3 = [ox + sd * cos30, oy - sd * sin30 - sh];

  const pts = (arr) => arr.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      {/* Left face */}
      <polygon points={pts([B0, B1, T1, T0])} fill="currentColor" opacity="0.18" stroke="currentColor" strokeWidth="0.7" strokeOpacity="0.5" />
      {/* Right face */}
      <polygon points={pts([B0, B3, T3, T0])} fill="currentColor" opacity="0.10" stroke="currentColor" strokeWidth="0.7" strokeOpacity="0.5" />
      {/* Top face */}
      <polygon points={pts([T0, T1, T2, T3])} fill="currentColor" opacity="0.25" stroke="currentColor" strokeWidth="0.7" strokeOpacity="0.5" />
    </svg>
  );
};

const ShippingRates = () => {
  // Deep links from the Pricing Rules page: ?tab=9 opens a tab, &rule=224 opens
  // that surcharge rule's dialog once the rules are loaded.
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(searchParams.get('tab') || '', 10);
    return Number.isFinite(t) ? t : 0;
  });
  const deepLinkRule = useRef(searchParams.get('rule'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [countries, setCountries] = useState([]);
  const [bins, setBins] = useState([]);
  const [weightRates, setWeightRates] = useState([]);
  const [volumetricRates, setVolumetricRates] = useState([]);
  const [coliRates, setColiRates] = useState([]);
  const [zones, setZones] = useState([]);
  const [palletBins, setPalletBins] = useState([]);
  const [palletRates, setPalletRates] = useState([]);
  const [freightRates, setFreightRates] = useState([]);   // pallets priced by weight (messagerie bands)
  // Pallet Rates tab: dedicated pallet cards priced on weight bands. One screen,
  // two views: 'volumetric' = the carrier pallet grids on CHARGEABLE weight
  // (Jeremy's 50 kg grid), 'real' = the messagerie bands on real weight.
  const [palletBandBasis, setPalletBandBasis] = useState('volumetric');
  const [palletBandData, setPalletBandData] = useState(null);
  const [palletBandLoading, setPalletBandLoading] = useState(false);
  const [palletBandFilter, setPalletBandFilter] = useState('');
  const palletBandFileRef = useRef(null);
  // Test a destination: which lane does this postcode resolve to, per carrier?
  const [palletBandProbe, setPalletBandProbe] = useState('');
  const [palletBandProbeWeight, setPalletBandProbeWeight] = useState('');
  const [palletLaneKey, setPalletLaneKey] = useState(null);
  const [freightPalletMode, setFreightPalletMode] = useState(null);
  // Per destination: which carrier prices PALLETS by weight (freight bands) and
  // on how many zones. Drives the "how a pallet is priced" panel and the
  // "by weight" badge in the Pallet tab (Geoffrey 2026-09-16: it was not clear
  // in the UI which rates a pallet to France is priced on).
  const freightByCountry = (() => {
    const out = {};
    freightRates.forEach(fr => {
      const c = out[fr.country_id] = out[fr.country_id] || { country_name: fr.country_name, iso2_code: fr.iso2_code, carriers: {} };
      const k = c.carriers[fr.carrier_name] = c.carriers[fr.carrier_name] || { zones: 0, countries: 0, min_kg: Infinity, max_kg: 0 };
      if (fr.scope === 'zone') k.zones += 1; else k.countries += 1;
      (fr.bands || []).forEach(b => { k.min_kg = Math.min(k.min_kg, b.min_kg); k.max_kg = Math.max(k.max_kg, b.max_kg); });
    });
    return out;
  })();
  const [modifiedPalletRates, setModifiedPalletRates] = useState({});
  const [palletRateFilter, setPalletRateFilter] = useState('');

  // Pickup tab (GLS ParcelShop / Locker): per-country weight-band rates + profiles
  const [pickupRates, setPickupRates] = useState([]);
  // Countries that may offer BOTH pickup networks at once (PickupConfig
  // PICKUP_MULTI_NETWORK_COUNTRIES). There the Active toggle ADDS a carrier
  // instead of replacing the live one.
  const [pickupMultiCountries, setPickupMultiCountries] = useState([]);
  const [pickupProfiles, setPickupProfiles] = useState([]);
  const [modifiedPickupRates, setModifiedPickupRates] = useState({});
  const [modifiedPickupProfiles, setModifiedPickupProfiles] = useState({});
  // Fuel per pickup CARRIER x country (the figure that actually applies), keyed country_id_carrier_id.
  const [modifiedPickupCarrierFuel, setModifiedPickupCarrierFuel] = useState({});
  const [modifiedPickupOdoo, setModifiedPickupOdoo] = useState({});
  const [modifiedPickupFuel, setModifiedPickupFuel] = useState({});
  const [pickupRateFilter, setPickupRateFilter] = useState('');

  // Returns tab (GLS / bpost return rates): per-country weight-band rates
  const [returnRates, setReturnRates] = useState([]);
  const [modifiedReturnRates, setModifiedReturnRates] = useState({});
  const [returnRateFilter, setReturnRateFilter] = useState('');

  // Search filters
  const [countryFilter, setCountryFilter] = useState('');
  const [binFilter, setBinFilter] = useState('');
  const [weightRateFilter, setWeightRateFilter] = useState('');
  const [volRateFilter, setVolRateFilter] = useState('');

  // Sorting for countries table
  const [countrySortField, setCountrySortField] = useState('name');
  const [countrySortDir, setCountrySortDir] = useState('asc');
  // Feature filters for countries table (AND semantics: country must match all active)
  const [countryFeatureFilters, setCountryFeatureFilters] = useState(() => new Set());

  const [editingCountry, setEditingCountry] = useState(null);
  const [editingBin, setEditingBin] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [binDialogOpen, setBinDialogOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);

  const [modifiedWeightRates, setModifiedWeightRates] = useState({});
  const [modifiedVolumetricRates, setModifiedVolumetricRates] = useState({});
  const [modifiedZoneWeightRates, setModifiedZoneWeightRates] = useState({});
  const [modifiedZoneVolumetricRates, setModifiedZoneVolumetricRates] = useState({});
  const [modifiedZonePalletRates, setModifiedZonePalletRates] = useState({});
  const [modifiedColiRates, setModifiedColiRates] = useState({});
  const [modifiedFuelSurcharges, setModifiedFuelSurcharges] = useState({});
  // Which extra-cost chip is expanded for editing: `${rateKey}-${rateType}` or null.
  const [editingExtra, setEditingExtra] = useState(null);

  // Carriers
  const [carriers, setCarriers] = useState([]);
  const [carrierAssignments, setCarrierAssignments] = useState([]);
  const [editingCarrier, setEditingCarrier] = useState(null);
  const [carrierDialogOpen, setCarrierDialogOpen] = useState(false);
  const [carrierFilter, setCarrierFilter] = useState('');
  const [expandedCarrierCountries, setExpandedCarrierCountries] = useState({});
  // Carrier usage stats — how often each carrier won, over a period
  const [carrierUsagePeriod, setCarrierUsagePeriod] = useState('30');
  const [carrierUsage, setCarrierUsage] = useState({ totals: [], total_shipments: 0 });
  const [carrierUsageLoading, setCarrierUsageLoading] = useState(false);
  const [countryDetailId, setCountryDetailId] = useState(null);
  const [pendingAssignmentChanges, setPendingAssignmentChanges] = useState({});

  // Per-carrier zone rows are collapsed by default (set holds the EXPANDED keys)
  const [expandedZoneRows, setExpandedZoneRows] = useState(() => new Set());
  const toggleZoneRows = (key) => setExpandedZoneRows((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Add carrier rates modal
  const [addRateModalOpen, setAddRateModalOpen] = useState(false);
  const [addRateType, setAddRateType] = useState(null); // 'weight' | 'volumetric' | 'coli' | 'pallet'
  const [addRateCountryId, setAddRateCountryId] = useState('');
  const [addRateCarrierId, setAddRateCarrierId] = useState('');

  // Surcharges tab
  const [surchargeRules, setSurchargeRules] = useState([]);
  const [surchargeCarrierCountries, setSurchargeCarrierCountries] = useState({});
  const [surchargeCarrierFilter, setSurchargeCarrierFilter] = useState('');
  // Which side of the fence to look at: parcel rules, pallet rules, or both.
  const [surchargeAppliesFilter, setSurchargeAppliesFilter] = useState('all');
  const [surchargeDialogOpen, setSurchargeDialogOpen] = useState(false);
  const [editingSurcharge, setEditingSurcharge] = useState(null);
  const [expandedSurchargeCarrier, setExpandedSurchargeCarrier] = useState(null);

  const weightFileRef = useRef(null);
  const volumetricFileRef = useRef(null);
  const coliFileRef = useRef(null);
  const palletFileRef = useRef(null);

  const handleExcelExport = async (type) => {
    try {
      const response = await axiosInstance.get(`api/shipping-rates/${type}/excel/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_rates.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Failed to export');
    }
  };

  const handleExcelImport = async (type, file) => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(`api/shipping-rates/${type}/excel/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess(response.data.message);
        fetchData();
      } else {
        setError(response.data.error || 'Import failed');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to import');
    } finally {
      setSaving(false);
    }
  };

  const [reloadingCache, setReloadingCache] = useState(false);
  const [cacheReloadMsg, setCacheReloadMsg] = useState('');
  const handleReloadCache = async () => {
    setReloadingCache(true);
    setCacheReloadMsg('');
    try {
      const res = await axiosInstance.post('api/shipping-rates/reload-cache/');
      setCacheReloadMsg(res.data?.message || 'Rate cache flushed — changes are now live.');
    } catch (err) {
      setCacheReloadMsg(err.response?.data?.message || 'Failed to flush rate cache.');
    } finally {
      setReloadingCache(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('api/shipping-rates/');
      if (response.data.success) {
        setCountries(response.data.countries);
        setBins(response.data.bins);
        setWeightRates(response.data.weight_rates);
        setVolumetricRates(response.data.volumetric_rates);
        setColiRates(response.data.coli_rates || []);
        setPalletBins(response.data.pallet_bins || []);
        setPalletRates(response.data.pallet_rates || []);
        setFreightRates(response.data.freight_rates || []);
        setFreightPalletMode(response.data.freight_pallet_mode || null);
        setCarriers(response.data.carriers || []);
        setCarrierAssignments(response.data.carrier_assignments || []);
        const [zonesResponse, surchargeResponse, pickupRatesResponse, pickupProfilesResponse, returnRatesResponse] = await Promise.all([
          axiosInstance.get('api/shipping-rates/zones/'),
          axiosInstance.get('api/carriers/surcharge-rules/'),
          axiosInstance.get('api/shipping-rates/pickup/'),
          axiosInstance.get('api/shipping-rates/pickup-profiles/'),
          axiosInstance.get('api/shipping-rates/returns/'),
        ]);
        if (surchargeResponse.data.success) {
          setSurchargeRules(surchargeResponse.data.rules || []);
          if (deepLinkRule.current) {
            const wanted = (surchargeResponse.data.rules || []).find(r => String(r.id) === String(deepLinkRule.current));
            deepLinkRule.current = null;
            if (wanted) { setEditingSurcharge(wanted); setSurchargeDialogOpen(true); }
          }
          setSurchargeCarrierCountries(surchargeResponse.data.carrier_countries || {});
        }
        if (zonesResponse.data.success) {
          setZones(zonesResponse.data.zones);
        }
        if (pickupRatesResponse.data.success) {
          setPickupRates(pickupRatesResponse.data.pickup_rates || []);
          setPickupMultiCountries(pickupRatesResponse.data.multi_network_countries || []);
        }
        if (pickupProfilesResponse.data.success) {
          setPickupProfiles(pickupProfilesResponse.data.pickup_profiles || []);
        }
        if (returnRatesResponse.data.success) {
          setReturnRates(returnRatesResponse.data.return_rates || []);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Carrier usage — only fetch while the Carriers tab is selected
  const fetchCarrierUsage = useCallback(async (period) => {
    setCarrierUsageLoading(true);
    try {
      const params = {};
      if (period === 'wtd' || period === 'mtd' || period === 'ytd') {
        const now = new Date();
        let d;
        if (period === 'wtd') {
          const dow = (now.getDay() + 6) % 7; // Mon=0
          d = new Date(now); d.setDate(now.getDate() - dow);
        } else if (period === 'mtd') {
          d = new Date(now.getFullYear(), now.getMonth(), 1);
        } else {
          d = new Date(now.getFullYear(), 0, 1);
        }
        params.start_date = d.toISOString().slice(0, 10);
      } else if (period && period !== '0') {
        params.days = period;
      }
      const res = await axiosInstance.get('api/shipping-rates/carrier-usage/', { params });
      setCarrierUsage(res.data || { totals: [], total_shipments: 0 });
    } catch {
      setCarrierUsage({ totals: [], total_shipments: 0 });
    } finally {
      setCarrierUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 8) fetchCarrierUsage(carrierUsagePeriod);
  }, [tabValue, carrierUsagePeriod, fetchCarrierUsage]);

  // Country handlers
  const handleCountrySave = async () => {
    if (!editingCountry) return;
    setSaving(true);
    try {
      if (editingCountry.id) {
        await axiosInstance.put('api/shipping-rates/countries/', editingCountry);
      } else {
        await axiosInstance.post('api/shipping-rates/countries/', editingCountry);
      }
      setSuccess('Country saved successfully');
      setCountryDialogOpen(false);
      setEditingCountry(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCountryDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this country?')) return;
    try {
      await axiosInstance.delete(`api/shipping-rates/countries/?id=${id}`);
      setSuccess('Country deleted');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  // Bin handlers
  const handleBinSave = async () => {
    if (!editingBin) return;
    setSaving(true);
    try {
      if (editingBin.id) {
        await axiosInstance.put('api/shipping-rates/bins/', editingBin);
      } else {
        await axiosInstance.post('api/shipping-rates/bins/', editingBin);
      }
      setSuccess('Bin saved successfully');
      setBinDialogOpen(false);
      setEditingBin(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Carrier CRUD
  const handleCarrierSave = async () => {
    if (!editingCarrier) return;
    setSaving(true);
    try {
      if (editingCarrier.id) {
        await axiosInstance.put('api/carriers/', editingCarrier);
      } else {
        await axiosInstance.post('api/carriers/', editingCarrier);
      }
      setSuccess('Carrier saved');
      setCarrierDialogOpen(false);
      setEditingCarrier(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save carrier');
    } finally {
      setSaving(false);
    }
  };

  const handleCarrierDelete = async (id) => {
    if (!confirm('Delete this carrier? Any country assignments using it will also be removed.')) return;
    try {
      await axiosInstance.delete(`api/carriers/?id=${id}`);
      setSuccess('Carrier deleted');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handleAssignmentChange = (countryId, rateType, carrierId) => {
    const key = `${countryId}-${rateType}`;
    // Check if this matches the server state — if so, remove from pending
    const serverAssignment = carrierAssignments.find(a => a.country_id === countryId && a.rate_type === rateType);
    const serverValue = serverAssignment ? serverAssignment.carrier_id : null;
    const newValue = carrierId ? parseInt(carrierId) : null;
    if (newValue === serverValue) {
      setPendingAssignmentChanges(prev => { const s = { ...prev }; delete s[key]; return s; });
    } else {
      setPendingAssignmentChanges(prev => ({ ...prev, [key]: newValue }));
    }
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      for (const [key, carrierId] of Object.entries(pendingAssignmentChanges)) {
        const [countryId, rateType] = key.split('-');
        if (carrierId) {
          await axiosInstance.put('api/carriers/assignments/', { country_id: parseInt(countryId), rate_type: rateType, carrier_id: carrierId });
        } else {
          const existing = carrierAssignments.find(a => a.country_id === parseInt(countryId) && a.rate_type === rateType);
          if (existing) await axiosInstance.delete(`api/carriers/assignments/?id=${existing.id}`);
        }
      }
      setPendingAssignmentChanges({});
      setSuccess('Carrier assignments saved');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  // Helper to get carrier assignment for a country + rate type
  const getCarrierForCountry = (countryId, rateType) => {
    const a = carrierAssignments.find(x => x.country_id === countryId && x.rate_type === rateType);
    if (!a) return null;
    return carriers.find(c => c.id === a.carrier_id) || null;
  };

  // Surcharge rule CRUD
  const CONDITION_OPTIONS = [
    { value: 'always', label: 'Always (every parcel)' },
    { value: 'longest_side', label: 'Longest side (cm)' },
    { value: 'second_side', label: '2nd side (cm)' },
    { value: 'third_side', label: 'Shortest side (cm)' },
    { value: 'any_side', label: 'Any side (cm)' },
    { value: 'sum_sides', label: 'L+W+H (cm)' },
    { value: 'girth', label: 'Girth L+2(W+H)' },
    { value: 'weight', label: 'Weight (kg)' },
    { value: 'volume_weight', label: 'Vol. weight (kg)' },
    { value: 'base_width', label: 'Pallet base, short side (cm)' },
    { value: 'base_length', label: 'Pallet base, long side (cm)' },
    { value: 'height', label: 'Pallet height (cm)' },
  ];
  const OPERATOR_OPTIONS = [
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
  ];
  const OUTCOME_OPTIONS = [
    { value: 'flat', label: 'Flat (€/parcel)' },
    { value: 'flat_shipment', label: 'Flat (€/shipment, once per consignment)' },
    { value: 'per_kg', label: 'Per-kg (€/kg)' },
    { value: 'refused', label: 'Refused' },
  ];

  const handleSurchargeSave = async () => {
    if (!editingSurcharge) return;
    setSaving(true);
    try {
      if (editingSurcharge.id) {
        await axiosInstance.put('api/carriers/surcharge-rules/', editingSurcharge);
      } else {
        await axiosInstance.post('api/carriers/surcharge-rules/', editingSurcharge);
      }
      setSuccess('Surcharge rule saved');
      setSurchargeDialogOpen(false);
      setEditingSurcharge(null);
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save surcharge rule');
    } finally {
      setSaving(false);
    }
  };

  const handleSurchargeDelete = async (id) => {
    if (!window.confirm('Delete this surcharge rule?')) return;
    try {
      await axiosInstance.delete(`api/carriers/surcharge-rules/?id=${id}`);
      setSuccess('Surcharge rule deleted');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const openNewSurchargeRule = (carrierId) => {
    setEditingSurcharge({
      carrier_id: carrierId,
      country_codes: '',
      surcharge_group: 'default',
      condition: 'longest_side',
      operator: 'gt',
      threshold: 0,
      outcome: 'flat',
      amount: 0,
      label: '',
      note: '',
      applies_to: 'parcel',
      weight_basis: 'real',
      valid_from: null,
      valid_until: null,
    });
    setSurchargeDialogOpen(true);
  };

  // Composite key for rate entries: "countryId_carrierId" (carrierId may be null)
  const rateKey = (cr) => `${cr.country_id}_${cr.carrier_id || ''}`;

  // Edit rate row modal state
  const [editRateModalOpen, setEditRateModalOpen] = useState(false);
  const [editRateRow, setEditRateRow] = useState(null); // { rateType, country_id, carrier_id, ... }
  const [editRateCarrierId, setEditRateCarrierId] = useState('');

  const openEditRateModal = (cr, rateType) => {
    setEditRateRow({ ...cr, rateType });
    setEditRateCarrierId(cr.carrier_id?.toString() || '');
    setEditRateModalOpen(true);
  };

  const confirmEditRate = async () => {
    if (!editRateRow || !editRateCarrierId) return;
    const newCarrierId = parseInt(editRateCarrierId);
    if (newCarrierId === editRateRow.carrier_id) { setEditRateModalOpen(false); return; }
    try {
      await axiosInstance.post('api/shipping-rates/reassign-carrier/', {
        country_id: editRateRow.country_id,
        old_carrier_id: editRateRow.carrier_id,
        new_carrier_id: newCarrierId,
        rate_type: editRateRow.rateType,
      });
      fetchRates();
      setEditRateModalOpen(false);
    } catch (err) {
      console.error('Failed to reassign carrier:', err);
    }
  };

  // Helper: compute country grouping counts from a sorted rates array
  const countryGroupCounts = (sortedRates) => {
    const counts = {};
    sortedRates.forEach(cr => { counts[cr.country_id] = (counts[cr.country_id] || 0) + 1; });
    return counts;
  };

  // Helper: render country cell — grouped header or single row
  const renderCountryCell = (cr, isMultiCarrier, rateType, editButton = true) => {
    if (isMultiCarrier) {
      return (
        <>
          <span className="ml-3 text-[10px] rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 font-medium">{cr.carrier_name}</span>
          {['weight', 'volumetric', 'coli', 'pallet'].includes(rateType) && !laneActive(cr, rateType) && <span className="ml-1 text-[10px] rounded-full bg-red-600 text-white px-1.5 py-0.5 font-semibold" title={laneFor(cr, rateType)?.disabled_note || 'switched off'}>switched off</span>}
          {editButton && <button onClick={() => openEditRateModal(cr, rateType)} className="ml-1 inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"><Pencil className="h-2.5 w-2.5" /></button>}
        </>
      );
    }
    return (
      <>
        <span className="mr-1 text-base">{getCountryFlag(cr.iso2_code)}</span>
        <span className="font-bold text-xs mr-1">{cr.iso2_code}</span>
        <span className="text-xs">{cr.country_name}</span>
        {cr.carrier_name && <span className="ml-1 text-[10px] rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 font-medium">{cr.carrier_name}</span>}
        {['weight', 'volumetric', 'coli', 'pallet'].includes(rateType) && !laneActive(cr, rateType) && <span className="ml-1 text-[10px] rounded-full bg-red-600 text-white px-1.5 py-0.5 font-semibold" title={laneFor(cr, rateType)?.disabled_note || 'switched off'}>switched off</span>}
        {editButton && cr.carrier_name && <button onClick={() => openEditRateModal(cr, rateType)} className="ml-1 inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"><Pencil className="h-2.5 w-2.5" /></button>}
      </>
    );
  };

  // Helper: render group header row for multi-carrier countries
  const renderGroupHeader = (cr, count, colSpan = 32) => (
    <tr className="bg-muted/40">
      <td colSpan={colSpan} className="sticky left-0 z-[1] px-3 py-1 font-semibold text-xs bg-muted/40">
        <span className="mr-1 text-base">{getCountryFlag(cr.iso2_code)}</span>
        <span className="font-bold mr-1">{cr.iso2_code}</span>
        <span>{cr.country_name}</span>
        <span className="ml-2 text-muted-foreground font-normal">({count} carriers)</span>
      </td>
    </tr>
  );

  const openAddRateModal = (rateType) => {
    setAddRateType(rateType);
    setAddRateCountryId('');
    setAddRateCarrierId('');
    setAddRateModalOpen(true);
  };

  const confirmAddRate = () => {
    const c = countries.find(x => x.id === parseInt(addRateCountryId));
    const carr = carriers.find(x => x.id === parseInt(addRateCarrierId));
    if (!c || !carr) return;
    // Check for duplicates
    const rateMap = { weight: weightRates, volumetric: volumetricRates, coli: coliRates, pallet: palletRates };
    const existing = (rateMap[addRateType] || []).find(cr => cr.country_id === c.id && cr.carrier_id === carr.id);
    if (existing) return;
    const carrName = carr.name + (carr.service ? ` (${carr.service})` : '');
    const newEntry = { country_id: c.id, country_name: c.name, iso2_code: c.iso2_code, carrier_id: carr.id, carrier_name: carrName, rates: {} };
    if (addRateType === 'weight') {
      newEntry.fuel_surcharge_weight = 0;
      setWeightRates(prev => [...prev, newEntry]);
    } else if (addRateType === 'volumetric') {
      newEntry.fuel_surcharge_volumetric = 0;
      setVolumetricRates(prev => [...prev, newEntry]);
    } else if (addRateType === 'coli') {
      newEntry.fuel_surcharge_coli = 0;
      setColiRates(prev => [...prev, newEntry]);
    } else if (addRateType === 'pallet') {
      newEntry.fuel_surcharge_pallet = 0;
      setPalletRates(prev => [...prev, newEntry]);
    }
    setAddRateModalOpen(false);
  };

  // Fuel surcharge handlers — saved per carrier assignment. Fuel is a PERCENTAGE
  // by default (input in %, stored as fraction); a lane can instead bill a FIXED
  // EUR amount per parcel (fuel_is_fixed), where the input is euros stored as-is.
  const assignmentFuelIsFixed = (cr, rateType) => {
    const a = carrierAssignments.find(x =>
      x.country_id === cr.country_id && x.carrier_id === cr.carrier_id && x.rate_type === rateType);
    return !!(a && a.fuel_is_fixed);
  };
  // Extra regulatory percentage on the base rate (e.g. GLS Belgium +2.50%),
  // applied as base × (1 + pct) BEFORE fuel. Stored as a fraction on the
  // assignment (CountryCarrier.surcharge_pct); 0 for the vast majority of lanes.
  const assignmentSurchargePct = (cr, rateType) => {
    const a = carrierAssignments.find(x =>
      x.country_id === cr.country_id && x.carrier_id === cr.carrier_id && x.rate_type === rateType);
    return (a && a.surcharge_pct) || 0;
  };
  const fuelMode = (cr, rateType) => {
    const key = rateKey(cr);
    const mod = modifiedFuelSurcharges[key];
    return (mod && 'fuel_is_fixed' in mod) ? mod.fuel_is_fixed : assignmentFuelIsFixed(cr, rateType);
  };
  const handleFuelSurchargeChange = (cr, value, rateType) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    const key = rateKey(cr);
    const isFixed = fuelMode(cr, rateType);
    setModifiedFuelSurcharges(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), country_id: cr.country_id, carrier_id: cr.carrier_id, rate_type: rateType,
               fuel_surcharge: isFixed ? numValue : numValue / 100, fuel_is_fixed: isFixed },
    }));
  };
  const toggleFuelMode = (cr, rateType) => {
    const key = rateKey(cr);
    const nowFixed = !fuelMode(cr, rateType);
    // Keep the raw stored value; only the unit/meaning flips.
    const rawStored = modifiedFuelSurcharges[key]?.fuel_surcharge ?? (cr[`fuel_surcharge_${rateType}`] || 0);
    setModifiedFuelSurcharges(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), country_id: cr.country_id, carrier_id: cr.carrier_id, rate_type: rateType,
               fuel_surcharge: rawStored, fuel_is_fixed: nowFixed },
    }));
  };

  // Extra cost on the base rate (regulatory / any surcharge). Percentage or a
  // fixed EUR/parcel amount, applied to the base BEFORE fuel. Edits are staged
  // into the same modifiedFuelSurcharges entry so the "Save All" button persists
  // them via the carrier-assignment PUT (surcharge_pct + surcharge_is_fixed).
  const assignmentSurchargeIsFixed = (cr, rateType) => {
    const a = carrierAssignments.find(x =>
      x.country_id === cr.country_id && x.carrier_id === cr.carrier_id && x.rate_type === rateType);
    return !!(a && a.surcharge_is_fixed);
  };
  const extraIsFixed = (cr, rateType) => {
    const m = modifiedFuelSurcharges[rateKey(cr)];
    return (m && 'surcharge_is_fixed' in m) ? m.surcharge_is_fixed : assignmentSurchargeIsFixed(cr, rateType);
  };
  const extraRaw = (cr, rateType) => {           // raw stored value: fraction (%) or EUR
    const m = modifiedFuelSurcharges[rateKey(cr)];
    return (m && 'surcharge_pct' in m) ? m.surcharge_pct : assignmentSurchargePct(cr, rateType);
  };
  const handleExtraChange = (cr, value, rateType) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    const key = rateKey(cr);
    const isFixed = extraIsFixed(cr, rateType);
    setModifiedFuelSurcharges(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), country_id: cr.country_id, carrier_id: cr.carrier_id, rate_type: rateType,
               surcharge_pct: isFixed ? numValue : numValue / 100, surcharge_is_fixed: isFixed },
    }));
  };
  const toggleExtraFixed = (cr, rateType) => {
    const key = rateKey(cr);
    const nowFixed = !extraIsFixed(cr, rateType);
    const rawStored = extraRaw(cr, rateType) || 0;
    setModifiedFuelSurcharges(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), country_id: cr.country_id, carrier_id: cr.carrier_id, rate_type: rateType,
               surcharge_pct: rawStored, surcharge_is_fixed: nowFixed },
    }));
  };
  // One fuel cell: number input (% or €) + a small unit toggle.
  const renderFuelCell = (cr, rateType) => {
    const key = rateKey(cr);
    const isFixed = fuelMode(cr, rateType);
    const raw = modifiedFuelSurcharges[key]?.fuel_surcharge ?? (cr[`fuel_surcharge_${rateType}`] || 0);
    const shown = isFixed ? raw : Math.round(raw * 100);
    const dirty = !!modifiedFuelSurcharges[key];
    return (
      <td className="px-0.5 py-0.5">
        <div className="flex items-center gap-0.5">
          <input
            key={`${key}-${rateType}-${isFixed ? 'eur' : 'pct'}`}
            type="number" step={isFixed ? '0.01' : '1'}
            defaultValue={shown}
            onChange={(e) => handleFuelSurchargeChange(cr, e.target.value, rateType)}
            className={`w-12 rounded border px-1.5 py-1 text-right text-xs font-semibold ${dirty ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/15'}`}
          />
          <button type="button" onClick={() => toggleFuelMode(cr, rateType)}
            title={isFixed ? 'Fixed €/parcel — click for %' : 'Percentage — click for fixed €/parcel'}
            className="w-5 shrink-0 rounded border border-border text-[11px] font-bold text-muted-foreground hover:bg-muted">
            {isFixed ? '€' : '%'}
          </button>
          {renderExtraChip(cr, rateType)}
        </div>
      </td>
    );
  };

  // Compact, click-to-edit "extra cost" on the base rate (regulatory or any
  // per-lane surcharge). Collapsed: a small chip showing the value (or a faint
  // "+" to add one). Expanded: value input + %/€ unit toggle. Edits stage into
  // modifiedFuelSurcharges and persist with "Save All". Needs a real carrier
  // assignment (extra cost is stored on CountryCarrier).
  const renderExtraChip = (cr, rateType) => {
    if (!cr.carrier_id) return null;
    const key = rateKey(cr);
    const editKey = `${key}-${rateType}`;
    const isFixed = extraIsFixed(cr, rateType);
    const val = extraRaw(cr, rateType) || 0;
    const dirty = !!(modifiedFuelSurcharges[key] && 'surcharge_pct' in modifiedFuelSurcharges[key]);
    if (editingExtra === editKey) {
      const shown = isFixed ? val : +(val * 100).toFixed(3);
      return (
        <span className="ml-0.5 inline-flex items-center gap-0.5">
          <input
            key={`${editKey}-${isFixed ? 'eur' : 'pct'}`}
            type="number" step="0.01" autoFocus
            defaultValue={shown}
            onChange={(e) => handleExtraChange(cr, e.target.value, rateType)}
            onBlur={() => setEditingExtra(null)}
            onKeyDown={(e) => { if (e.key === 'Enter') setEditingExtra(null); }}
            className={`w-11 rounded border px-1 py-1 text-right text-xs font-semibold ${dirty ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-blue-300 bg-blue-50/60 dark:bg-blue-900/15'}`}
          />
          <button type="button" onMouseDown={(e) => { e.preventDefault(); toggleExtraFixed(cr, rateType); }}
            title={isFixed ? 'Fixed €/parcel — click for %' : 'Percentage of base — click for fixed €/parcel'}
            className="w-5 shrink-0 rounded border border-border text-[11px] font-bold text-muted-foreground hover:bg-muted">
            {isFixed ? '€' : '%'}
          </button>
        </span>
      );
    }
    const label = isFixed ? `€${val.toFixed(2)}` : `${+(val * 100).toFixed(2)}%`;
    if (val > 0) {
      return (
        <button type="button" onClick={() => setEditingExtra(editKey)}
          title={`Extra cost on the base rate: ${isFixed ? `+€${val.toFixed(2)}/parcel` : `base × (1 + ${(val * 100).toFixed(2)}%)`}, applied before fuel. Click to edit.`}
          className={`shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold ${dirty ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>
          +{label}
        </button>
      );
    }
    return (
      <button type="button" onClick={() => setEditingExtra(editKey)}
        title="Add an extra cost (regulatory / surcharge) on the base rate"
        className="shrink-0 rounded px-1 text-[11px] font-bold text-muted-foreground opacity-30 hover:opacity-100 hover:bg-muted">
        +
      </button>
    );
  };

  const saveFuelSurcharges = async () => {
    for (const [key, data] of Object.entries(modifiedFuelSurcharges)) {
      if (data.carrier_id) {
        // Save to carrier assignment — only the fields actually edited, so a
        // fuel-only edit never resets the extra cost and vice versa.
        const payload = { country_id: data.country_id, rate_type: data.rate_type, carrier_id: data.carrier_id };
        if ('fuel_surcharge' in data) { payload.fuel_surcharge = data.fuel_surcharge; payload.fuel_is_fixed = !!data.fuel_is_fixed; }
        if ('surcharge_pct' in data) { payload.surcharge_pct = data.surcharge_pct; payload.surcharge_is_fixed = !!data.surcharge_is_fixed; }
        await axiosInstance.put('api/carriers/assignments/', payload);
      } else if ('fuel_surcharge' in data) {
        // Legacy: save to country (fallback)
        const fieldName = `fuel_surcharge_${data.rate_type}`;
        await axiosInstance.put('api/shipping-rates/countries/', { id: data.country_id, [fieldName]: data.fuel_surcharge });
      }
    }
    setModifiedFuelSurcharges({});
  };

  // Weight rate handlers (key = rateKey(cr) = "countryId_carrierId")
  const handleWeightRateChange = (key, weight, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedWeightRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [weight]: numValue }
    }));
  };

  const saveWeightRates = async (key) => {
    const entry = modifiedWeightRates[key];
    if (!entry || Object.keys(entry).length === 0) return;
    const [countryId, carrierId] = key.split('_');
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/weight/', {
        country_id: parseInt(countryId),
        carrier_id: carrierId ? parseInt(carrierId) : null,
        rates: entry,
      });
      setSuccess('Weight rates saved');
      setModifiedWeightRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteWeightRates = async (cr) => {
    const label = cr.carrier_name ? `${cr.country_name} / ${cr.carrier_name}` : cr.country_name;
    if (!confirm(`Delete all weight rates for ${label}?`)) return;
    setSaving(true);
    try {
      let url = `api/shipping-rates/weight/?country_id=${cr.country_id}`;
      if (cr.carrier_id) url += `&carrier_id=${cr.carrier_id}`;
      await axiosInstance.delete(url);
      setSuccess(`Weight rates deleted for ${label}`);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // Lane on/off (2026-10-05): a switched-off line keeps its rates but is
  // ignored by every pricing path (CountryCarrier.is_active).
  const laneFor = (cr, rateType) => carrierAssignments.find(a => a.country_id === cr.country_id
    && a.carrier_id === cr.carrier_id && a.rate_type === rateType);
  const laneActive = (cr, rateType) => { const a = laneFor(cr, rateType); return !a || a.is_active !== false; };
  const toggleLane = async (cr, rateType) => {
    const on = laneActive(cr, rateType);
    const who = `${cr.country_name} · ${cr.carrier_name || 'carrier'} (${rateType})`;
    let note = '';
    if (on) {
      note = window.prompt(`Switch OFF ${who}?\n\nIts rates are kept but no quote will use this line until you switch it back on. Reason (optional):`, '');
      if (note === null) return;
    } else if (!window.confirm(`Switch ${who} back ON?`)) return;
    try {
      await axiosInstance.post('api/carriers/lane-active/', { country_id: cr.country_id, carrier_id: cr.carrier_id, rate_type: rateType, is_active: !on, note });
      setSuccess(on ? `${who} switched off` : `${who} switched on`);
      fetchData();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not change the line');
    }
  };
  const LaneSwitch = ({ cr, rateType, hint = '' }) => {
    const on = laneActive(cr, rateType);
    const a = laneFor(cr, rateType);
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); toggleLane(cr, rateType); }}
        title={(on ? 'Active: click to switch this line OFF (rates are kept)' : `Switched OFF${a?.disabled_note ? ': ' + a.disabled_note : ''}. Click to switch on.`) + hint}
        className={`ml-0.5 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold ${on ? 'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20' : 'bg-red-600 text-white hover:bg-red-700'}`}>
        {on ? 'ON' : 'OFF'}
      </button>
    );
  };

  const toggleWeightOfficial = async (cr, isOfficial) => {
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/weight/', { country_id: cr.country_id, carrier_id: cr.carrier_id || null, is_official: !isOfficial });
      setSuccess(isOfficial ? 'Unmarked as official' : 'Marked as official');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Volumetric rate handlers (key = rateKey(cr))
  const handleVolumetricRateChange = (key, weightKg, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedVolumetricRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [weightKg]: numValue }
    }));
  };

  const saveVolumetricRates = async (key) => {
    const entry = modifiedVolumetricRates[key];
    if (!entry || Object.keys(entry).length === 0) return;
    const [countryId, carrierId] = key.split('_');
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/volumetric/', {
        country_id: parseInt(countryId),
        carrier_id: carrierId ? parseInt(carrierId) : null,
        rates: entry,
      });
      setSuccess('Volumetric rates saved');
      setModifiedVolumetricRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteVolumetricRates = async (cr) => {
    const label = cr.carrier_name ? `${cr.country_name} / ${cr.carrier_name}` : cr.country_name;
    if (!confirm(`Delete all volumetric rates for ${label}?`)) return;
    setSaving(true);
    try {
      let url = `api/shipping-rates/volumetric/?country_id=${cr.country_id}`;
      if (cr.carrier_id) url += `&carrier_id=${cr.carrier_id}`;
      await axiosInstance.delete(url);
      setSuccess(`Volumetric rates deleted for ${label}`);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const toggleVolumetricOfficial = async (cr, isOfficial) => {
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/volumetric/', { country_id: cr.country_id, carrier_id: cr.carrier_id || null, is_official: !isOfficial });
      setSuccess(isOfficial ? 'Unmarked as official' : 'Marked as official');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Zone handlers
  const handleZoneSave = async () => {
    if (!editingZone) return;
    setSaving(true);
    try {
      if (editingZone.id) {
        await axiosInstance.put('api/shipping-rates/zones/', editingZone);
      } else {
        await axiosInstance.post('api/shipping-rates/zones/', editingZone);
      }
      setSuccess('Zone saved successfully');
      setZoneDialogOpen(false);
      setEditingZone(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleZoneDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this zone?')) return;
    try {
      await axiosInstance.delete(`api/shipping-rates/zones/?id=${id}`);
      setSuccess('Zone deleted');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handleZoneWeightRateChange = (zoneId, weight, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedZoneWeightRates(prev => ({
      ...prev,
      [zoneId]: { ...(prev[zoneId] || {}), [weight]: numValue }
    }));
  };

  const saveZoneWeightRates = async (zoneId) => {
    const rates = modifiedZoneWeightRates[zoneId];
    if (!rates || Object.keys(rates).length === 0) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/zones/weight/', { zone_id: zoneId, rates });
      setSuccess('Zone weight rates saved');
      setModifiedZoneWeightRates(prev => { const s = { ...prev }; delete s[zoneId]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleZoneVolumetricRateChange = (zoneId, weightKg, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedZoneVolumetricRates(prev => ({
      ...prev,
      [zoneId]: { ...(prev[zoneId] || {}), [weightKg]: numValue }
    }));
  };

  const saveZoneVolumetricRates = async (zoneId) => {
    const rates = modifiedZoneVolumetricRates[zoneId];
    if (!rates || Object.keys(rates).length === 0) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/zones/volumetric/', { zone_id: zoneId, rates });
      setSuccess('Zone volumetric rates saved');
      setModifiedZoneVolumetricRates(prev => { const s = { ...prev }; delete s[zoneId]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Keyed by `${zoneId}_${carrierId}` so edits save against the carrier whose
  // row is being edited (NOT carrier=null, which renders invisibly — the Pallet
  // tab only shows zone rows under their carrier's mainland row).
  const handleZonePalletRateChange = (zoneId, carrierId, binId, value) => {
    const numValue = parseFloat(value) || 0;
    const key = `${zoneId}_${carrierId ?? ''}`;
    setModifiedZonePalletRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [binId]: numValue }
    }));
  };

  const saveZonePalletRates = async (key) => {
    const rates = modifiedZonePalletRates[key];
    if (!rates || Object.keys(rates).length === 0) return;
    const [zid, carId] = key.split('_');
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/zones/pallet/', {
        zone_id: parseInt(zid),
        carrier_id: carId ? parseInt(carId) : null,
        rates,
      });
      setSuccess('Zone pallet rates saved');
      setModifiedZonePalletRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Coli rate handlers (key = rateKey(cr))
  const handleColiRateChange = (key, numPackages, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedColiRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), rates: { ...(prev[key]?.rates || {}), [numPackages]: numValue } }
    }));
  };

  const handleColiAdditionalChange = (key, value) => {
    setModifiedColiRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), additional: parseFloat(value) || 0 }
    }));
  };

  const saveColiRates = async (key, countryId, carrierId) => {
    const mod = modifiedColiRates[key];
    if (!mod) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/coli/', {
        country_id: countryId,
        carrier_id: carrierId || null,
        rates: mod.rates || {},
        coli_additional_cost: mod.additional,
      });
      setSuccess('Per-parcel rates saved');
      setModifiedColiRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePalletRateChange = (key, binId, value) => {
    setModifiedPalletRates(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [binId]: parseFloat(value) || 0
      }
    }));
  };

  const savePalletRates = async (key, countryId, carrierId) => {
    const mod = modifiedPalletRates[key];
    if (!mod) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/pallet/', {
        country_id: countryId,
        carrier_id: carrierId || null,
        rates: mod,
      });
      setSuccess('Pallet rates saved');
      setModifiedPalletRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deletePalletRates = async (cr) => {
    const label = cr.carrier_name ? `${cr.country_name} / ${cr.carrier_name}` : cr.country_name;
    if (!confirm(`Delete all pallet rates for ${label}?`)) return;
    setSaving(true);
    try {
      let url = `api/shipping-rates/pallet/?country_id=${cr.country_id}`;
      if (cr.carrier_id) url += `&carrier_id=${cr.carrier_id}`;
      await axiosInstance.delete(url);
      setSuccess(`Pallet rates deleted for ${label}`);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const togglePalletOfficial = async (cr, isOfficial) => {
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/pallet/', { country_id: cr.country_id, carrier_id: cr.carrier_id || null, is_official: !isOfficial });
      setSuccess(isOfficial ? 'Unmarked as official' : 'Marked as official');
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // ---- Pickup tab handlers (GLS ParcelShop / Locker) ----
  // Key includes network so shop and locker edits (same GLS carrier) don't collide.
  const pickupRateKey = (pr) => `${pr.country_id}_${pr.carrier_id || ''}_${pr.network || 'shop'}`;
  const handlePickupRateChange = (key, weight, value) => {
    const numValue = parseFloat(value) || 0;
    setModifiedPickupRates(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [weight]: numValue }
    }));
  };

  const savePickupRates = async (pr) => {
    const key = pickupRateKey(pr);
    const mod = modifiedPickupRates[key];
    if (!mod) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/pickup/', {
        country_id: pr.country_id,
        carrier_id: pr.carrier_id || null,
        network: pr.network || 'shop',
        rates: mod,
      });
      setSuccess('Pickup rates saved');
      setModifiedPickupRates(prev => { const s = { ...prev }; delete s[pickupRateKey(pr)]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Switch which carrier's pickup rates are live for a country. Exclusive: turns
  // every OTHER carrier off, so exactly one is active (e.g. GLS -> bpost for BE).
  // Carrier chips are click-through: jump to the Carriers tab with that
  // carrier filtered, so "what IS this contract?" is one click from the rate
  // instead of a hunt. Strip the service variant, since the Carriers tab
  // searches on the carrier name.
  const openCarrier = (name) => {
    setCarrierFilter((name || '').replace(/\s*\(.*\)$/, '').trim());
    setTabValue(8);
  };

  const loadPalletBands = () => {
    setPalletBandLoading(true);
    return axiosInstance.get('api/shipping-rates/pallet-bands/', { params: {
        basis: palletBandBasis,
        postal_code: palletBandProbe || undefined,
        weight_kg: palletBandProbeWeight || undefined,
      } })
      .then(r => setPalletBandData(r.data))
      .catch(err => setError(err?.response?.data?.error || 'Failed to load pallet rates'))
      .finally(() => setPalletBandLoading(false));
  };

  // window.open sends no Authorization header, so the endpoint answered 403.
  // Fetch through axios and hand the blob to a download link, like the other
  // rate tabs do.
  const handlePalletBandExport = async () => {
    try {
      const r = await axiosInstance.get('api/shipping-rates/pallet-bands/excel/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'pallet_band_rates.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to export');
    }
  };

  const handlePalletBandImport = async (file) => {
    if (!file) return;
    setSaving(true); setError(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axiosInstance.post('api/shipping-rates/pallet-bands/excel/', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.success) {
        setSuccess(r.data.message);
        if (r.data.skipped_total) {
          setError(`${r.data.skipped_total} row(s) skipped: ${(r.data.skipped || []).slice(0, 3).join('; ')}`);
        }
        await loadPalletBands();
      } else {
        setError(r.data.error || 'Import failed');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Import failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (tabValue !== 12) return;
    let cancelled = false;
    setPalletBandLoading(true);
    axiosInstance.get('api/shipping-rates/pallet-bands/', { params: {
        basis: palletBandBasis,
        postal_code: palletBandProbe || undefined,
        weight_kg: palletBandProbeWeight || undefined,
      } })
      .then(r => { if (!cancelled) setPalletBandData(r.data); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.error || 'Failed to load pallet rates'); })
      .finally(() => { if (!cancelled) setPalletBandLoading(false); });
    return () => { cancelled = true; };
  }, [tabValue, palletBandBasis, palletBandProbe, palletBandProbeWeight]);

  const togglePickupCarrier = async (countryId, carrierId, active, iso2) => {
    setSaving(true);
    try {
      // Exclusive everywhere by default: activating a carrier switches the
      // others off, so exactly one network is live. In a multi-network country
      // (BE / NL) it ADDS instead, because both have a Shopify delivery method
      // and an Atlas binding of their own.
      const multi = pickupMultiCountries.includes((iso2 || '').toUpperCase());
      await axiosInstance.put('api/shipping-rates/pickup/', {
        country_id: countryId, carrier_id: carrierId || null,
        set_active: active, exclusive: !multi,
      });
      setSuccess(active ? 'Pickup carrier activated' : 'Pickup carrier disabled');
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to toggle carrier');
    } finally {
      setSaving(false);
    }
  };

  // --- Returns tab (per-country, per-carrier weight-band return rates) ---
  const returnRateKey = (rr) => `${rr.country_id}_${rr.carrier_id || ''}`;
  const handleReturnRateChange = (key, weight, value) => {
    const num = parseFloat(value) || 0;
    setModifiedReturnRates(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [weight]: num } }));
  };
  const saveReturnRates = async (rr) => {
    const key = returnRateKey(rr);
    const mod = modifiedReturnRates[key];
    if (!mod) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/returns/', {
        country_id: rr.country_id, carrier_id: rr.carrier_id || null, rates: mod,
      });
      setSuccess('Return rates saved');
      setModifiedReturnRates(prev => { const s = { ...prev }; delete s[key]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally { setSaving(false); }
  };
  // A carrier that follows the outbound card (ReturnRateRule): only its
  // deduction is edited, the bands come from the outbound rates.
  const saveReturnDeduction = async (rr, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === rr.deduction) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/returns/', {
        country_id: rr.country_id, carrier_id: rr.carrier_id, deduction: num,
      });
      setSuccess('Return deduction saved');
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally { setSaving(false); }
  };
  const toggleReturnCarrier = async (countryId, carrierId, active) => {
    setSaving(true);
    try {
      // Non-exclusive: GLS and bpost return rates can both be active at once.
      await axiosInstance.put('api/shipping-rates/returns/', {
        country_id: countryId, carrier_id: carrierId || null, set_active: active, exclusive: false,
      });
      setSuccess(active ? 'Return carrier enabled' : 'Return carrier disabled');
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to toggle carrier');
    } finally { setSaving(false); }
  };

  // Fuel surcharge for pickup lives on the country (fuel_surcharge_pickup), not a
  // carrier assignment. Keyed by country_id, stored as a fraction (e.g. 0.15).
  const handlePickupFuelChange = (countryId, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    setModifiedPickupFuel(prev => ({ ...prev, [countryId]: numValue / 100 }));
  };

  // Profile field edits, keyed by country_id + network.
  const handlePickupProfileChange = (countryId, network, field, value) => {
    const key = `${countryId}_${network}`;
    setModifiedPickupProfiles(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { country_id: countryId, network }), [field]: value }
    }));
  };

  // The carrier-specific profile row that carries this carrier's pickup fuel.
  const pickupCarrierProfile = (row, carrierId, network = 'shop') =>
    ((row.profileEntry && row.profileEntry.carriers) || []).find(p => p.carrier_id === carrierId && p.network === network);

  // Odoo delivery method the carrier advice names for this carrier's pickups
  // here (shop network). Blank = Shippy's default, shown as the placeholder.
  const savePickupCarrierOdoo = async (row, carrierId) => {
    const key = `${row.country_id}_${carrierId}`;
    const mapping = modifiedPickupOdoo[key];
    if (mapping === undefined) return;
    setSaving(true);
    try {
      await axiosInstance.put('api/shipping-rates/pickup-profiles/',
        { country_id: row.country_id, network: 'shop', carrier_id: carrierId, odoo_mapping: mapping });
      setSuccess(mapping ? `Odoo mapping saved: ${mapping}` : 'Odoo mapping cleared: Shippy default applies');
      setModifiedPickupOdoo(prev => { const n = { ...prev }; delete n[key]; return n; });
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const savePickupCarrierFuel = async (row, carrierId) => {
    const key = `${row.country_id}_${carrierId}`;
    const fuel = modifiedPickupCarrierFuel[key];
    if (fuel === undefined) return;
    setSaving(true);
    try {
      // One figure per carrier; it is stored on every network row the carrier has.
      for (const network of ['shop', 'locker']) {
        if (!pickupCarrierProfile(row, carrierId, network)) continue;
        await axiosInstance.put('api/shipping-rates/pickup-profiles/',
          { country_id: row.country_id, network, carrier_id: carrierId, fuel_surcharge: fuel });
      }
      setSuccess('Pickup fuel saved');
      setModifiedPickupCarrierFuel(prev => { const n = { ...prev }; delete n[key]; return n; });
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const savePickupProfile = async (countryId, network) => {
    const key = `${countryId}_${network}`;
    const mod = modifiedPickupProfiles[key];
    const fuelMod = modifiedPickupFuel[countryId];
    if (!mod && fuelMod === undefined) return;
    setSaving(true);
    try {
      const payload = { country_id: countryId, network, ...(mod || {}) };
      if (fuelMod !== undefined) payload.fuel_surcharge_pickup = fuelMod;
      await axiosInstance.put('api/shipping-rates/pickup-profiles/', payload);
      setSuccess('Pickup profile saved');
      setModifiedPickupProfiles(prev => { const s = { ...prev }; delete s[key]; return s; });
      if (fuelMod !== undefined) setModifiedPickupFuel(prev => { const s = { ...prev }; delete s[countryId]; return s; });
      fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <ShippyLoader size={64} message="Loading shipping rates..." />
      </div>
    );
  }

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Shipping Rates Management</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReloadCache} disabled={reloadingCache}
                  title="Flush the server's in-memory rate cache so DB rate/carrier changes take effect immediately (no redeploy)">
            {reloadingCache ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Waypoints className="h-4 w-4 mr-2" />}
            {reloadingCache ? 'Reloading…' : 'Reload rate cache'}
          </Button>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>
      {cacheReloadMsg && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 px-4 py-2 text-sm text-emerald-800 dark:text-emerald-200 flex items-center justify-between">
          <span>{cacheReloadMsg}</span>
          <button onClick={() => setCacheReloadMsg('')}><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2"><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
        <div className="border-b border-border/60 flex items-center">
          {tabGroups.map((group, gi) => (
            <div key={group.label} className="flex items-center">
              {gi > 0 && <div className="w-px h-6 bg-border mx-1" />}
              <div className="flex items-center gap-0.5 px-1">
                {group.tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isApi = group.label === 'Developer';
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setTabValue(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                        tabValue === tab.id
                          ? isApi ? 'border-violet-500 text-violet-600 dark:text-violet-400' : 'border-primary text-primary'
                          : isApi ? 'border-transparent text-violet-400 dark:text-violet-500 hover:text-violet-600 dark:hover:text-violet-400 hover:border-violet-300' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                      {tab.superseded && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0 text-[9px] font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          title={tab.superseded}
                        >
                          <Archive className="h-2.5 w-2.5" />
                          fallback
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4">
          {/* Countries Tab */}
          {tabValue === 0 && (
            <div>
              <div className="mb-4 flex items-center gap-3">
                <Button onClick={() => {
                  setEditingCountry({ id: 0, iso2_code: '', name: '', vat_rate: 0, franco_threshold: null, franco_b2c: null, is_eu: false, is_active: true });
                  setCountryDialogOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Country
                </Button>
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search countries..." value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="pl-9 h-9" />
                </div>
              </div>
              {(() => {
                const handleCountrySort = (field) => {
                  if (countrySortField === field) {
                    setCountrySortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                  } else {
                    setCountrySortField(field);
                    setCountrySortDir('asc');
                  }
                };
                const SortIcon = ({ field }) => {
                  if (countrySortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
                  return countrySortDir === 'asc'
                    ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
                    : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
                };
                const q = countryFilter.toLowerCase();
                const FEATURE_FILTERS = {
                  cs:     { label: 'CS',     pred: c => c.carrier_service,   tip: 'On Shippy carrier service' },
                  pickup: { label: 'Pickup', pred: c => c.pickup,            tip: 'Pickup Point (ParcelShop) offered' },
                  locker: { label: 'Locker', pred: c => c.locker,            tip: 'Parcel Locker offered' },
                  multi:  { label: 'Multi',  pred: c => c.multi_carrier,     tip: 'Multi-carrier rates' },
                  split:  { label: 'Split',  pred: c => c.partial_shipping,  tip: "Ship-what's-in-stock" },
                  gift:   { label: 'Gift',   pred: c => c.franco_gift != null, tip: 'Has a franco gift (partial-franco discount)' },
                  franco: { label: 'Franco B2C', pred: c => c.franco_b2c != null, tip: 'Has a B2C free-shipping threshold' },
                  eu:     { label: 'EU',     pred: c => c.is_eu,             tip: 'EU member' },
                  gaps:   { label: '⚑ Gaps', pred: c => c.carrier_service && !(c.pickup && c.multi_carrier), tip: 'On Shippy but missing pickup and/or multi-carrier' },
                  inactive: { label: 'Inactive', pred: c => !c.is_active,    tip: 'Show only inactive countries' },
                };
                const activeFilters = [...countryFeatureFilters];
                const sortedCountries = [...countries]
                  .filter(c => !q || c.name.toLowerCase().includes(q) || c.iso2_code.toLowerCase().includes(q))
                  .filter(c => activeFilters.every(k => FEATURE_FILTERS[k]?.pred(c)))
                  .sort((a, b) => {
                    const dir = countrySortDir === 'asc' ? 1 : -1;
                    const valA = a[countrySortField];
                    const valB = b[countrySortField];
                    if (valA == null && valB == null) return 0;
                    if (valA == null) return 1;
                    if (valB == null) return -1;
                    if (typeof valA === 'string') return dir * valA.localeCompare(valB);
                    return dir * (valA - valB);
                  });

                // carrier lookup + per-country assignments, built once per render
                const carrierById = {};
                carriers.forEach(c => { carrierById[c.id] = c; });
                const assignmentsByCountry = {};
                carrierAssignments.forEach(a => {
                  (assignmentsByCountry[a.country_id] ||= []).push(a);
                });
                // `short` rides IN the chip text. The rate type used to live only
                // in the colour and the title attribute, so a carrier assigned to
                // two rate types rendered as two chips with identical text - and a
                // copy-paste of the table (which keeps neither colour nor tooltip)
                // came out as duplicate lines that looked like a data error.
                const RT_META = {
                  weight:     { label: 'Real weight', short: 'wt',  cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
                  volumetric: { label: 'Volumetric',  short: 'vol', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
                  coli:       { label: 'Per parcel',  short: 'par', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
                  pallet:     { label: 'Pallet',      short: 'plt', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
                };
                const carrierLabel = (cid) => {
                  const c = carrierById[cid];
                  if (!c) return `#${cid}`;
                  return c.service ? `${c.name} ${c.service}` : c.name;
                };

                return (
                <div className="overflow-x-auto">
                  {/* Feature filters (AND) */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className="text-xs text-muted-foreground mr-0.5">Filter</span>
                    {Object.entries(FEATURE_FILTERS).map(([key, ff]) => {
                      const on = countryFeatureFilters.has(key);
                      return (
                        <button key={key} title={ff.tip}
                          onClick={() => setCountryFeatureFilters(prev => {
                            const n = new Set(prev);
                            n.has(key) ? n.delete(key) : n.add(key);
                            return n;
                          })}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border transition-colors ${on
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-transparent text-muted-foreground border-border hover:bg-muted'}`}>
                          {ff.label}
                        </button>
                      );
                    })}
                    {(countryFeatureFilters.size > 0 || countryFilter) && (
                      <button onClick={() => { setCountryFeatureFilters(new Set()); setCountryFilter(''); }}
                        className="text-xs text-muted-foreground hover:text-foreground underline ml-0.5">
                        Clear
                      </button>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">{sortedCountries.length} of {countries.length}</span>
                  </div>
                  {/* Carrier colour legend */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 text-xs text-muted-foreground">
                    <span>Carrier rate type:</span>
                    {Object.values(RT_META).map(m => (
                      <span key={m.label} className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${m.cls}`}>{m.label}</span>
                    ))}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-2 py-1.5 text-left font-medium cursor-pointer select-none" onClick={() => handleCountrySort('name')}>
                          <span className="inline-flex items-center">Country <SortIcon field="name" /></span>
                        </th>
                        <th className="px-2 py-1.5 text-left font-medium cursor-pointer select-none" onClick={() => handleCountrySort('vat_rate')} title="VAT rate · ✓/✗ = VAT charged on B2C">
                          <span className="inline-flex items-center">VAT <SortIcon field="vat_rate" /></span>
                        </th>
                        <th className="px-2 py-1.5 text-left font-medium cursor-pointer select-none" onClick={() => handleCountrySort('franco_b2c')} title="Franco B2B / Franco B2C / franco gift (partial-franco discount, applied only when shipping ≤ 10% of order value)">
                          <span className="inline-flex items-center">Franco <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">B2B/B2C/gift</span> <SortIcon field="franco_b2c" /></span>
                        </th>
                        <th className="px-2 py-1.5 text-left font-medium" title="Checkout features live (config-driven): C carrier service · P Pickup Point · L Parcel Locker · M multi-carrier · S split shipment">Checkout</th>
                        <th className="px-2 py-1.5 text-left font-medium">Carriers</th>
                        <th className="px-2 py-1.5 text-right font-medium cursor-pointer select-none" onClick={() => handleCountrySort('is_active')} title="Active / inactive">
                          <span className="inline-flex items-center justify-end">Status <SortIcon field="is_active" /></span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCountries.map((country) => (
                        <tr key={country.id}
                            className="border-b border-border hover:bg-muted/30 cursor-pointer"
                            onClick={() => setCountryDetailId(country.id)}
                            title="Click to see rate analysis">
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className="mr-1.5">{getCountryFlag(country.iso2_code)}</span>
                            <span className="font-bold">{country.iso2_code}</span>
                            <span className="ml-1.5 text-muted-foreground">{country.name}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                            {(country.vat_rate * 100).toFixed(0)}%
                            <span title={country.charge_vat_b2c ? 'VAT charged on B2C' : 'No VAT on B2C'} className={`ml-1 ${country.charge_vat_b2c ? 'text-emerald-500' : 'text-muted-foreground/40'}`}>{country.charge_vat_b2c ? '✓' : '✗'}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                            <span className={country.franco_threshold ? '' : 'text-muted-foreground/40'}>{country.franco_threshold ? `\u20AC${country.franco_threshold}` : '\u2014'}</span>
                            <span className="text-muted-foreground/30 mx-0.5">/</span>
                            <span className={country.franco_b2c ? '' : 'text-muted-foreground/40'}>{country.franco_b2c ? `\u20AC${country.franco_b2c}` : '\u2014'}</span>
                            <span className="text-muted-foreground/30 mx-0.5">/</span>
                            <span className={country.franco_gift != null ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-muted-foreground/40'}>{country.franco_gift != null ? `€${country.franco_gift}` : '—'}</span>
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap font-mono font-semibold">
                            {[
                              ['C', country.carrier_service, 'Carrier service live at checkout'],
                              ['P', country.pickup, 'GLS ParcelShop pickup (Pickup Point) offered at checkout'],
                              ['L', country.locker, 'GLS Parcel Locker offered at checkout'],
                              ['M', country.multi_carrier, 'Multi-carrier alternative rates at checkout'],
                              ['S', country.partial_shipping, "Ship-what's-in-stock (split shipment) offered"],
                            ].map(([label, on, tip], i) => (
                              <React.Fragment key={label}>
                                {i > 0 && <span className="text-muted-foreground/25">·</span>}
                                <span title={tip} className={on ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/30'}>{label}</span>
                              </React.Fragment>
                            ))}
                          </td>
                          <td className="px-2 py-1.5 max-w-xs">
                            {(() => {
                              const assigns = (assignmentsByCountry[country.id] || [])
                                .slice()
                                .sort((a, b) =>
                                  carrierLabel(a.carrier_id).localeCompare(carrierLabel(b.carrier_id)) ||
                                  (a.rate_type || '').localeCompare(b.rate_type || '')
                                );
                              if (assigns.length === 0) {
                                return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">None</span>;
                              }
                              return (
                                <div className="flex gap-1 flex-wrap">
                                  {assigns.map((a, i) => {
                                    const m = RT_META[a.rate_type] || { label: a.rate_type, short: a.rate_type, cls: 'bg-gray-100 text-gray-700' };
                                    return (
                                      <span
                                        key={`${a.carrier_id}-${a.rate_type}-${i}`}
                                        title={`${carrierLabel(a.carrier_id)} — ${m.label}`}
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}
                                      >
                                        {carrierLabel(a.carrier_id)}
                                        <span className="opacity-60 font-normal">{m.short}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center justify-end gap-1.5">
                              <span title={country.is_active ? 'Active' : 'Inactive'} className={`inline-block h-2 w-2 rounded-full shrink-0 ${country.is_active ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                              <button className="rounded p-1 hover:bg-accent" onClick={(e) => { e.stopPropagation(); setEditingCountry(country); setCountryDialogOpen(true); }}>
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" onClick={(e) => { e.stopPropagation(); handleCountryDelete(country.id); }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>
          )}

          {/* Zones Tab */}
          {tabValue === 1 && (
            <div>
              <div className="mb-4 flex items-center gap-3">
                <Button onClick={() => {
                  setEditingZone({ id: 0, name: '', country_id: countries[0]?.id || 0, country_name: '', country_iso2: '', postal_code_patterns: '', vat_rate: null, franco_threshold: null, franco_b2c: null, is_eu: true, inherit_country_carriers: false, is_active: true, notes: '', weight_rates: {}, volumetric_rates: {} });
                  setZoneDialogOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Zone
                </Button>
                <p className="text-xs text-muted-foreground">Special shipping zones (islands, remote areas) with different rates than mainland. Zone rates appear in the Weight & Volumetric tabs under their parent country.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Country</th>
                      <th className="px-3 py-2 text-left font-medium">Zone Name</th>
                      <th className="px-3 py-2 text-left font-medium">Postal Codes</th>
                      <th className="px-3 py-2 text-left font-medium">VAT %</th>
                      <th className="px-3 py-2 text-left font-medium">Franco B2B</th>
                      <th className="px-3 py-2 text-left font-medium">Franco B2C</th>
                      <th className="px-3 py-2 text-left font-medium">Pallet</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Rates</th>
                      <th className="px-3 py-2 text-left font-medium">Active</th>
                      <th className="px-3 py-2 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((zone) => (
                      <tr key={zone.id} className="border-b border-border hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <span className="mr-1">{getCountryFlag(zone.country_iso2)}</span>
                          <span className="font-bold text-xs mr-1">{zone.country_iso2}</span>
                          <span className="text-xs">{zone.country_name}</span>
                        </td>
                        <td className="px-3 py-2 font-medium whitespace-nowrap">
                          {zone.name}
                          {zone.inherit_country_carriers && (
                            <span className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300" title="Override zone: the country's other carriers still serve these postal codes at their country rates">+country</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs max-w-[360px]">
                          {/* A summary, not the raw list: range zones hold thousands of
                              ranges and no prefixes, so printing postal_code_patterns
                              showed them as empty and printed prefix zones as one
                              unbroken line. The full set lives on the Zones page. */}
                          <ZonePostcodeSummary zone={zone} />
                        </td>
                        <td className="px-3 py-2">{zone.vat_rate != null ? `${(zone.vat_rate * 100).toFixed(0)}%` : '-'}</td>
                        <td className="px-3 py-2">{zone.franco_threshold != null ? `\u20AC${zone.franco_threshold}` : '-'}</td>
                        <td className="px-3 py-2">{zone.franco_b2c != null ? `\u20AC${zone.franco_b2c}` : '-'}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap">
                            {zone.weight_rates_count > 0 && (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Weight</span>
                            )}
                            {zone.volumetric_rates_count > 0 && (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">Vol</span>
                            )}
                            {zone.freight_rates_count > 0 && (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300" title="Freight bands: pallets to this zone are priced by weight (see Pallet tab)">Freight</span>
                            )}
                            {zone.weight_rates_count === 0 && zone.volumetric_rates_count === 0 && !(zone.freight_rates_count > 0) && (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            {zone.weight_rates_count > 0 ? (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${zone.weight_rates_count >= 30 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                                W {zone.weight_rates_count}/30
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">W -</span>
                            )}
                            {zone.volumetric_rates_count > 0 ? (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${zone.volumetric_rates_count >= 29 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                                V {zone.volumetric_rates_count}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">V -</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            zone.is_active
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {zone.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button className="rounded p-1 hover:bg-accent" onClick={() => { setEditingZone(zone); setZoneDialogOpen(true); }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" onClick={() => handleZoneDelete(zone.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {zones.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">
                          No zones configured. Add zones for islands or special regions.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bins Tab */}
          {tabValue === 2 && (
            <div>
              <div className="mb-4 flex items-center gap-3">
                <Button onClick={() => {
                  setEditingBin({ id: 0, name: '', width: 0, height: 0, depth: 0, max_weight: 30, packaging_cost: 0, is_active: true, sort_order: bins.length + 1, dimensions_key: '' });
                  setBinDialogOpen(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Bin
                </Button>
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search bins..." value={binFilter} onChange={(e) => setBinFilter(e.target.value)} className="pl-9 h-9" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Order</th>
                      <th className="px-3 py-2 text-left font-medium">Name</th>
                      <th className="px-3 py-2 text-left font-medium">Dimensions (cm)</th>
                      <th className="px-3 py-2 text-left font-medium">Max Weight</th>
                      <th className="px-3 py-2 text-left font-medium">Packaging Cost</th>
                      <th className="px-3 py-2 text-left font-medium">Active</th>
                      <th className="px-3 py-2 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bins.filter(b => {
                      if (!binFilter) return true;
                      const q = binFilter.toLowerCase();
                      return b.name.toLowerCase().includes(q)
                        || `${b.width}x${b.height}x${b.depth}`.includes(q)
                        || `${b.width} x ${b.height} x ${b.depth}`.includes(q);
                    }).sort((a, b) => a.packaging_cost - b.packaging_cost).map((bin) => (
                      <tr key={bin.id} className={`border-b border-border ${bin.is_pallet ? 'bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-100/60 dark:hover:bg-amber-900/20' : 'hover:bg-muted/30'}`}>
                        <td className="px-3 py-2">{bin.sort_order}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <BoxViz w={bin.width} h={bin.height} d={bin.depth} />
                            {bin.name}
                            {bin.is_pallet && (
                              <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-amber-200 text-amber-800 dark:bg-amber-800/40 dark:text-amber-300">Pallet</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{bin.width} x {bin.height} x {bin.depth}</td>
                        <td className="px-3 py-2">{bin.max_weight} kg</td>
                        <td className="px-3 py-2">{bin.packaging_cost?.toFixed(2) || '0.00'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            bin.is_active
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {bin.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <button className="rounded p-1 hover:bg-accent" onClick={() => { setEditingBin(bin); setBinDialogOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Weight Rates Tab */}
          {tabValue === 3 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Weight-based shipping rates (EUR per package weight tier). <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block w-3 h-3 rounded border border-emerald-400 bg-emerald-50/60" /> Official rate</span></p>
                  <div className="relative max-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input placeholder="Filter country..." value={weightRateFilter} onChange={(e) => setWeightRateFilter(e.target.value)} className="pl-8 h-8 text-xs" />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openAddRateModal('weight')}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add country + carrier
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExcelExport('weight')}>
                    <Download className="h-4 w-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => weightFileRef.current?.click()} disabled={saving}>
                    <Upload className="h-4 w-4 mr-1.5" /> Import Excel
                  </Button>
                  <input
                    ref={weightFileRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => { handleExcelImport('weight', e.target.files[0]); e.target.value = ''; }}
                  />
                  <Button
                    disabled={(Object.keys(modifiedWeightRates).length === 0 && Object.keys(modifiedZoneWeightRates).length === 0 && Object.keys(modifiedFuelSurcharges).length === 0) || saving}
                    onClick={async () => {
                      if (Object.keys(modifiedFuelSurcharges).length > 0) await saveFuelSurcharges();
                      for (const key of Object.keys(modifiedWeightRates)) await saveWeightRates(key);
                      for (const zid of Object.keys(modifiedZoneWeightRates)) await saveZoneWeightRates(parseInt(zid));
                      fetchData();
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save All ({Object.keys(modifiedWeightRates).length + Object.keys(modifiedZoneWeightRates).length + Object.keys(modifiedFuelSurcharges).length})
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[calc(100vh-300px)] border border-border rounded-md">
                <table className="text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-muted">
                      <th className="sticky left-0 z-20 bg-muted px-3 py-2 text-left font-medium min-w-[180px]">Country</th>
                      <th className="px-1 py-2 text-center font-medium text-xs min-w-[62px]" title="Fuel / extra cost surcharge applied on top of base rate">Fuel %</th>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((kg) => (
                        <th key={kg} className="px-1 py-2 text-center font-medium text-xs min-w-[60px]">{kg}kg</th>
                      ))}
                      <th className="px-2 py-2 text-center font-medium text-xs min-w-[70px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sorted = weightRates.filter(cr => {
                        if (!weightRateFilter) return true;
                        const q = weightRateFilter.toLowerCase();
                        return cr.country_name.toLowerCase().includes(q) || cr.iso2_code.toLowerCase().includes(q) || (cr.carrier_name || '').toLowerCase().includes(q);
                      }).sort((a, b) => a.country_name.localeCompare(b.country_name) || (a.carrier_name || '').localeCompare(b.carrier_name || ''));
                      // Drop pure "vol only" noise from the weight tab: a row with
                      // no country-level weight rates AND no zone weight rates for
                      // this carrier, where the country only has volumetric rates.
                      const visibleRows = sorted.filter((cr) => {
                        if (Object.values(cr.rates).some(r => r.cost > 0)) return true;
                        const hasZoneRates = zones.some(z =>
                          z.country_id === cr.country_id &&
                          z.weight_rates_by_carrier?.[cr.carrier_id] &&
                          Object.keys(z.weight_rates_by_carrier[cr.carrier_id]).length > 0);
                        if (hasZoneRates) return true;
                        return !volumetricRates.some(vr => vr.country_id === cr.country_id);
                      });
                      const countByCountry = countryGroupCounts(visibleRows);
                      let lastCountryId = null;
                      return visibleRows.map((cr) => {
                      const crKey = rateKey(cr);
                      const countryZones = zones.filter(z => z.country_id === cr.country_id);
                      const allOfficial = Object.values(cr.rates).length > 0 && Object.values(cr.rates).every(r => r.is_official);
                      const hasAnyRates = Object.values(cr.rates).some(r => r.cost > 0);
                      const crZonesWithRates = countryZones.filter((zone) => {
                        const bc = zone.weight_rates_by_carrier?.[cr.carrier_id];
                        return bc && Object.keys(bc).length > 0;
                      });
                      const hasZoneRates = crZonesWithRates.length > 0;
                      const zonesExpanded = expandedZoneRows.has(crKey);
                      const isMultiCarrier = countByCountry[cr.country_id] > 1;
                      const showCountryHeader = isMultiCarrier && cr.country_id !== lastCountryId;
                      lastCountryId = cr.country_id;
                      return (
                        <React.Fragment key={crKey}>
                          {showCountryHeader && renderGroupHeader(cr, countByCountry[cr.country_id], 33)}
                          <tr className={`border-b border-border ${hasAnyRates ? 'hover:bg-muted/30' : 'opacity-50 hover:opacity-100'}`}>
                            <td className={`sticky left-0 z-[1] px-3 py-1 font-medium whitespace-nowrap ${modifiedWeightRates[crKey] ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-card'}`}>
                              {hasZoneRates && (
                                <button
                                  onClick={() => toggleZoneRows(crKey)}
                                  title={zonesExpanded ? 'Collapse zone rates' : `Show ${crZonesWithRates.length} zone rates`}
                                  className="mr-1 inline-flex items-center align-middle text-muted-foreground hover:text-foreground"
                                >
                                  {zonesExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                </button>
                              )}
                              {renderCountryCell(cr, isMultiCarrier, 'weight')}
                              {hasZoneRates && (
                                <span className="ml-1 text-[10px] rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 font-medium">{crZonesWithRates.length} zone{crZonesWithRates.length !== 1 ? 's' : ''}</span>
                              )}
                              {!hasAnyRates && !hasZoneRates && <span className="ml-1 text-[10px] rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 font-medium">new</span>}
                            </td>
                            {renderFuelCell(cr, 'weight')}
                            {Array.from({ length: 30 }, (_, i) => i + 1).map((kg) => {
                              const rate = cr.rates[kg.toString()];
                              const modified = modifiedWeightRates[crKey]?.[kg.toString()];
                              const isOfficial = rate?.is_official;
                              return (
                                <td key={kg} className="px-0.5 py-0.5">
                                  <input
                                    type="number"
                                    step="0.1"
                                    defaultValue={rate?.cost?.toFixed(1) || '0'}
                                    onChange={(e) => handleWeightRateChange(crKey, kg.toString(), e.target.value)}
                                    className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-1 py-0.5 text-center whitespace-nowrap">
                              <button
                                title={allOfficial ? 'Unmark as official' : 'Mark as official'}
                                onClick={() => toggleWeightOfficial(cr, allOfficial)}
                                className={`inline-flex items-center justify-center rounded p-1 text-xs ${allOfficial ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-muted-foreground hover:bg-muted'}`}
                              >
                                <span className={`inline-block w-3 h-3 rounded border ${allOfficial ? 'border-emerald-500 bg-emerald-400' : 'border-border bg-background'}`} />
                              </button>
                              <LaneSwitch cr={cr} rateType="weight" />
                              <button
                                title="Delete all rates"
                                onClick={() => deleteWeightRates(cr)}
                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-0.5"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                          {/* Zone rows — collapsible; only those with rates for THIS carrier */}
                          {zonesExpanded && crZonesWithRates
                            .map((zone) => {
                              const carrierRates = zone.weight_rates_by_carrier?.[cr.carrier_id] || {};
                              return (
                            <tr key={`zone-${zone.id}-${cr.carrier_id}`} className="border-b border-border bg-muted/20">
                              <td className={`sticky left-0 z-[1] pl-6 pr-3 py-1 ${modifiedZoneWeightRates[zone.id] ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-muted/30'}`}>
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs italic whitespace-nowrap">{zone.name}</span>
                                  <span className="text-[10px] border border-border rounded px-1 max-w-[180px] truncate font-mono" title={zone.postal_code_patterns}>{(zone.postal_code_patterns || '').split(',').length > 6 ? `${(zone.postal_code_patterns || '').split(',').slice(0, 4).join(',')} +${(zone.postal_code_patterns || '').split(',').length - 4}` : zone.postal_code_patterns}</span>
                                </div>
                              </td>
                              <td className="px-0.5 py-0.5 text-center text-xs text-muted-foreground italic">{Math.round((cr.fuel_surcharge_weight || 0) * 100)}%</td>
                              {Array.from({ length: 30 }, (_, i) => i + 1).map((kg) => {
                                const rate = carrierRates[kg.toString()];
                                const modified = modifiedZoneWeightRates[zone.id]?.[kg.toString()];
                                return (
                                  <td key={kg} className="px-0.5 py-0.5">
                                    <input
                                      type="number"
                                      step="0.1"
                                      defaultValue={rate?.cost?.toFixed(1) || '0'}
                                      onChange={(e) => handleZoneWeightRateChange(zone.id, kg.toString(), e.target.value)}
                                      className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-border bg-muted/20'}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    }); })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Volumetric Rates Tab */}
          {tabValue === 4 && (
            (() => {
              // No country is hidden any more (2026-10-02): BE/NL/LU/FR used to be
              // excluded as weight-only lanes, which hid real volumetric lanes there
              // (GLS Belgium EuroBusinessParcel to France since 2026-09-01, 32 rows,
              // ~80 orders a month) and made the carrier look undefined.
              const EXCLUDE_ISO = new Set();

              // Build country lookup
              const countryMap = {};
              countries.forEach(c => { countryMap[c.id] = c; });

              // Filter out excluded countries
              const filteredRates = volumetricRates.filter(cr => !EXCLUDE_ISO.has(cr.iso2_code));

              // Use EU-style tier columns for all countries
              // EU tiers: [1-10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 125, 150, 175, 200, 225, 250, 275, 300]
              const euCountryRates = filteredRates.filter(cr => countryMap[cr.country_id]?.is_eu);
              const tierSet = new Set();
              euCountryRates.forEach(cr => Object.keys(cr.rates).forEach(k => tierSet.add(parseInt(k))));
              zones.filter(z => !z.is_eu).forEach(z => Object.keys(z.volumetric_rates || {}).forEach(k => tierSet.add(parseInt(k))));
              const tiers = [...tierSet].sort((a, b) => a - b);
              if (tiers.length === 0) tiers.push(...[1,2,3,4,5,6,7,8,9,10,15,20,25,30,40,50,60,70,80,90,100,125,150,175,200,225,250,275,300]);

              // Split EU first, then Non-EU
              const sortByName = (a, b) => a.country_name.localeCompare(b.country_name) || (a.carrier_name || '').localeCompare(b.carrier_name || '');
              const euRates = filteredRates.filter(cr => countryMap[cr.country_id]?.is_eu).sort(sortByName);
              const nonEuRates = filteredRates.filter(cr => !countryMap[cr.country_id]?.is_eu).sort(sortByName);
              const q = volRateFilter?.toLowerCase() || '';

              return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Volumetric weight-based shipping rates (EUR per volumetric kg tier, {tiers.length} tiers). <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block w-3 h-3 rounded border border-emerald-400 bg-emerald-50/60" /> Official rate</span></p>
                  <div className="relative max-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input placeholder="Filter country..." value={volRateFilter} onChange={(e) => setVolRateFilter(e.target.value)} className="pl-8 h-8 text-xs" />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openAddRateModal('volumetric')}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add country + carrier
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExcelExport('volumetric')}>
                    <Download className="h-4 w-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => volumetricFileRef.current?.click()} disabled={saving}>
                    <Upload className="h-4 w-4 mr-1.5" /> Import Excel
                  </Button>
                  <input
                    ref={volumetricFileRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => { handleExcelImport('volumetric', e.target.files[0]); e.target.value = ''; }}
                  />
                  <Button
                    disabled={(Object.keys(modifiedVolumetricRates).length === 0 && Object.keys(modifiedZoneVolumetricRates).length === 0 && Object.keys(modifiedFuelSurcharges).length === 0) || saving}
                    onClick={async () => {
                      if (Object.keys(modifiedFuelSurcharges).length > 0) await saveFuelSurcharges();
                      for (const key of Object.keys(modifiedVolumetricRates)) await saveVolumetricRates(key);
                      for (const zid of Object.keys(modifiedZoneVolumetricRates)) await saveZoneVolumetricRates(parseInt(zid));
                      fetchData();
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save All ({Object.keys(modifiedVolumetricRates).length + Object.keys(modifiedZoneVolumetricRates).length + Object.keys(modifiedFuelSurcharges).length})
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[calc(100vh-300px)] border border-border rounded-md">
                <table className="text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-muted">
                      <th className="sticky left-0 z-20 bg-muted px-3 py-2 text-left font-medium min-w-[180px]">Country</th>
                      <th className="px-1 py-2 text-center font-medium text-xs min-w-[62px]" title="Fuel / extra cost surcharge applied on top of base rate">Fuel %</th>
                      {tiers.map((kg) => (
                        <th key={kg} className="px-1 py-2 text-center font-medium text-xs min-w-[60px]">{kg}kg</th>
                      ))}
                      <th className="px-2 py-2 text-center font-medium text-xs min-w-[70px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* EU countries section */}
                    {euRates.length > 0 && (
                      <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                        <td colSpan={tiers.length + 3} className="px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">EU Countries</td>
                      </tr>
                    )}
                    {(() => {
                      const filteredEu = euRates.filter(cr => !q || cr.country_name.toLowerCase().includes(q) || cr.iso2_code.toLowerCase().includes(q) || (cr.carrier_name || '').toLowerCase().includes(q));
                      const euCountByCountry = countryGroupCounts(filteredEu);
                      let lastEuCountryId = null;
                      return filteredEu.map((cr) => {
                      const crKey = rateKey(cr);
                      const countryZones = zones.filter(z => z.country_id === cr.country_id);
                      const allOfficial = Object.values(cr.rates).length > 0 && Object.values(cr.rates).every(r => r.is_official);
                      const isMultiCarrier = euCountByCountry[cr.country_id] > 1;
                      const showHeader = isMultiCarrier && cr.country_id !== lastEuCountryId;
                      lastEuCountryId = cr.country_id;
                      return (
                        <React.Fragment key={crKey}>
                          {showHeader && renderGroupHeader(cr, euCountByCountry[cr.country_id], tiers.length + 3)}
                          <tr className="border-b border-border hover:bg-muted/30">
                            <td className={`sticky left-0 z-[1] px-3 py-1 font-medium whitespace-nowrap ${modifiedVolumetricRates[crKey] ? 'bg-blue-50 dark:bg-blue-900/10' : 'bg-card'}`}>
                              {renderCountryCell(cr, isMultiCarrier, 'volumetric')}
                            </td>
                            {renderFuelCell(cr, 'volumetric')}
                            {tiers.map((kg) => {
                              const rate = cr.rates[kg.toString()];
                              const modified = modifiedVolumetricRates[crKey]?.[kg.toString()];
                              const isOfficial = rate?.is_official;
                              return (
                                <td key={kg} className="px-0.5 py-0.5">
                                  <input
                                    type="number"
                                    step="0.1"
                                    defaultValue={rate?.cost?.toFixed(1) || '0'}
                                    onChange={(e) => handleVolumetricRateChange(crKey, kg.toString(), e.target.value)}
                                    className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-1 py-0.5 text-center whitespace-nowrap">
                              <button
                                title={allOfficial ? 'Unmark as official' : 'Mark as official'}
                                onClick={() => toggleVolumetricOfficial(cr, allOfficial)}
                                className={`inline-flex items-center justify-center rounded p-1 text-xs ${allOfficial ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-muted-foreground hover:bg-muted'}`}
                              >
                                <span className={`inline-block w-3 h-3 rounded border ${allOfficial ? 'border-emerald-500 bg-emerald-400' : 'border-border bg-background'}`} />
                              </button>
                              <LaneSwitch cr={cr} rateType="volumetric" />
                              <button
                                title="Delete all rates"
                                onClick={() => deleteVolumetricRates(cr)}
                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-0.5"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                          {countryZones
                            .filter((zone) => {
                              const byCarrier = zone.volumetric_rates_by_carrier?.[cr.carrier_id];
                              return byCarrier && Object.keys(byCarrier).length > 0;
                            })
                            .map((zone) => {
                              const carrierRates = zone.volumetric_rates_by_carrier?.[cr.carrier_id] || {};
                              return (
                            <tr key={`zone-${zone.id}-${cr.carrier_id}`} className="border-b border-border bg-muted/20">
                              <td className={`sticky left-0 z-[1] pl-6 pr-3 py-1 ${modifiedZoneVolumetricRates[zone.id] ? 'bg-blue-50 dark:bg-blue-900/10' : 'bg-muted/30'}`}>
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs italic whitespace-nowrap">{zone.name}</span>
                                  <span className="text-[10px] border border-border rounded px-1 max-w-[180px] truncate font-mono" title={zone.postal_code_patterns}>{(zone.postal_code_patterns || '').split(',').length > 6 ? `${(zone.postal_code_patterns || '').split(',').slice(0, 4).join(',')} +${(zone.postal_code_patterns || '').split(',').length - 4}` : zone.postal_code_patterns}</span>
                                </div>
                              </td>
                              <td className="px-0.5 py-0.5 text-center text-xs text-muted-foreground italic">{Math.round((cr.fuel_surcharge_volumetric || 0) * 100)}%</td>
                              {tiers.map((kg) => {
                                const rate = carrierRates[kg.toString()];
                                const modified = modifiedZoneVolumetricRates[zone.id]?.[kg.toString()];
                                const isOfficial = rate?.is_official;
                                return (
                                  <td key={kg} className="px-0.5 py-0.5">
                                    <input
                                      type="number"
                                      step="0.1"
                                      defaultValue={rate?.cost?.toFixed(1) || '0'}
                                      onChange={(e) => handleZoneVolumetricRateChange(zone.id, kg.toString(), e.target.value)}
                                      className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-muted/20'}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    }); })()}
                    {/* Non-EU countries section */}
                    {nonEuRates.length > 0 && (
                      <tr className="bg-purple-50/50 dark:bg-purple-900/10">
                        <td colSpan={tiers.length + 3} className="px-3 py-1 text-xs font-semibold text-purple-700 dark:text-purple-300">Non-EU Countries</td>
                      </tr>
                    )}
                    {(() => {
                      const filteredNonEu = nonEuRates.filter(cr => !q || cr.country_name.toLowerCase().includes(q) || cr.iso2_code.toLowerCase().includes(q) || (cr.carrier_name || '').toLowerCase().includes(q));
                      const nonEuCountByCountry = countryGroupCounts(filteredNonEu);
                      let lastNonEuCountryId = null;
                      return filteredNonEu.map((cr) => {
                      const crKey = rateKey(cr);
                      const countryZones = zones.filter(z => z.country_id === cr.country_id);
                      const allOfficial = Object.values(cr.rates).length > 0 && Object.values(cr.rates).every(r => r.is_official);
                      const isMultiCarrier = nonEuCountByCountry[cr.country_id] > 1;
                      const showHeader = isMultiCarrier && cr.country_id !== lastNonEuCountryId;
                      lastNonEuCountryId = cr.country_id;
                      return (
                        <React.Fragment key={crKey}>
                          {showHeader && renderGroupHeader(cr, nonEuCountByCountry[cr.country_id], tiers.length + 3)}
                          <tr className="border-b border-border hover:bg-muted/30">
                            <td className={`sticky left-0 z-[1] px-3 py-1 font-medium whitespace-nowrap ${modifiedVolumetricRates[crKey] ? 'bg-blue-50 dark:bg-blue-900/10' : 'bg-card'}`}>
                              {renderCountryCell(cr, isMultiCarrier, 'volumetric')}
                            </td>
                            {renderFuelCell(cr, 'volumetric')}
                            {tiers.map((kg) => {
                              const rate = cr.rates[kg.toString()];
                              const modified = modifiedVolumetricRates[crKey]?.[kg.toString()];
                              const isOfficial = rate?.is_official;
                              return (
                                <td key={kg} className="px-0.5 py-0.5">
                                  <input
                                    type="number"
                                    step="0.1"
                                    defaultValue={rate?.cost?.toFixed(1) || '0'}
                                    onChange={(e) => handleVolumetricRateChange(crKey, kg.toString(), e.target.value)}
                                    className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-1 py-0.5 text-center whitespace-nowrap">
                              <button
                                title={allOfficial ? 'Unmark as official' : 'Mark as official'}
                                onClick={() => toggleVolumetricOfficial(cr, allOfficial)}
                                className={`inline-flex items-center justify-center rounded p-1 text-xs ${allOfficial ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-muted-foreground hover:bg-muted'}`}
                              >
                                <span className={`inline-block w-3 h-3 rounded border ${allOfficial ? 'border-emerald-500 bg-emerald-400' : 'border-border bg-background'}`} />
                              </button>
                              <LaneSwitch cr={cr} rateType="volumetric" />
                              <button
                                title="Delete all rates"
                                onClick={() => deleteVolumetricRates(cr)}
                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-0.5"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                          {countryZones
                            .filter((zone) => {
                              const byCarrier = zone.volumetric_rates_by_carrier?.[cr.carrier_id];
                              return byCarrier && Object.keys(byCarrier).length > 0;
                            })
                            .map((zone) => {
                              const carrierRates = zone.volumetric_rates_by_carrier?.[cr.carrier_id] || {};
                              return (
                            <tr key={`zone-${zone.id}-${cr.carrier_id}`} className="border-b border-border bg-muted/20">
                              <td className={`sticky left-0 z-[1] pl-6 pr-3 py-1 ${modifiedZoneVolumetricRates[zone.id] ? 'bg-blue-50 dark:bg-blue-900/10' : 'bg-muted/30'}`}>
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs italic whitespace-nowrap">{zone.name}</span>
                                  <span className="text-[10px] border border-border rounded px-1 max-w-[180px] truncate font-mono" title={zone.postal_code_patterns}>{(zone.postal_code_patterns || '').split(',').length > 6 ? `${(zone.postal_code_patterns || '').split(',').slice(0, 4).join(',')} +${(zone.postal_code_patterns || '').split(',').length - 4}` : zone.postal_code_patterns}</span>
                                </div>
                              </td>
                              <td className="px-0.5 py-0.5 text-center text-xs text-muted-foreground italic">{Math.round((cr.fuel_surcharge_volumetric || 0) * 100)}%</td>
                              {tiers.map((kg) => {
                                const rate = carrierRates[kg.toString()];
                                const modified = modifiedZoneVolumetricRates[zone.id]?.[kg.toString()];
                                const isOfficial = rate?.is_official;
                                return (
                                  <td key={kg} className="px-0.5 py-0.5">
                                    <input
                                      type="number"
                                      step="0.1"
                                      defaultValue={rate?.cost?.toFixed(1) || '0'}
                                      onChange={(e) => handleZoneVolumetricRateChange(zone.id, kg.toString(), e.target.value)}
                                      className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-muted/20'}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    }); })()}
                  </tbody>
                </table>
              </div>
            </div>
              );
            })()
          )}

          {/* Per-Parcel Rates Tab */}
          {tabValue === 5 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Per-parcel shipping rates for BE, NL, LU. Cost depends on total number of packages.</p>
                  <Button variant="outline" size="sm" onClick={() => openAddRateModal('coli')}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add country + carrier
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExcelExport('coli')}>
                    <Download className="h-4 w-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => coliFileRef.current?.click()} disabled={saving}>
                    <Upload className="h-4 w-4 mr-1.5" /> Import Excel
                  </Button>
                  <input
                    ref={coliFileRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => { handleExcelImport('coli', e.target.files[0]); e.target.value = ''; }}
                  />
                  <Button
                    disabled={(Object.keys(modifiedColiRates).length === 0 && Object.keys(modifiedFuelSurcharges).length === 0) || saving}
                    onClick={async () => {
                      if (Object.keys(modifiedFuelSurcharges).length > 0) await saveFuelSurcharges();
                      for (const key of Object.keys(modifiedColiRates)) {
                        const [cid, carId] = key.split('_');
                        await saveColiRates(key, parseInt(cid), carId ? parseInt(carId) : null);
                      }
                      fetchData();
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save All ({Object.keys(modifiedColiRates).length + Object.keys(modifiedFuelSurcharges).length})
                  </Button>
                </div>
              </div>
              {coliRates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>No per-parcel rates configured yet.</p>
                  <p className="text-xs mt-1">Import official rates to populate BE, NL, LU per-parcel pricing.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-border rounded-md">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-3 py-2 text-left font-medium min-w-[180px]">Country</th>
                        <th className="px-1 py-2 text-center font-medium text-xs min-w-[62px]" title="Fuel / extra cost surcharge applied on top of base rate">Fuel %</th>
                        {[1, 2, 3, 4, 5].map(n => (
                          <th key={n} className="px-2 py-2 text-center font-medium min-w-[90px]">{n} pkg</th>
                        ))}
                        <th className="px-2 py-2 text-center font-medium min-w-[100px]">Per Additional</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const sortedColi = [...coliRates].sort((a, b) => a.country_name.localeCompare(b.country_name) || (a.carrier_name || '').localeCompare(b.carrier_name || ''));
                        const coliCountByCountry = countryGroupCounts(sortedColi);
                        let lastColiCountryId = null;
                        return sortedColi.map((cr) => {
                        const crKey = rateKey(cr);
                        const isMultiCarrier = coliCountByCountry[cr.country_id] > 1;
                        const showHeader = isMultiCarrier && cr.country_id !== lastColiCountryId;
                        lastColiCountryId = cr.country_id;
                        return (
                        <React.Fragment key={crKey}>
                        {showHeader && renderGroupHeader(cr, coliCountByCountry[cr.country_id], 9)}
                        <tr className="border-b border-border hover:bg-muted/30">
                          <td className={`px-3 py-2 font-medium whitespace-nowrap ${modifiedColiRates[crKey] ? 'bg-teal-50 dark:bg-teal-900/10' : ''}`}>
                            {renderCountryCell(cr, isMultiCarrier, 'coli')}
                          </td>
                          {renderFuelCell(cr, 'coli')}
                          {[1, 2, 3, 4, 5].map(n => {
                            const rate = cr.rates[n.toString()];
                            const modified = modifiedColiRates[crKey]?.rates?.[n.toString()];
                            const isOfficial = rate?.is_official;
                            return (
                              <td key={n} className="px-1 py-1 text-center">
                                <input
                                  type="number"
                                  step="0.01"
                                  defaultValue={rate?.cost?.toFixed(2) || '0'}
                                  onChange={(e) => handleColiRateChange(crKey, n.toString(), e.target.value)}
                                  className={`w-20 mx-auto rounded border px-2 py-1.5 text-right text-xs ${modified !== undefined ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                />
                              </td>
                            );
                          })}
                          <td className="px-1 py-1 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs text-muted-foreground">+</span>
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={cr.coli_additional_cost?.toFixed(2) || '0'}
                                onChange={(e) => handleColiAdditionalChange(crKey, e.target.value)}
                                className={`w-20 rounded border px-2 py-1.5 text-right text-xs ${modifiedColiRates[crKey]?.additional !== undefined ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20' : 'border-border bg-background'}`}
                              />
                              <span className="text-xs text-muted-foreground">/pkg</span>
                              <LaneSwitch cr={cr} rateType="coli" />
                            </div>
                          </td>
                        </tr>
                        </React.Fragment>
                        );
                      }); })()}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-3 h-3 rounded border border-emerald-400 bg-emerald-50/60" />
                <span>Green = Official carrier rate</span>
              </div>
            </div>
          )}

          {/* Pallet Rates Tab */}
          {tabValue === 6 && (
            <div>
              <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-2.5 text-xs flex items-start gap-2">
                <Archive className="h-4 w-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
                <div>
                  <strong>Switched off: these fixed prices are not used anywhere</strong> (since 2026-10-02, <code>PALLET_PER_SIZE_RATES_ENABLED = False</code>).
                  A pallet is priced by weight on the carrier cards in the <button type="button" onClick={() => setTabValue(12)} className="underline font-semibold">Pallet
                  Rates</button> tab, on the messagerie freight bands, or on a postal zone&rsquo;s own pallet rows (islands). The rows are kept for
                  reference only. Pallet fuel is set per lane on the Carriers tab, not here.
                  <div className="mt-1"></div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Per-pallet-type shipping rates. Each column is a pallet size (WxHxD cm), each row is a country.</p>
                  <Button variant="outline" size="sm" onClick={() => openAddRateModal('pallet')}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add country + carrier
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search countries..." value={palletRateFilter} onChange={(e) => setPalletRateFilter(e.target.value)} className="pl-9 h-9" />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleExcelExport('pallet')}>
                    <Download className="h-4 w-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => palletFileRef.current?.click()} disabled={saving}>
                    <Upload className="h-4 w-4 mr-1.5" /> Import Excel
                  </Button>
                  <input
                    ref={palletFileRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => { handleExcelImport('pallet', e.target.files[0]); e.target.value = ''; }}
                  />
                  <Button
                    disabled={(Object.keys(modifiedPalletRates).length === 0 && Object.keys(modifiedZonePalletRates).length === 0 && Object.keys(modifiedFuelSurcharges).length === 0) || saving}
                    onClick={async () => {
                      if (Object.keys(modifiedFuelSurcharges).length > 0) await saveFuelSurcharges();
                      for (const key of Object.keys(modifiedPalletRates)) {
                        const [cid, carId] = key.split('_');
                        await savePalletRates(key, parseInt(cid), carId ? parseInt(carId) : null);
                      }
                      for (const key of Object.keys(modifiedZonePalletRates)) await saveZonePalletRates(key);
                      fetchData();
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save All ({Object.keys(modifiedPalletRates).length + Object.keys(modifiedZonePalletRates).length + Object.keys(modifiedFuelSurcharges).length})
                  </Button>
                </div>
              </div>
              {/* How a pallet is priced per destination: freight bands (by weight) vs the per-size table */}
              <div className="mb-4 rounded-lg border border-teal-200 dark:border-teal-900/60 bg-teal-50/60 dark:bg-teal-950/20 px-4 py-3 text-xs">
                <div className="font-semibold text-sm mb-1">How a pallet is priced per destination</div>
                {Object.values(freightByCountry).length === 0 ? (
                  <p className="text-muted-foreground">Every country: the per-size pallet rates below (fuel per row); the cheapest carrier that fits the pallet wins.</p>
                ) : (
                  <ul className="space-y-1">
                    {Object.values(freightByCountry).sort((a, b) => a.country_name.localeCompare(b.country_name)).map(c => (
                      <li key={c.iso2_code}>
                        <strong>{c.country_name}</strong>: priced <strong>by weight</strong> on{' '}
                        {Object.entries(c.carriers).map(([name, k], i) => (
                          <span key={name}>{i > 0 ? ' and ' : ''}<strong>{name}</strong> freight bands ({k.zones > 0 ? `${k.zones} postcode zone${k.zones > 1 ? 's' : ''}` : 'whole country'}, {k.min_kg}–{k.max_kg} kg)</span>
                        ))}
                        . The per-size rows for {c.country_name} below are the <strong>fallback only</strong>: over {(freightPalletMode?.max_length_cm || 400) / 100} m long, {(freightPalletMode?.max_height_cm || 200) / 100} m high or the top band
                        {freightPalletMode?.mode && freightPalletMode.mode !== 'replace' ? ` (mode ${freightPalletMode.mode}: the cheaper of the two is taken)` : ''}.{' '}
                        <a href="#freight-by-weight" className="underline text-teal-800 dark:text-teal-300">See the bands</a>
                      </li>
                    ))}
                    <li className="text-muted-foreground">Every other country: the per-size pallet rates below (fuel per row); the cheapest carrier that fits the pallet wins. The 1–30 kg weight bands and the volumetric bands price <strong>parcels</strong>, never a pallet.</li>
                  </ul>
                )}
              </div>
              {palletBins.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Truck className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>No pallet types configured yet.</p>
                  <p className="text-xs mt-1">Run migrations to create pallet bins and default rates.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-border rounded-md">
                  {(() => {
                    const groupColors = ['bg-blue-50/60 dark:bg-blue-950/20', 'bg-violet-50/60 dark:bg-violet-950/20', 'bg-amber-50/60 dark:bg-amber-950/20', 'bg-rose-50/60 dark:bg-rose-950/20'];
                    const groupHeaderColors = ['bg-blue-100/80 dark:bg-blue-900/30', 'bg-violet-100/80 dark:bg-violet-900/30', 'bg-amber-100/80 dark:bg-amber-900/30', 'bg-rose-100/80 dark:bg-rose-900/30'];
                    const groupTextColors = ['text-blue-700 dark:text-blue-300', 'text-violet-700 dark:text-violet-300', 'text-amber-700 dark:text-amber-300', 'text-rose-700 dark:text-rose-300'];
                    // Build group info
                    const groups = [];
                    let prev = null;
                    palletBins.forEach((bin, i) => {
                      if (bin.group !== prev) {
                        groups.push({ group: bin.group, startIdx: i, count: 1 });
                        prev = bin.group;
                      } else {
                        groups[groups.length - 1].count++;
                      }
                    });
                    // Map bin index to group index
                    const binGroupIdx = palletBins.map((bin, i) => groups.findIndex(g => g.group === bin.group));
                    return (
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        {/* Group header row */}
                        <tr>
                          <th rowSpan={2} className="px-3 py-2 text-left font-medium min-w-[180px] sticky left-0 bg-muted z-10 border-b border-border">Country</th>
                          <th rowSpan={2} className="px-1 py-2 text-center font-medium text-xs min-w-[62px] border-b border-border bg-muted" title="Fuel / extra cost surcharge applied on top of base rate">Fuel %</th>
                          {groups.map((g, gi) => (
                            <th key={g.group} colSpan={g.count} className={`px-2 py-2 text-center font-semibold text-xs border-b border-border ${gi > 0 ? 'border-l-2 border-l-border' : ''} ${groupHeaderColors[gi]}`}>
                              <span className={groupTextColors[gi]}>{g.group} cm</span>
                            </th>
                          ))}
                          <th rowSpan={2} className="px-2 py-2 text-center font-medium min-w-[60px] border-b border-border border-l-2 border-l-border bg-muted" />
                        </tr>
                        {/* Sub-header: heights */}
                        <tr>
                          {palletBins.map((bin, i) => {
                            const gi = binGroupIdx[i];
                            const isGroupStart = i === groups[gi].startIdx;
                            return (
                              <th key={bin.id} className={`px-2 py-1.5 text-center min-w-[100px] border-b border-border ${isGroupStart && gi > 0 ? 'border-l-2 border-l-border' : ''} ${groupColors[gi]}`}>
                                <div className={`text-[11px] font-medium ${groupTextColors[gi]}`}>{bin.height_label}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sortedPallet = palletRates
                            .filter(pr => !palletRateFilter || pr.country_name.toLowerCase().includes(palletRateFilter.toLowerCase()) || pr.iso2_code.toLowerCase().includes(palletRateFilter.toLowerCase()) || (pr.carrier_name || '').toLowerCase().includes(palletRateFilter.toLowerCase()))
                            .sort((a, b) => a.country_name.localeCompare(b.country_name) || (a.carrier_name || '').localeCompare(b.carrier_name || ''));
                          const palletCountByCountry = countryGroupCounts(sortedPallet);
                          let lastPalletCountryId = null;
                          return sortedPallet.map((pr) => {
                          const prKey = rateKey(pr);
                          const allOfficial = Object.values(pr.rates).length > 0 && Object.values(pr.rates).every(r => r.is_official);
                          const isMultiCarrier = palletCountByCountry[pr.country_id] > 1;
                          const showHeader = isMultiCarrier && pr.country_id !== lastPalletCountryId;
                          const countryZones = zones.filter(z => z.country_id === pr.country_id);
                          lastPalletCountryId = pr.country_id;
                          return (
                          <React.Fragment key={prKey}>
                          {showHeader && renderGroupHeader(pr, palletCountByCountry[pr.country_id], palletBins.length + 3)}
                          <tr className="border-b border-border hover:bg-muted/30">
                            <td className={`px-3 py-1.5 font-medium whitespace-nowrap sticky left-0 z-10 ${modifiedPalletRates[prKey] ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-card'}`}>
                              {renderCountryCell(pr, isMultiCarrier, 'pallet')}
                              {freightByCountry[pr.country_id] && (
                                <span
                                  className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                                  title={`Pallets to ${pr.country_name} are priced by weight on ${Object.keys(freightByCountry[pr.country_id].carriers).join(' / ')} freight bands; these per-size rates are the fallback beyond the freight limits.`}
                                >
                                  by weight · {Object.keys(freightByCountry[pr.country_id].carriers).join(' / ')}
                                </span>
                              )}
                            </td>
                            {renderFuelCell(pr, 'pallet')}
                            {palletBins.map((bin, i) => {
                              const rate = pr.rates[bin.id.toString()];
                              const modified = modifiedPalletRates[prKey]?.[bin.id.toString()];
                              const isOfficial = rate?.is_official;
                              const gi = binGroupIdx[i];
                              const isGroupStart = i === groups[gi].startIdx;
                              return (
                                <td key={bin.id} className={`px-1 py-1 text-center ${groupColors[gi]} ${isGroupStart && gi > 0 ? 'border-l-2 border-l-border' : ''}`}>
                                  <input
                                    type="number"
                                    step="1"
                                    defaultValue={rate?.cost != null ? Math.round(rate.cost) : ''}
                                    onChange={(e) => handlePalletRateChange(prKey, bin.id.toString(), e.target.value)}
                                    className={`w-[76px] mx-auto rounded border px-2 py-1.5 text-right text-xs ${modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-white dark:bg-background'}`}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-1 py-0.5 text-center whitespace-nowrap border-l-2 border-l-border">
                              <button
                                title={allOfficial ? 'Unmark as official' : 'Mark as official'}
                                onClick={() => togglePalletOfficial(pr, allOfficial)}
                                className={`inline-flex items-center justify-center rounded p-1 text-xs ${allOfficial ? 'text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : 'text-muted-foreground hover:bg-muted'}`}
                              >
                                <span className={`inline-block w-3 h-3 rounded border ${allOfficial ? 'border-emerald-500 bg-emerald-400' : 'border-border bg-background'}`} />
                              </button>
                              <LaneSwitch cr={pr} rateType="pallet" hint={'\nSwitches this carrier\'s pallets off for the whole country: its card, zone rows and these rows.'} />
                              <button
                                title="Delete all rates"
                                onClick={() => deletePalletRates(pr)}
                                className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-0.5"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                          {/* Zone rows — only those that have rates for THIS carrier */}
                          {countryZones
                            .filter((zone) => {
                              const byCarrier = zone.pallet_rates_by_carrier?.[pr.carrier_id];
                              return byCarrier && Object.keys(byCarrier).length > 0;
                            })
                            .map((zone) => {
                              const carrierRates = zone.pallet_rates_by_carrier?.[pr.carrier_id] || {};
                              const zoneKey = `${zone.id}_${pr.carrier_id ?? ''}`;
                              return (
                            <tr key={`zone-pallet-${zone.id}-${pr.carrier_id}`} className="border-b border-border bg-muted/20">
                              <td className={`sticky left-0 z-10 pl-6 pr-3 py-1 ${modifiedZonePalletRates[zoneKey] ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-muted/30'}`}>
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs italic whitespace-nowrap">{zone.name}</span>
                                  <span className="text-[10px] border border-border rounded px-1 max-w-[180px] truncate font-mono" title={zone.postal_code_patterns}>{(zone.postal_code_patterns || '').split(',').length > 6 ? `${(zone.postal_code_patterns || '').split(',').slice(0, 4).join(',')} +${(zone.postal_code_patterns || '').split(',').length - 4}` : zone.postal_code_patterns}</span>
                                </div>
                              </td>
                              <td className="px-0.5 py-0.5 text-center text-xs text-muted-foreground italic">{Math.round((pr.fuel_surcharge_pallet || 0) * 100)}%</td>
                              {palletBins.map((bin, i) => {
                                const rate = carrierRates[bin.id.toString()];
                                const modified = modifiedZonePalletRates[zoneKey]?.[bin.id.toString()];
                                const gi = binGroupIdx[i];
                                const isGroupStart = i === groups[gi].startIdx;
                                return (
                                  <td key={bin.id} className={`px-1 py-1 text-center ${groupColors[gi]} ${isGroupStart && gi > 0 ? 'border-l-2 border-l-border' : ''}`}>
                                    <input
                                      type="number"
                                      step="1"
                                      defaultValue={rate?.cost != null ? Math.round(rate.cost) : ''}
                                      onChange={(e) => handleZonePalletRateChange(zone.id, pr.carrier_id, bin.id.toString(), e.target.value)}
                                      className={`w-[76px] mx-auto rounded border px-2 py-1.5 text-right text-xs ${modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-border bg-muted/20'}`}
                                    />
                                  </td>
                                );
                              })}
                              <td className="px-1 py-0.5 text-center border-l-2 border-l-border" />
                            </tr>
                              );
                            })}
                          </React.Fragment>
                          );
                        }); })()}
                      </tbody>
                    </table>
                    );
                  })()}
                </div>
              )}
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-emerald-400 bg-emerald-50/60" />
                  <span>Official rate</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-orange-400 bg-orange-50" />
                  <span>Modified (unsaved)</span>
                </div>
                <span>{palletBins.length} pallet types × {palletRates.length} countries</span>
              </div>

              {/* Freight by weight: pallets priced on messagerie weight bands (read-only, loaded by manage.py load_geodis_freight_bands) */}
              {freightRates.length > 0 && (
                <div className="mt-8" id="freight-by-weight">
                  <h3 className="text-sm font-semibold">Freight by weight (pallets priced on the messagerie grid)</h3>
                  <p className="text-xs mb-1"><strong>These bands price pallets.</strong> The same carrier&rsquo;s 1&ndash;30 kg weight bands (Weight tab, zone rows) price parcels and are never used for a pallet.</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Where a carrier holds these bands, a pallet is priced by its <strong>weight</strong> (items + pallet tare) instead of the per-size
                    pallet rate above: forfait bands up to 99 kg, then a price per 100 kg on the weight rounded up to 10 kg (cents half-up),
                    max 1 t, 4 m long, 2 m high; beyond that the per-size rate applies again. Read-only: loaded from Jérémy&rsquo;s grid with{' '}
                    <code className="text-[11px] bg-muted px-1 rounded">manage.py load_geodis_freight_bands</code>. Fuel comes from the carrier&rsquo;s pallet assignment.
                  </p>
                  {(() => {
                    const byCarrier = {};
                    freightRates.forEach(fr => { (byCarrier[fr.carrier_name] = byCarrier[fr.carrier_name] || []).push(fr); });
                    return Object.entries(byCarrier).map(([carrierName, rows]) => {
                      const bands = rows[0].bands;
                      const sorted = [...rows].sort((a, b) => (a.bands[0]?.cost || 0) - (b.bands[0]?.cost || 0));
                      return (
                        <div key={carrierName} className="mb-4">
                          <div className="text-xs font-medium mb-1">{carrierName} <span className="text-muted-foreground">· {rows.length} {rows[0].scope === 'zone' ? 'zones' : 'countries'}</span></div>
                          <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-border bg-muted/50">
                                  <th className="px-2 py-1.5 text-left font-medium">Zone / country</th>
                                  <th className="px-2 py-1.5 text-left font-medium">Postcodes</th>
                                  {bands.map(b => (
                                    <th key={b.min_kg} className="px-2 py-1.5 text-right font-medium whitespace-nowrap" title={b.per_100kg ? 'price per 100 kg, weight rounded up to 10 kg' : 'flat price for the band'}>
                                      {b.min_kg}–{b.max_kg}{b.per_100kg ? ' /100kg' : ''}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sorted.map(fr => (
                                  <tr key={(fr.zone_id || fr.country_id) + '-' + fr.carrier_id} className="border-b border-border hover:bg-muted/30">
                                    <td className="px-2 py-1 whitespace-nowrap">{fr.scope === 'zone' ? fr.zone_name : fr.country_name}</td>
                                    <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground max-w-[220px] truncate" title={fr.postal_code_patterns || ''}>{fr.postal_code_patterns || fr.iso2_code}</td>
                                    {fr.bands.map(b => (
                                      <td key={b.min_kg} className={`px-2 py-1 text-right tabular-nums ${b.per_100kg ? 'text-teal-800 dark:text-teal-300' : ''}`}>{Number(b.cost).toFixed(2)}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Pallet Rates Tab — dedicated pallet cards priced on weight bands */}
          {tabValue === 12 && (
            <div>
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-lg border border-border overflow-hidden">
                    {[['volumetric', 'Volumetric'], ['real', 'Real weight'], ['per_size', 'Per pallet size']].map(([v, label]) => (
                      <button
                        key={v}
                        onClick={() => setPalletBandBasis(v)}
                        className={`px-3 py-1.5 text-xs font-medium ${palletBandBasis === v ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground max-w-2xl">
                    {palletBandBasis === 'volumetric'
                      ? 'Carrier pallet cards priced on the CHARGEABLE weight (the higher of real and volumetric). EUR per band, excluding VAT, fuel and surcharges.'
                      : palletBandBasis === 'real'
                      ? 'Messagerie bands priced on the REAL weight of the pallet (Geodis France, per departement tier).'
                      : 'One flat price per pallet SIZE, the quickest way to see what a standard pallet costs to a country. Kept as the fallback wherever a carrier card is dearer or does not cover the weight.'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search country or carrier..." value={palletBandFilter}
                           onChange={(e) => setPalletBandFilter(e.target.value)} className="pl-9 h-9" />
                  </div>
                  <Input placeholder="Postcode..." value={palletBandProbe}
                         onChange={(e) => setPalletBandProbe(e.target.value)} className="h-9 w-[130px]"
                         title="Which lane does this destination resolve to? Filter by country to read it." />
                  <Input placeholder="kg" value={palletBandProbeWeight} type="number"
                         onChange={(e) => setPalletBandProbeWeight(e.target.value)} className="h-9 w-[80px]"
                         title="Optional: drops lanes whose weight cap this exceeds" />
                  <Button variant="outline" size="sm"
                          onClick={handlePalletBandExport}
                          title="Export the volumetric pallet grid to Excel">
                    <Download className="h-4 w-4 mr-1.5" /> Export Excel
                  </Button>
                  <Button variant="outline" size="sm" disabled={saving || palletBandBasis !== 'volumetric'}
                          onClick={() => palletBandFileRef.current?.click()}
                          title={palletBandBasis === 'volumetric'
                            ? 'Import an edited sheet. A blank cell means the carrier refuses that weight.'
                            : 'The real-weight bands have their own loader; switch to Volumetric to import.'}>
                    <Upload className="h-4 w-4 mr-1.5" /> Import Excel
                  </Button>
                  <input ref={palletBandFileRef} type="file" accept=".xlsx" className="hidden"
                         onChange={(e) => { handlePalletBandImport(e.target.files[0]); e.target.value = ''; }} />
                </div>
              </div>

              <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-2.5 text-xs">
                {palletBandData?.basis === 'per_size'
                  ? <><strong>Computed, not stored.</strong> Every price here is an <strong>empty</strong> pallet of that size run through the
                      live engine, so it is the number a quote would use, fuel included. Hover a price to see which card and band it came
                      from. Nothing on this view is editable: change the bands on the Volumetric or Real weight views and these follow.{' '}</>
                  : palletBandData?.mode === 'replace'
                  ? <><strong>These rates price every pallet.</strong> A pallet is priced from the carrier cards by weight, including
                      wide bases; in France the Geodis messagerie bands compete on real weight and usually win on a light pallet. The
                      per-size <strong>Pallet</strong> tab is only the fallback for a lane with no card. Island and remote zones with
                      their own pallet or freight rates are never undercut by a national card, and a destination with no card of its own
                      falls through to the Rest of World card. On top of this the pallet is billed on the <strong>load it carries</strong>
                      (tallest item + 15&nbsp;cm deck), not on the pallet it was chosen from.{' '}</>
                  : palletBandData?.mode && palletBandData.mode !== 'off'
                  ? <><strong>These rates are live.</strong> A pallet is priced from the carrier cards, and the per-size table is kept wherever
                      it is cheaper, so no lane can get dearer than before. Island and remote zones keep their own rates, and wide-base pallets
                      keep the 2&times; rule.{' '}</>
                  : <><strong>Not used for pricing yet.</strong> These rates sit next to the per-size Pallet tab and change no quote until the
                      pallet engine is switched over to them.{' '}</>}
 These prices <strong>exclude fuel</strong>, like the carrier cards themselves: the engine adds the
                percentage in the Fuel column, which comes from the carrier's pallet assignment (or the country default, marked *) and is
                edited in the <strong>Pallet</strong> tab. The EUR 15 pallet packaging is added once per pallet on top.
                A pallet is priced at the <strong>cheapest band at or above</strong> its weight,
                never simply the first one: the Geodis cards are degressive, so crossing a band can lower the total.
                Where a country has several lanes for one carrier, the destination picks it: the most specific postal match wins,
                and when nothing matches (the North / South lanes carry no postcodes) the <strong>dearest</strong> lane is used so a
                missed match never under-quotes. Type a postcode above to see which lane a destination lands on.
              </div>
              {(palletBandData?.carrier_rules || []).length > 0 && (
                <div className="mb-3 rounded-lg border border-border bg-card px-4 py-2.5 text-xs">
                  <div className="font-medium mb-1">How each carrier counts a pallet's weight</div>
                  <div className="text-muted-foreground mb-1.5">
                    A band is looked up on the <strong>chargeable</strong> weight: the highest of the terms below. Floor space
                    (floor metres = length &times; width / 2.4 m&sup2;) ignores height, so a pallet cut does not lower it.
                  </div>
                  <ul className="space-y-0.5">
                    {(palletBandData.carrier_rules || []).map(r => (
                      <li key={r.carrier_id}>
                        <span className="font-medium">{r.carrier_name}</span>:{' '}
                        {r.weight_basis === 'real'
                          ? 'real weight (per-pallet forfaits)'
                          : r.kg_per_m3
                          ? <>highest of real weight, volume &times; {r.kg_per_m3} kg/m&sup3;{r.kg_per_floor_m ? <>, <strong>floor metres &times; {r.kg_per_floor_m} kg</strong></> : ''}</>
                          : `highest of real weight and L×W×H ÷ ${r.volumetric_divisor}`}
                        {r.min_kg ? `, minimum ${r.min_kg} kg` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {palletBandData?.basis === 'per_size' && (palletBandData?.lanes || []).length ? (() => {
                const q = palletBandFilter.trim().toLowerCase();
                const lanes = (palletBandData.lanes || []).filter(l => !q
                  || l.country_name.toLowerCase().includes(q) || (l.iso2_code || '').toLowerCase().includes(q)
                  || (l.carrier_name || '').toLowerCase().includes(q));
                const sizes = palletBandData.bands || [];
                const labels = palletBandData.bin_labels || {};
                // Twelve size columns are really four BASES at three heights.
                // Group them so the eye compares like with like, and so the
                // height is read once per column instead of three times.
                const baseGroups = [];
                sizes.forEach(n => {
                  const parts = (labels[n] || n.replace('PLT-', '')).split('x');
                  const base = parts.slice(0, 2).join('\u00D7');
                  const height = parts[2] || '';
                  let g = baseGroups.find(x => x.base === base);
                  if (!g) { g = { base, cols: [] }; baseGroups.push(g); }
                  g.cols.push({ name: n, height });
                });
                return (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">
                      {lanes.length} countr{lanes.length === 1 ? 'y' : 'ies'} · what an <strong>empty</strong> pallet of each size costs,
                      priced exactly as a quote prices it, fuel included. Hover a price to see which card and band it came from.
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-muted/60">
                            <th rowSpan={2} className="px-2 py-2 text-left font-medium sticky left-0 bg-muted/60 z-10 min-w-[170px] align-bottom">Country</th>
                            <th rowSpan={2} className="px-2 py-2 text-left font-medium min-w-[200px] align-bottom">Carrier(s) used</th>
                            {baseGroups.map((g, gi) => (
                              <th key={g.base} colSpan={g.cols.length}
                                  className={`px-2 pt-2 pb-1 text-center font-semibold whitespace-nowrap tracking-wide ${gi ? 'border-l-2 border-border' : ''}`}>
                                {g.base} <span className="font-normal text-muted-foreground">cm base</span>
                              </th>
                            ))}
                          </tr>
                          <tr className="border-b border-border bg-muted/60">
                            {baseGroups.map((g, gi) => g.cols.map((c, ci) => (
                              <th key={c.name}
                                  className={`px-2 pb-2 text-right font-normal text-muted-foreground whitespace-nowrap ${gi && ci === 0 ? 'border-l-2 border-border' : ''}`}
                                  title={c.name}>
                                {c.height} cm
                              </th>
                            )))}
                          </tr>
                        </thead>
                        <tbody>
                          {lanes.map((l, li) => (
                            <tr key={l.key} className={`border-b border-border hover:bg-muted/40 ${li % 2 ? 'bg-muted/20' : ''}`}>
                              <td className={`px-2 py-1.5 whitespace-nowrap sticky left-0 z-10 ${li % 2 ? 'bg-[#f7f7f8] dark:bg-[#1b1b1d]' : 'bg-card'}`}>
                                <CountryCell iso2={l.iso2_code} name={l.country_name} />
                              </td>
                              <td className="px-2 py-1.5 max-w-[230px]" title={l.carrier_name}>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {(l.carrier_name ? l.carrier_name.split(', ') : []).map(cn => (
                                    <button key={cn} type="button" onClick={() => openCarrier(cn)}
                                            title={`${cn}\nOpen in the Carriers tab`}
                                            className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-muted transition-colors">
                                      <CarrierLogo name={cn} className="h-4" />
                                      <span className="text-[11px] text-muted-foreground">{cn.replace(/\s*\(.*\)$/, '')}</span>
                                    </button>
                                  ))}
                                  {!l.carrier_name && <span className="text-muted-foreground">—</span>}
                                </div>
                              </td>
                              {baseGroups.map((g, gi) => g.cols.map((c, ci) => {
                                const v = l.rates[c.name];
                                const m = (l.methods || {})[c.name] || '';
                                const card = m.startsWith('pallet_card');
                                return (
                                  <td key={c.name} title={m}
                                      className={`px-2 py-1.5 text-right tabular-nums ${card ? '' : 'text-muted-foreground'} ${gi && ci === 0 ? 'border-l-2 border-border' : ''}`}>
                                    {v != null ? v.toFixed(2) : ''}
                                  </td>
                                );
                              }))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Greyed prices come from the per-size fallback table rather than a carrier card (island zones, wide-base pallets,
                      or weights no card covers).
                    </p>
                  </>
                );
              })() : (palletBandLoading || (palletBandData && palletBandData.basis !== palletBandBasis)) ? (
                <div className="text-center py-12 text-muted-foreground text-sm">Loading pallet rates...</div>
              ) : !(palletBandData?.lanes || []).length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Weight className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>No {palletBandBasis === 'real' ? 'real-weight' : palletBandBasis === 'per_size' ? 'per-pallet-size' : 'volumetric'} pallet rates loaded.</p>
                  {palletBandBasis !== 'per_size' && (
                    <p className="text-xs mt-1">Load them with <code className="text-[11px] bg-muted px-1 rounded">manage.py import_pallet_band_rates --apply</code></p>
                  )}
                </div>
              ) : (() => {
                const q = palletBandFilter.trim().toLowerCase();
                const lanes = (palletBandData.lanes || []).filter(l => !q
                  || l.country_name.toLowerCase().includes(q) || (l.iso2_code || '').toLowerCase().includes(q)
                  || l.carrier_name.toLowerCase().includes(q) || (l.zone_label || '').toLowerCase().includes(q));
                const bands = palletBandData.bands || [];
                return (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">
                      {lanes.length} lane{lanes.length === 1 ? '' : 's'} · {bands.length} weight bands ({bands[0]}–{bands[bands.length - 1]} kg)
                      {' '}· a blank cell means the carrier will not take a pallet at that weight
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-border bg-muted/60">
                            <th className="px-2 py-2 text-left font-medium sticky left-0 bg-muted/60 z-10 min-w-[150px]">Country</th>
                            <th className="px-2 py-2 text-left font-medium min-w-[170px]">Carrier</th>
                            <th className="px-2 py-2 text-left font-medium min-w-[150px]">Zone</th>
                            <th className="px-2 py-2 text-right font-medium whitespace-nowrap" title="Fuel the engine adds on top of these prices. It is not stored here: it comes from the carrier's pallet assignment, or the country default, both edited in the Pallet tab.">Fuel</th>
                            {bands.map(b => (
                              <th key={b} className="px-2 py-2 text-right font-medium whitespace-nowrap">{b}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {lanes.map((l, li) => (
                            <tr key={l.key} onClick={() => setPalletLaneKey(l.key)}
                                title="Click: which zones and postcodes end up on this row, on a map, and what they pay"
                                className={`border-b border-border hover:bg-muted/40 cursor-pointer ${li % 2 ? 'bg-muted/20' : ''}`}>
                              <td className={`px-2 py-1.5 whitespace-nowrap sticky left-0 z-10 ${li % 2 ? 'bg-[#f7f7f8] dark:bg-[#1b1b1d]' : 'bg-card'}`}>
                                <CountryCell iso2={l.iso2_code} name={l.country_name} />
                              </td>
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                <button type="button" onClick={(e) => { e.stopPropagation(); openCarrier(l.carrier_name); }}
                                        title={`${l.carrier_name}${l.rate_card ? ' — ' + l.rate_card : ''}\nOpen in the Carriers tab`}
                                        className="inline-flex items-center gap-1.5 rounded px-1 -mx-1 py-0.5 hover:bg-muted transition-colors text-left group">
                                  <CarrierLogo name={l.carrier_name} className="h-4" />
                                  <span className="group-hover:underline">{l.carrier_name}</span>
                                </button>
                                {l.carrier_id && (
                                  <LaneSwitch cr={l} rateType="pallet"
                                              hint={`\nApplies to every ${l.carrier_name} pallet lane in ${l.country_name}, not only this zone row.`} />
                                )}
                                {l.carrier_id && !laneActive(l, 'pallet') && (
                                  <span className="ml-1 text-[10px] rounded-full bg-red-600 text-white px-1.5 py-0.5 font-semibold">switched off</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 max-w-[240px]"
                                  title={`${l.zone_label}${l.postal_codes ? ' · ' + l.postal_codes : ''}`}>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-muted-foreground truncate max-w-[140px]">{l.zone_label || '—'}</span>
                                  {l.matches_probe && (
                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                          title="This destination resolves to this lane for this carrier">
                                      resolves here
                                    </span>
                                  )}
                                  {l.lane_kind === 'zone' && (
                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300"
                                          title={`This lane covers the postcodes of postal zone ${(l.postal_codes || '').replace(/^zone:/i, '')} (${l.zone_ranges} postcode ranges, e.g. the carrier's remote-area list). Click the row to see them.`}>
                                      {l.zone_ranges.toLocaleString()} postcode ranges
                                    </span>
                                  )}
                                  {l.lane_kind === 'descriptive' && (
                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground"
                                          title="No postal codes on this lane, so it is only taken as the fallback (the dearest lane wins)">
                                      no postcodes
                                    </span>
                                  )}
                                  {l.max_kg && (
                                    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                          title="The carrier will not take a pallet heavier than this on this lane">
                                      max {l.max_kg} kg
                                    </span>
                                  )}
                                </div>
                                {l.postal_codes && !/^tous/i.test(l.postal_codes) && l.lane_kind !== 'zone' && (
                                  <div className="text-[10px] text-muted-foreground/70 truncate max-w-[230px]">{l.postal_codes}</div>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap"
                                  title={l.fuel_source === 'carrier'
                                    ? "The fuel the quote adds: this carrier's own fuel for this country (per carrier x country, kept current by the fuel index)"
                                    : "The country's default pallet fuel, because this carrier has no fuel of its own in this country (Pallet tab)"}>
                                {l.fuel_pct != null ? `${(l.fuel_pct * 100).toFixed(0)}%` : ''}
                                {l.fuel_source === 'country' && <span className="text-muted-foreground/60">*</span>}
                              </td>
                              {bands.map((b, i) => {
                                const v = l.rates[String(b)];
                                const prev = i > 0 ? l.rates[String(bands[i - 1])] : undefined;
                                const drop = v != null && prev != null && v < prev - 0.005;
                                return (
                                  <td key={b}
                                      className={`px-2 py-1.5 text-right tabular-nums ${drop ? 'text-teal-700 dark:text-teal-300 font-semibold' : ''}`}
                                      title={drop ? `Cheaper than the ${bands[i - 1]} kg band: the card is degressive here` : undefined}>
                                    {v != null ? v.toFixed(2) : ''}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Pickup Tab — GLS ParcelShop / Locker rates + profiles */}
          {tabValue === 10 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Pickup-point rates (ParcelShop / Locker), EUR per weight band 1-30 kg, base ex-fuel. A country can hold several carriers (e.g. GLS + bpost) — use <span className="font-medium">Set active</span> to switch which one is live. Each country also has ParcelShop / Locker size limits.</p>
                  <div className="relative max-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input placeholder="Filter country..." value={pickupRateFilter} onChange={(e) => setPickupRateFilter(e.target.value)} className="pl-8 h-8 text-xs" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    disabled={(Object.keys(modifiedPickupRates).length === 0 && Object.keys(modifiedPickupProfiles).length === 0 && Object.keys(modifiedPickupFuel).length === 0) || saving}
                    onClick={async () => {
                      // Save rate edits (one PUT per country+carrier+network row).
                      for (const pr of pickupRates) {
                        if (modifiedPickupRates[pickupRateKey(pr)]) await savePickupRates(pr);
                      }
                      // Save profile + fuel edits (one PUT per country+network).
                      const profKeys = new Set(Object.keys(modifiedPickupProfiles));
                      for (const cid of Object.keys(modifiedPickupFuel)) {
                        if (!Object.keys(modifiedPickupProfiles).some(k => k.startsWith(`${cid}_`))) {
                          profKeys.add(`${cid}_shop`);
                        }
                      }
                      for (const key of profKeys) {
                        const [cid, network] = key.split('_');
                        await savePickupProfile(parseInt(cid), network || 'shop');
                      }
                      fetchData();
                    }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save All ({Object.keys(modifiedPickupRates).length + Object.keys(modifiedPickupProfiles).length + Object.keys(modifiedPickupFuel).length})
                  </Button>
                </div>
              </div>
              {(() => {
                // Build the country list: any country that has pickup rates OR a profile.
                const profileByCountry = {};
                pickupProfiles.forEach(p => { profileByCountry[p.country_id] = p; });
                // country_id -> { carrier_key -> { carrier_id, carrier_name, is_active, shop, locker } }
                // A country can carry SEVERAL pickup carriers (e.g. GLS + bpost); each
                // is shown separately with its own Active toggle. Normally exactly one
                // is live and activating one switches the others off; in a multi-network
                // country (BE / NL) both can be live and each is offered as its own
                // checkout rate, so the toggle adds instead of replacing.
                const carriersByCountry = {};
                pickupRates.forEach(pr => {
                  const car = (carriersByCountry[pr.country_id] || (carriersByCountry[pr.country_id] = {}));
                  const ckey = pr.carrier_id ?? 'null';
                  const entry = car[ckey] || (car[ckey] = {
                    carrier_id: pr.carrier_id || null, carrier_name: pr.carrier_name || 'Pickup',
                    is_active: false, shop: null, locker: null,
                  });
                  entry[pr.network || 'shop'] = pr;
                  if (pr.is_active) entry.is_active = true;
                });
                const countryIds = new Set([...Object.keys(carriersByCountry).map(Number), ...Object.keys(profileByCountry).map(Number)]);
                const rows = [...countryIds].map(cid => {
                  const carrierList = Object.values(carriersByCountry[cid] || {})
                    .sort((a, b) => (b.is_active - a.is_active) || String(a.carrier_name).localeCompare(String(b.carrier_name)));
                  const anyPr = carrierList.map(x => x.shop || x.locker).find(Boolean);
                  const prof = profileByCountry[cid];
                  const c = countries.find(x => x.id === cid);
                  return {
                    country_id: cid,
                    country_name: anyPr?.country_name || prof?.country_name || c?.name || `#${cid}`,
                    iso2_code: anyPr?.iso2_code || prof?.iso2_code || c?.iso2_code || '',
                    fuel_surcharge_pickup: (anyPr?.fuel_surcharge_pickup ?? prof?.fuel_surcharge_pickup ?? c?.fuel_surcharge_pickup ?? 0),
                    carriers: carrierList,
                    profileEntry: prof || null,
                  };
                }).filter(r => {
                  if (!pickupRateFilter) return true;
                  const q = pickupRateFilter.toLowerCase();
                  return r.country_name.toLowerCase().includes(q) || r.iso2_code.toLowerCase().includes(q);
                }).sort((a, b) => a.country_name.localeCompare(b.country_name));

                const ruleOptions = [
                  { value: '', label: 'None' },
                  { value: 'sum_lwh', label: 'L + W + H' },
                  { value: 'sum_lw', label: 'L + W' },
                  { value: 'girth', label: 'Girth (L + 2W + 2H)' },
                  { value: 'longest_side', label: 'Longest side' },
                ];

                if (rows.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <MapPin className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No pickup rates or profiles configured yet.</p>
                      <p className="text-xs mt-1">Add GLS pickup rates and ParcelShop limits per country to populate this tab.</p>
                    </div>
                  );
                }

                // One weight-band grid for a given network (shop = 1-30kg,
                // locker = 1-20kg). Renders nothing when that network has no rates.
                const pickupGrid = (pr, maxBand, title) => {
                  if (!pr) return null;
                  const prKey = pickupRateKey(pr);
                  return (
                    <div className="border-t border-border">
                      <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
                      <div className="overflow-x-auto">
                        <table className="text-sm">
                          <thead className="bg-muted"><tr>
                            {Array.from({ length: maxBand }, (_, i) => i + 1).map((kg) => (
                              <th key={kg} className="px-1 py-1.5 text-center font-medium text-xs min-w-[60px]">{kg}kg</th>
                            ))}
                          </tr></thead>
                          <tbody><tr className="border-t border-border">
                            {Array.from({ length: maxBand }, (_, i) => i + 1).map((kg) => {
                              const rate = pr.rates?.[kg.toString()];
                              const modified = modifiedPickupRates[prKey]?.[kg.toString()];
                              const isOfficial = rate?.is_official;
                              return (
                                <td key={kg} className="px-0.5 py-0.5">
                                  <input type="number" step="0.1"
                                    defaultValue={rate?.cost != null ? rate.cost.toFixed(1) : ''}
                                    onChange={(e) => handlePickupRateChange(prKey, kg.toString(), e.target.value)}
                                    className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                  />
                                </td>
                              );
                            })}
                          </tr></tbody>
                        </table>
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="space-y-6">
                    {rows.map((row) => {
                      const fuelPct = modifiedPickupFuel[row.country_id] !== undefined
                        ? Math.round(modifiedPickupFuel[row.country_id] * 100)
                        : Math.round((row.fuel_surcharge_pickup || 0) * 100);
                      return (
                        <div key={row.country_id} className="border border-border rounded-md">
                          {/* Country header + fuel surcharge */}
                          <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{getCountryFlag(row.iso2_code)}</span>
                              <span className="font-bold">{row.iso2_code}</span>
                              <span className="font-medium">{row.country_name}</span>
                              {(() => {
                                const act = row.carriers.filter(c => c.is_active).map(c => c.carrier_name);
                                return (
                                  <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 font-medium ${act.length ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' : 'bg-muted text-muted-foreground'}`}>
                                    {act.length ? `${act.join(', ')} pickup` : 'no active pickup'}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="flex items-center gap-2" title="Fallback only: a carrier's own fuel (in its block below) is what its pickup rate is grossed with. This figure applies to a carrier that has none of its own.">
                              <Label className="text-xs text-muted-foreground">Fallback fuel %</Label>
                              <input
                                type="number"
                                step="1"
                                value={fuelPct}
                                onChange={(e) => handlePickupFuelChange(row.country_id, e.target.value)}
                                className={`w-16 rounded border px-1.5 py-1 text-right text-xs font-semibold ${modifiedPickupFuel[row.country_id] !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-amber-300 bg-amber-50/60 dark:bg-amber-900/15'}`}
                              />
                            </div>
                          </div>

                          {/* One sub-block per pickup carrier (e.g. GLS + bpost), each with
                              an Active toggle. Only the active carrier is priced at checkout. */}
                          {row.carriers.length === 0 && (
                            <div className="px-3 py-3 text-xs text-muted-foreground italic">No pickup rates loaded for this country.</div>
                          )}
                          {row.carriers.map((car) => (
                            <div key={car.carrier_id ?? 'null'} className={car.is_active ? '' : 'opacity-60'}>
                              <div className="flex items-center justify-between px-3 py-1.5 bg-muted/20 border-t border-border">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold">{car.carrier_name}</span>
                                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-medium ${car.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{car.is_active ? 'Active' : 'Disabled'}</span>
                                  {(() => {
                                    // Fuel for THIS carrier in this country: what the pickup
                                    // rate is actually grossed with. Blank = the country
                                    // fallback below applies.
                                    const prof = pickupCarrierProfile(row, car.carrier_id);
                                    const fKey = `${row.country_id}_${car.carrier_id}`;
                                    const mod = modifiedPickupCarrierFuel[fKey];
                                    const stored = prof && prof.fuel_surcharge != null ? prof.fuel_surcharge : null;
                                    const shown = mod !== undefined ? Math.round(mod * 1000) / 10 : (stored != null ? Math.round(stored * 1000) / 10 : '');
                                    return (
                                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                                            title={stored == null ? 'No carrier-specific fuel: the country fallback applies' : 'Fuel this carrier\'s pickup bands are grossed with'}>
                                        fuel
                                        <input type="number" step="0.5" value={shown} placeholder={row.fuel_surcharge_pickup != null ? `${Math.round(row.fuel_surcharge_pickup * 1000) / 10}` : '–'}
                                          onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setModifiedPickupCarrierFuel(prev => ({ ...prev, [fKey]: v / 100 })); }}
                                          className={`w-14 rounded border px-1.5 py-0.5 text-right text-[11px] font-semibold ${mod !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-border bg-background'}`} />%
                                        {mod !== undefined && (
                                          <button type="button" disabled={saving} onClick={() => savePickupCarrierFuel(row, car.carrier_id)}
                                            className="ml-1 rounded border border-orange-400 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50">Save</button>
                                        )}
                                      </span>
                                    );
                                  })()}
                                  {(() => {
                                    // Odoo delivery method for this carrier's pickups here.
                                    // Stored on the carrier's profile row; blank shows the
                                    // default the carrier advice sends today.
                                    const prof = pickupCarrierProfile(row, car.carrier_id);
                                    const oKey = `${row.country_id}_${car.carrier_id}`;
                                    const mod = modifiedPickupOdoo[oKey];
                                    const stored = prof?.odoo_mapping || '';
                                    const effective = prof?.odoo_mapping_effective || row.profileEntry?.odoo_effective?.[`${car.carrier_id}_shop`] || '';
                                    const shown = mod !== undefined ? mod : stored;
                                    return (
                                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                                            title={stored ? 'Odoo delivery method the carrier advice names for this pickup (set here)' : (effective ? `Shippy default: the carrier advice sends "${effective}". Type a name to override it.` : 'No mapping: Odoo picks the method itself. Type the Odoo delivery method name to set one.')}>
                                        Odoo
                                        <input type="text" value={shown} placeholder={effective || 'Odoo picks (no mapping)'}
                                          onChange={(e) => setModifiedPickupOdoo(prev => ({ ...prev, [oKey]: e.target.value }))}
                                          className={`w-52 rounded border px-1.5 py-0.5 text-[11px] font-mono ${mod !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : stored ? 'border-border bg-background' : 'border-dashed border-border bg-background'}`} />
                                        {mod !== undefined && (
                                          <button type="button" disabled={saving} onClick={() => savePickupCarrierOdoo(row, car.carrier_id)}
                                            className="ml-1 rounded border border-orange-400 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50">Save</button>
                                        )}
                                      </span>
                                    );
                                  })()}
                                </div>
{pickupMultiCountries.includes((row.iso2_code || "").toUpperCase()) && (
                                  <span className="ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                                        title="This country offers BOTH pickup networks at checkout, one rate each (bpost Pickup Point / GLS Pickup Point). Activating a carrier here ADDS it; it does not switch the other off. Each rate needs its own Shopify delivery method and Atlas binding.">
                                    both networks
                                  </span>
                                )}
                                <button type="button" disabled={saving}
                                  onClick={() => togglePickupCarrier(row.country_id, car.carrier_id, !car.is_active, row.iso2_code)}
                                  className={`text-[11px] rounded border px-2 py-1 font-medium disabled:opacity-50 ${car.is_active ? 'border-border text-muted-foreground hover:bg-muted' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20'}`}>
                                  {car.is_active ? 'Disable' : 'Set active'}
                                </button>
                              </div>
                              {pickupGrid(car.shop, 30, 'ParcelShop rates (€/band, ex-fuel)')}
                              {pickupGrid(car.locker, 20, 'Parcel Locker rates (€/band, ex-fuel)')}
                            </div>
                          ))}

                          {/* Profile rows: ParcelShop + Locker limits */}
                          <div className="border-t border-border p-3 space-y-2">
                            {['shop', 'locker'].map((network) => {
                              const pKey = `${row.country_id}_${network}`;
                              const profMod = modifiedPickupProfiles[pKey] || {};
                              const saved = row.profileEntry && row.profileEntry.profiles ? row.profileEntry.profiles[network] : null;
                              const fieldVal = (field, fallbackField = field) => (
                                profMod[field] !== undefined ? profMod[field] : (saved ? (saved[fallbackField] ?? '') : '')
                              );
                              const dirty = Object.keys(profMod).some(k => k !== 'country_id' && k !== 'network');
                              return (
                                <div key={network} className={`flex flex-wrap items-end gap-2 rounded p-2 ${dirty ? 'bg-orange-50/60 dark:bg-orange-900/10' : 'bg-muted/20'}`}>
                                  <span className="text-xs font-medium w-20">{network === 'shop' ? 'ParcelShop' : 'Locker'}</span>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Max kg</Label>
                                    <input type="number" step="0.1" value={fieldVal('max_weight_kg')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'max_weight_kg', e.target.value)}
                                      className="w-16 rounded border border-border bg-background px-1.5 py-1 text-right text-xs" />
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Max L cm</Label>
                                    <input type="number" step="1" value={fieldVal('max_l_cm')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'max_l_cm', e.target.value)}
                                      className="w-16 rounded border border-border bg-background px-1.5 py-1 text-right text-xs" />
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Max W cm</Label>
                                    <input type="number" step="1" value={fieldVal('max_w_cm')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'max_w_cm', e.target.value)}
                                      className="w-16 rounded border border-border bg-background px-1.5 py-1 text-right text-xs" />
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Max H cm</Label>
                                    <input type="number" step="1" value={fieldVal('max_h_cm')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'max_h_cm', e.target.value)}
                                      className="w-16 rounded border border-border bg-background px-1.5 py-1 text-right text-xs" />
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Special rule</Label>
                                    <select value={fieldVal('special_rule_type')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'special_rule_type', e.target.value)}
                                      className="rounded border border-border bg-background px-1.5 py-1 text-xs h-[30px]">
                                      {ruleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <Label className="text-[10px] text-muted-foreground">Rule value cm</Label>
                                    <input type="number" step="1" value={fieldVal('special_rule_value')}
                                      onChange={(e) => handlePickupProfileChange(row.country_id, network, 'special_rule_value', e.target.value)}
                                      className="w-16 rounded border border-border bg-background px-1.5 py-1 text-right text-xs" />
                                  </div>
                                  <Button size="sm" variant="outline" disabled={saving || (!dirty && modifiedPickupFuel[row.country_id] === undefined)}
                                    onClick={() => savePickupProfile(row.country_id, network)}>
                                    <Save className="h-3.5 w-3.5 mr-1" /> Save
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-emerald-400 bg-emerald-50/60" />
                  <span>Official rate</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border border-orange-400 bg-orange-50" />
                  <span>Modified (unsaved)</span>
                </div>
              </div>
            </div>
          )}

          {/* Returns Tab — GLS / bpost return rates per country + carrier toggle */}
          {tabValue === 11 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Return-shipping rates, EUR per weight band 1-30 kg (as billed). A country can hold several carriers (GLS + bpost) and <span className="font-medium">both can be active at once</span> — <span className="font-medium">Enable / Disable</span> each independently. Green = official, blue = follows the outbound card (outbound band minus a deduction, set per carrier).</p>
                  <div className="relative max-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input placeholder="Filter country..." value={returnRateFilter} onChange={(e) => setReturnRateFilter(e.target.value)} className="pl-8 h-8 text-xs" />
                  </div>
                </div>
                <Button
                  disabled={Object.keys(modifiedReturnRates).length === 0 || saving}
                  onClick={async () => {
                    for (const rr of returnRates) {
                      if (modifiedReturnRates[returnRateKey(rr)]) await saveReturnRates(rr);
                    }
                    fetchData();
                  }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save All ({Object.keys(modifiedReturnRates).length})
                </Button>
              </div>
              {(() => {
                const byCountry = {};
                returnRates.forEach(rr => {
                  const c = (byCountry[rr.country_id] || (byCountry[rr.country_id] = {
                    country_id: rr.country_id, country_name: rr.country_name, iso2_code: rr.iso2_code, carriers: [] }));
                  c.carriers.push(rr);
                });
                let rows = Object.values(byCountry).map(c => ({
                  ...c,
                  carriers: c.carriers.slice().sort((a, b) => (b.is_active - a.is_active) || String(a.carrier_name).localeCompare(String(b.carrier_name))),
                }));
                if (returnRateFilter) {
                  const q = returnRateFilter.toLowerCase();
                  rows = rows.filter(r => r.country_name.toLowerCase().includes(q) || (r.iso2_code || '').toLowerCase().includes(q));
                }
                rows.sort((a, b) => a.country_name.localeCompare(b.country_name));
                if (rows.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <Undo2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No return rates configured yet.</p>
                      <p className="text-xs mt-1">Seed bpost via <code className="text-xs bg-muted px-1 rounded">manage.py load_bpost_return_rates</code>, or add per country.</p>
                    </div>
                  );
                }
                const grid = (rr) => {
                  const key = returnRateKey(rr);
                  return (
                    <div className="overflow-x-auto">
                      <table className="text-sm">
                        <thead className="bg-muted"><tr>
                          {Array.from({ length: 30 }, (_, i) => i + 1).map((kg) => (
                            <th key={kg} className="px-1 py-1.5 text-center font-medium text-xs min-w-[60px]">{kg}kg</th>
                          ))}
                        </tr></thead>
                        <tbody><tr className="border-t border-border">
                          {Array.from({ length: 30 }, (_, i) => i + 1).map((kg) => {
                            const rate = rr.rates?.[kg.toString()];
                            const modified = modifiedReturnRates[key]?.[kg.toString()];
                            const isOfficial = rate?.is_official;
                            return (
                              <td key={kg} className="px-0.5 py-0.5">
                                <input type="number" step="0.1"
                                  defaultValue={rate?.cost != null ? rate.cost.toFixed(rr.follows_outbound ? 2 : 1) : ''}
                                  readOnly={!!rr.follows_outbound}
                                  title={rr.follows_outbound ? 'Outbound band minus the deduction (read-only)' : undefined}
                                  onChange={(e) => handleReturnRateChange(key, kg.toString(), e.target.value)}
                                  className={`w-14 rounded border px-1.5 py-1 text-right text-xs ${rr.follows_outbound ? 'border-sky-300 bg-sky-50/60 dark:bg-sky-900/15 cursor-default' : modified !== undefined ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : isOfficial ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/15' : 'border-border bg-background'}`}
                                />
                              </td>
                            );
                          })}
                        </tr></tbody>
                      </table>
                    </div>
                  );
                };
                return (
                  <div className="space-y-6">
                    {rows.map((row) => (
                      <div key={row.country_id} className="border border-border rounded-md">
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border">
                          <span className="text-base">{getCountryFlag(row.iso2_code)}</span>
                          <span className="font-bold">{row.iso2_code}</span>
                          <span className="font-medium">{row.country_name}</span>
                          {(() => {
                            const act = row.carriers.filter(c => c.is_active).map(c => c.carrier_name);
                            return <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 font-medium ${act.length ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' : 'bg-muted text-muted-foreground'}`}>{act.length ? `${act.join(', ')} return` : 'no active return'}</span>;
                          })()}
                        </div>
                        {row.carriers.map((car) => (
                          <div key={car.carrier_id ?? 'null'} className={car.is_active ? '' : 'opacity-60'}>
                            <div className="flex items-center justify-between px-3 py-1.5 bg-muted/20 border-t border-border">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold">{car.carrier_name || 'Return'}</span>
                                <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-medium ${car.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{car.is_active ? 'Active' : 'Disabled'}</span>
                                {car.follows_outbound && (
                                  <span className="flex items-center gap-1 text-[10px] rounded-full px-1.5 py-0.5 font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
                                    Follows outbound, minus EUR
                                    <input type="number" step="0.05" min="0" defaultValue={Number(car.deduction || 0).toFixed(2)}
                                      disabled={saving}
                                      onBlur={(e) => saveReturnDeduction(car, e.target.value)}
                                      className="w-12 rounded border border-sky-300 bg-background px-1 py-0 text-right text-[10px]" />
                                  </span>
                                )}
                              </div>
                              <button type="button" disabled={saving}
                                onClick={() => toggleReturnCarrier(row.country_id, car.carrier_id, !car.is_active)}
                                className={`text-[11px] rounded border px-2 py-1 font-medium disabled:opacity-50 ${car.is_active ? 'border-border text-muted-foreground hover:bg-muted' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20'}`}>
                                {car.is_active ? 'Disable' : 'Enable'}
                              </button>
                            </div>
                            {grid(car)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* API Reference Tab */}
          {tabValue === 7 && (
            <div className="max-w-3xl">
              <h2 className="text-xl font-bold mb-1">Packing Simulator API</h2>
              <p className="text-sm text-muted-foreground mb-4">
                External API for calculating shipping costs and optimal packing from Shopify variant IDs.
              </p>

              <div className="rounded-md bg-gray-900 text-gray-100 p-4 mb-6 font-mono text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block rounded bg-emerald-600 px-2 py-0.5 text-xs font-bold">POST</span>
                  <span className="break-all">/api/v1/packing/&lt;slug&gt;/&lt;token&gt;/</span>
                </div>
                <p className="text-gray-400 text-xs">Replace &lt;token&gt; with your API token</p>
              </div>

              <h3 className="text-base font-semibold mb-2">Request Parameters</h3>
              <div className="overflow-x-auto border border-border rounded-md mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Parameter</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Required</th>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">items</code></td>
                      <td className="px-3 py-2 text-xs">array</td>
                      <td className="px-3 py-2"><span className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full px-2 py-0.5 text-xs">Yes</span></td>
                      <td className="px-3 py-2 text-xs">Array of items: <code className="bg-muted px-1 rounded">{"variant_id, quantity"}</code></td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">country</code></td>
                      <td className="px-3 py-2 text-xs">string</td>
                      <td className="px-3 py-2"><span className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full px-2 py-0.5 text-xs">Yes</span></td>
                      <td className="px-3 py-2 text-xs">Full country name (e.g., "Netherlands", "Spain")</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">postal_code</code></td>
                      <td className="px-3 py-2 text-xs">string</td>
                      <td className="px-3 py-2"><span className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded-full px-2 py-0.5 text-xs">Optional</span></td>
                      <td className="px-3 py-2 text-xs">Postal/ZIP code for zone-specific pricing</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">order_total</code></td>
                      <td className="px-3 py-2 text-xs">number</td>
                      <td className="px-3 py-2"><span className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded-full px-2 py-0.5 text-xs">Optional</span></td>
                      <td className="px-3 py-2 text-xs">Order total in EUR for franco check</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">item_costs</code></td>
                      <td className="px-3 py-2 text-xs">object</td>
                      <td className="px-3 py-2"><span className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded-full px-2 py-0.5 text-xs">Optional</span></td>
                      <td className="px-3 py-2 text-xs">Unit cost per variant_id for margin-based franco</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-base font-semibold mb-2">Example Request</h3>
              <div className="relative rounded-md bg-gray-900 text-gray-300 p-4 mb-6 font-mono text-xs overflow-auto">
                <button
                  className="absolute top-2 right-2 rounded p-1 text-gray-400 hover:text-white hover:bg-gray-700"
                  onClick={() => {
                    navigator.clipboard.writeText(`{
  "items": [
    {"variant_id": "44331400233206", "quantity": 2},
    {"variant_id": "44331202773238", "quantity": 3}
  ],
  "country": "Spain",
  "postal_code": "07001",
  "order_total": 850.00,
  "item_costs": {
    "44331400233206": 45.00,
    "44331202773238": 32.50
  }
}`);
                    setSuccess('Copied to clipboard!');
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <pre>{`POST /api/v1/packing/<slug>/<token>/
Content-Type: application/json

{
  "items": [
    {"variant_id": "44331400233206", "quantity": 2},
    {"variant_id": "44331202773238", "quantity": 3}
  ],
  "country": "Spain",
  "postal_code": "07001",
  "order_total": 850.00,
  "item_costs": {
    "44331400233206": 45.00,
    "44331202773238": 32.50
  }
}`}</pre>
              </div>

              <h3 className="text-base font-semibold mb-2">Supported Countries</h3>
              <p className="text-xs text-muted-foreground mb-2">Use full country names in the <code className="bg-muted px-1 rounded">country</code> parameter.</p>
              <div className="flex flex-wrap gap-1.5 mb-6">
                {countries.filter(c => c.is_active).map((country) => (
                  <span key={country.id} className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs">
                    {getCountryFlag(country.iso2_code)} {country.name}
                  </span>
                ))}
              </div>

              <h3 className="text-base font-semibold mb-2">Special Shipping Zones</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Pass the <code className="bg-muted px-1 rounded">postal_code</code> parameter for zone-specific pricing.
              </p>
              <div className="overflow-x-auto border border-border rounded-md mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Zone</th>
                      <th className="px-3 py-2 text-left font-medium">Country</th>
                      <th className="px-3 py-2 text-left font-medium">Postal Code Prefixes</th>
                      <th className="px-3 py-2 text-left font-medium">Franco B2B</th>
                      <th className="px-3 py-2 text-left font-medium">Franco B2C</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((zone) => (
                      <tr key={zone.id} className="border-t border-border">
                        <td className="px-3 py-2">{zone.name}</td>
                        <td className="px-3 py-2">{getCountryFlag(zone.country_iso2)} {zone.country_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{zone.postal_code_patterns}</td>
                        <td className="px-3 py-2">{zone.franco_threshold ? `${zone.franco_threshold}` : 'Uses country'}</td>
                        <td className="px-3 py-2">{zone.franco_b2c ? `${zone.franco_b2c}` : 'Uses country'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <hr className="border-border my-6" />

              <h2 className="text-xl font-bold mb-1">Shopify Carrier Service (B2C)</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Real-time shipping rates at Shopify checkout. Shopify sends cart items + destination, Shippy returns calculated rates.
              </p>

              <div className="rounded-md bg-gray-900 text-gray-100 p-4 mb-6 font-mono text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block rounded bg-emerald-600 px-2 py-0.5 text-xs font-bold">POST</span>
                  <span className="break-all">/api/v1/shopify/carrier-service/</span>
                </div>
                <p className="text-gray-400 text-xs">Authenticated via Shopify HMAC-SHA256 signature</p>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 mb-6">
                <strong>How it differs from B2B API:</strong>
                <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                  <li>Shopify sends ISO2 country codes (e.g. "BE"), not full names &mdash; automatically resolved</li>
                  <li>Prices in <strong>cents</strong> in the store's currency &mdash; converted to/from EUR internally</li>
                  <li>Uses <strong>B2C franco thresholds</strong> (not B2B)</li>
                  <li>Item costs (COGS) looked up from Boxopedia for margin-based franco</li>
                  <li>Must respond within <strong>10 seconds</strong> &mdash; falls back to weight-based rate if packing times out</li>
                  <li>Returns Shopify-compatible rate objects, not raw packing data</li>
                </ul>
              </div>

              <h3 className="text-base font-semibold mb-2">Request (sent by Shopify)</h3>
              <div className="rounded-md bg-gray-900 text-gray-300 p-4 mb-6 font-mono text-xs overflow-auto">
                <pre>{`{
  "rate": {
    "destination": {
      "country": "FR",
      "postal_code": "75001",
      "province": "Île-de-France",
      "city": "Paris"
    },
    "items": [
      {
        "variant_id": 44331400233206,
        "quantity": 2,
        "grams": 2500,
        "price": 12500,
        "sku": "PH5-WHITE",
        "name": "PH5 Pendant"
      }
    ],
    "currency": "EUR"
  }
}`}</pre>
              </div>

              <h3 className="text-base font-semibold mb-2">Response (returned to Shopify)</h3>
              <div className="rounded-md bg-gray-900 text-gray-300 p-4 mb-6 font-mono text-xs overflow-auto">
                <pre>{`{
  "rates": [
    {
      "service_name": "Standard Shipping",
      "service_code": "shippy_standard",
      "total_price": 1250,
      "currency": "EUR"
    }
  ]
}`}</pre>
              </div>

              <h3 className="text-base font-semibold mb-2">Setup</h3>
              <div className="overflow-x-auto border border-border rounded-md mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">Environment Variable</th>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="px-3 py-2"><code className="text-xs bg-muted px-1 rounded">SHOPIFY_CARRIER_SECRET</code></td>
                      <td className="px-3 py-2 text-xs">Shared secret from your Shopify carrier service app settings for HMAC validation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Carriers Tab */}
          {tabValue === 8 && (
            <div>
              {/* Carrier list */}
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-3">
                  <Button onClick={() => {
                    setEditingCarrier({ id: 0, name: '', service: '', is_active: true, odoo_mapping: '', odoo_mapping_pickup: '' });
                    setCarrierDialogOpen(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" /> Add Carrier
                  </Button>
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search carriers..." value={carrierFilter} onChange={(e) => setCarrierFilter(e.target.value)} className="pl-9 h-9" />
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Winner counts:</label>
                    <select
                      className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
                      value={carrierUsagePeriod}
                      onChange={(e) => setCarrierUsagePeriod(e.target.value)}
                    >
                      <optgroup label="Rolling">
                        <option value="1">Last 24h</option>
                        <option value="3">Last 3 days</option>
                        <option value="7">Last 7 days</option>
                        <option value="10">Last 10 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                      </optgroup>
                      <optgroup label="To date">
                        <option value="wtd">Week to date</option>
                        <option value="mtd">Month to date</option>
                        <option value="ytd">Year to date</option>
                      </optgroup>
                      <option value="0">All time</option>
                    </select>
                    {carrierUsageLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <span className="text-xs text-muted-foreground">{carrierUsage.total_shipments?.toLocaleString() || 0} shipments</span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {(() => {
                    // country_id -> iso2 lookup, built once per render
                    const isoById = {};
                    countries.forEach(c => { isoById[c.id] = c.iso2_code; });
                    // carrier_id -> sorted unique iso2[] + rate-type set, from
                    // CountryCarrier assignments (a carrier can serve different
                    // countries under different rate models).
                    const codesByCarrier = {};
                    const typesByCarrier = {};
                    carrierAssignments.forEach(a => {
                      const iso = isoById[a.country_id];
                      if (iso) (codesByCarrier[a.carrier_id] ||= new Set()).add(iso);
                      if (a.rate_type) (typesByCarrier[a.carrier_id] ||= new Set()).add(a.rate_type);
                    });
                    const carrierCodes = (cid) =>
                      [...(codesByCarrier[cid] || [])].sort();
                    const TYPE_META = {
                      weight:     { label: 'Real weight', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
                      volumetric: { label: 'Volumetric',  cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
                      coli:       { label: 'Per parcel',  cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
                      pallet:     { label: 'Pallet',      cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
                    };
                    const carrierTypes = (cid) =>
                      [...(typesByCarrier[cid] || [])].sort();
                    return (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-3 py-2 text-left font-medium">Service</th>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Countries</th>
                        <th className="px-3 py-2 text-right font-medium">Won</th>
                        <th className="px-3 py-2 text-left font-medium">Active</th>
                        <th className="px-3 py-2 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const q = carrierFilter.trim().toLowerCase();
                        const matched = carriers.filter(c =>
                          !q || c.name.toLowerCase().includes(q) || (c.service || '').toLowerCase().includes(q)
                        );
                        const usageMap = new Map();
                        (carrierUsage.totals || []).forEach(t => usageMap.set(t.carrier_name, t));
                        const usageForCarrier = (c) => {
                          const full = (c.service ? `${c.name} ${c.service}` : c.name).trim();
                          return usageMap.get(full) || usageMap.get(c.name) || null;
                        };
                        const brandTotal = (brand) => {
                          let n = 0;
                          for (const t of carrierUsage.totals || []) {
                            if (t.carrier_name === brand || t.carrier_name.startsWith(brand + ' ')) n += t.count;
                          }
                          return n;
                        };
                        if (matched.length === 0) {
                          return (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                              {carriers.length === 0 ? 'No carriers yet. Add one to get started.' : 'No carriers match your search.'}
                            </td></tr>
                          );
                        }
                        const order = [];
                        const groups = {};
                        matched.forEach(c => {
                          if (!groups[c.name]) { groups[c.name] = []; order.push(c.name); }
                          groups[c.name].push(c);
                        });
                        return order.map(brand => {
                          const svcs = groups[brand];
                          return (
                          <React.Fragment key={brand}>
                            <tr className="border-y border-border bg-muted/40">
                              <td colSpan={6} className="px-3 py-2">
                                <div className="flex items-center gap-2.5">
                                  <CarrierLogo name={brand} />
                                  <CountryFlag name={brand} />
                                  <span className="font-semibold">{brand}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {svcs.length} service{svcs.length === 1 ? '' : 's'}
                                  </span>
                                  {(() => {
                                    const bt = brandTotal(brand);
                                    return bt > 0 ? (
                                      <span className="ml-auto text-xs text-muted-foreground">
                                        Won <span className="font-semibold text-foreground">{bt.toLocaleString()}</span> shipments
                                      </span>
                                    ) : null;
                                  })()}
                                </div>
                              </td>
                            </tr>
                            {svcs.map(carrier => {
                              const codes = carrierCodes(carrier.id);
                              const types = carrierTypes(carrier.id);
                              const expanded = !!expandedCarrierCountries[carrier.id];
                              return (
                              <tr key={carrier.id} className="border-b border-border hover:bg-muted/30">
                                <td className="py-2 pr-3 pl-10">
                                  {carrier.service
                                    ? <span className="font-medium">{carrier.service}</span>
                                    : <span className="text-muted-foreground italic">—</span>}
                                  {carrier.odoo_mapping ? (
                                    <span className="ml-2 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground" title="Odoo delivery method for home delivery">
                                      {carrier.odoo_mapping}
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-[10px] italic text-muted-foreground/60" title="No Odoo delivery method set for home delivery: Odoo picks the method itself. Set it via Edit Carrier">
                                      no Odoo mapping
                                    </span>
                                  )}
                                  {pickupRates.some((pr) => pr.carrier_id === carrier.id) && (() => {
                                    // This carrier runs a pickup network: show the Odoo
                                    // method its pickups get (Carriers tab default; country
                                    // exceptions live on the pickup tab).
                                    const overrides = pickupProfiles.flatMap((pp) => (pp.carriers || [])
                                      .filter((c) => c.carrier_id === carrier.id && c.network === 'shop' && c.odoo_mapping)
                                      .map((c) => `${pp.iso2_code}: ${c.odoo_mapping}`));
                                    return carrier.odoo_mapping_pickup ? (
                                      <span className="ml-1 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                                            title={`Odoo delivery method for a pickup-point delivery${overrides.length ? `. Country exceptions (pickup tab): ${overrides.join(', ')}` : ''}`}>
                                        pickup: {carrier.odoo_mapping_pickup}{overrides.length ? ` (+${overrides.length})` : ''}
                                      </span>
                                    ) : (
                                      <span className="ml-1 text-[10px] italic text-muted-foreground/60"
                                            title={`No Odoo delivery method set for this carrier's pickups: Odoo picks the method itself${overrides.length ? `, except ${overrides.join(', ')}` : ''}. Set it via Edit Carrier`}>
                                        pickup: no Odoo mapping{overrides.length ? ` (+${overrides.length})` : ''}
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="px-3 py-2">
                                  {types.length === 0 ? (
                                    <span className="text-muted-foreground italic">—</span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1">
                                      {types.map(t => {
                                        const m = TYPE_META[t] || { label: t, cls: 'bg-gray-100 text-gray-700' };
                                        return (
                                          <span key={t} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>
                                            {m.label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 max-w-md">
                                  {codes.length === 0 ? (
                                    <span className="text-muted-foreground italic">none</span>
                                  ) : (
                                    <div className="flex items-start gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedCarrierCountries(s => ({ ...s, [carrier.id]: !s[carrier.id] }))}
                                        title={expanded ? 'Hide countries' : `Show ${codes.length} countries`}
                                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 text-xs font-semibold shrink-0 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                                      >
                                        {codes.length}
                                        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                      </button>
                                      {expanded && (
                                        <span className="text-xs text-muted-foreground font-mono break-words">
                                          {codes.join(', ')}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {(() => {
                                    const u = usageForCarrier(carrier);
                                    if (!u) return <span className="text-muted-foreground">—</span>;
                                    const topCountries = (u.top_countries || []).map(tc => `${tc.country} (${tc.count})`).join(', ');
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1"
                                        title={topCountries ? `Top countries: ${topCountries}` : ''}
                                      >
                                        <span className="font-semibold">{u.count.toLocaleString()}</span>
                                        <span className="text-[10px] text-muted-foreground">({u.share_pct}%)</span>
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${carrier.is_active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                                    {carrier.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  {carrier.fuel_on_surcharges && (
                                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium" title="Fuel % is applied on surcharges too (UPS-style)">
                                      fuel on surcharges
                                    </span>
                                  )}
                                  {carrier.green_surcharge_pct > 0 && (
                                    <span className="ml-1 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium" title="Green surcharge: % of the transport charge added after fuel, not fuel-surcharged (DHL Express GoGreen Plus)">
                                      green {(carrier.green_surcharge_pct * 100).toFixed(2)}%
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-end gap-1">
                                    <button className="rounded p-1 hover:bg-accent" onClick={() => { setEditingCarrier(carrier); setCarrierDialogOpen(true); }}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" onClick={() => handleCarrierDelete(carrier.id)}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                            {/* Pickup networks are priced on their own bands (Pickup tab),
                                not as a carrier service row, so they were invisible here.
                                One line per carrier x network (Geoffrey 2026-09-30). */}
                            {svcs.flatMap((carrier) => ['shop', 'locker'].map((net) => {
                              const rows = pickupRates.filter((pr) => pr.carrier_id === carrier.id && (pr.network || 'shop') === net);
                              if (!rows.length) return null;
                              const active = rows.filter((pr) => pr.is_active);
                              const label = net === 'locker' ? 'Parcel Locker' : 'Pickup Point (ParcelShop)';
                              const usage = (carrierUsage.totals || []).filter((t) => net === 'shop'
                                ? /parcelshop|pickup point/i.test(t.carrier_name) && new RegExp('^' + brand.split(' ')[0], 'i').test(t.carrier_name)
                                : /locker/i.test(t.carrier_name) && new RegExp('^' + brand.split(' ')[0], 'i').test(t.carrier_name));
                              const won = usage.reduce((n, t) => n + (t.count || 0), 0);
                              const mapping = carrier.odoo_mapping_pickup;
                              const overrides = pickupProfiles.flatMap((pp) => (pp.carriers || [])
                                .filter((c) => c.carrier_id === carrier.id && c.network === net && c.odoo_mapping)
                                .map((c) => `${pp.iso2_code}: ${c.odoo_mapping}`));
                              return (
                                <tr key={`${carrier.id}-${net}`} className="border-b border-border hover:bg-muted/30 bg-muted/10">
                                  <td className="py-2 pr-3 pl-10">
                                    <span className="font-medium">{label}</span>
                                    <span className="ml-2 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground" title={`Odoo delivery method for a pickup on this network${overrides.length ? '. Country exceptions: ' + overrides.join(', ') : ''}`}>
                                      {mapping ? `${mapping}${overrides.length ? ` (+${overrides.length})` : ''}` : 'no Odoo mapping'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2"><span className="inline-flex items-center rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 px-1.5 py-0.5 text-[10px] font-semibold">pickup</span></td>
                                  <td className="px-3 py-2 text-xs">
                                    <span className="font-semibold">{active.length}</span><span className="text-muted-foreground"> active of {rows.length}</span>
                                    <div className="text-[10px] text-muted-foreground max-w-[360px] truncate" title={active.map((pr) => pr.iso2_code).sort().join(', ')}>{active.map((pr) => pr.iso2_code).sort().join(' ')}</div>
                                  </td>
                                  <td className="px-3 py-2 text-right tabular-nums text-xs">{won ? won.toLocaleString() : <span className="text-muted-foreground">0</span>}</td>
                                  <td className="px-3 py-2 text-xs">{active.length ? <span className="text-emerald-700 dark:text-emerald-400">Active</span> : <span className="text-muted-foreground">Off</span>}</td>
                                  <td className="px-3 py-2 text-right">
                                    <button type="button" className="text-xs rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => setTabValue(10)} title="Rates per weight band, fuel and limits per country live on the Pickup tab">Pickup tab</button>
                                  </td>
                                </tr>
                              );
                            }))}
                          </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                    );
                  })()}
                </div>
              </div>

              {/* Info about carrier assignments */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 mt-2">
                <p className="text-sm text-muted-foreground">
                  Carrier-country assignments are now managed directly in each rate tab (Weight, Volumetric, Per-Parcel, Pallet).
                  Use the "Add country + carrier" dropdown in each tab to assign a carrier's rates to a country.
                </p>
              </div>
            </div>
          )}

          {tabValue === 9 && (
            <div>
              <div className="mb-4 flex items-center gap-3">
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={surchargeCarrierFilter}
                  onChange={(e) => setSurchargeCarrierFilter(e.target.value)}
                >
                  <option value="">All Carriers</option>
                  {carriers.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.service ? ` — ${c.service}` : ''}</option>
                  ))}
                </select>
                <div className="inline-flex rounded-lg border border-border overflow-hidden">
                  {[['all', 'All'], ['parcel', 'Parcels'], ['pallet', 'Pallets']].map(([v, label]) => (
                    <button
                      key={v}
                      onClick={() => setSurchargeAppliesFilter(v)}
                      title={v === 'parcel'
                        ? 'Rules evaluated for a PARCEL: girth caps, the per-parcel weight limit, dimensional bands'
                        : v === 'pallet'
                        ? 'Rules evaluated for a PALLET: the DHL oversize piece, carrier width and weight caps'
                        : 'Every rule, whichever side it belongs to'}
                      className={`px-3 py-1.5 text-xs font-medium ${surchargeAppliesFilter === v ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Button onClick={() => {
                  const cid = surchargeCarrierFilter ? parseInt(surchargeCarrierFilter) : (carriers[0]?.id || 0);
                  openNewSurchargeRule(cid);
                }} disabled={carriers.length === 0}>
                  <Plus className="h-4 w-4 mr-2" /> Add Rule
                </Button>
              </div>

              {(() => {
                // Group rules by carrier
                let filteredRules = surchargeCarrierFilter
                  ? surchargeRules.filter(r => r.carrier_id === parseInt(surchargeCarrierFilter))
                  : surchargeRules;
                // A rule marked 'both' belongs to either view, so it shows in both.
                if (surchargeAppliesFilter !== 'all') {
                  filteredRules = filteredRules.filter(r =>
                    (r.applies_to || 'parcel') === surchargeAppliesFilter || (r.applies_to || 'parcel') === 'both');
                }
                const byCarrier = {};
                filteredRules.forEach(r => {
                  const key = r.carrier_id;
                  if (!byCarrier[key]) byCarrier[key] = { name: r.carrier_name, service: r.carrier_service, rules: [] };
                  byCarrier[key].rules.push(r);
                });

                if (Object.keys(byCarrier).length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                      <AlertTriangle className="h-8 w-8 mb-3 opacity-40" />
                      <p className="text-sm">No surcharge rules defined yet.</p>
                      <p className="text-xs mt-1">Add rules to track carrier dimension/weight surcharges.</p>
                    </div>
                  );
                }

                // Object keys that look like integers iterate in NUMERIC order, so
                // this list came out by carrier id - which is deploy order, and
                // means nothing to anyone reading it. Sort by the name shown.
                const carrierEntries = Object.entries(byCarrier).sort((a, b) => {
                  const an = `${a[1].name || ''} ${a[1].service || ''}`.trim().toLowerCase();
                  const bn = `${b[1].name || ''} ${b[1].service || ''}`.trim().toLowerCase();
                  return an.localeCompare(bn);
                });

                return carrierEntries.map(([carrierId, carrier]) => {
                  const isExpanded = expandedSurchargeCarrier === null || expandedSurchargeCarrier === parseInt(carrierId);
                  // Group rules by surcharge_group for visual separation
                  const groups = {};
                  carrier.rules.forEach(r => {
                    if (!groups[r.surcharge_group]) groups[r.surcharge_group] = [];
                    groups[r.surcharge_group].push(r);
                  });

                  return (
                    <div key={carrierId} className="mb-4 border border-border rounded-md overflow-hidden">
                      <div
                        className="flex items-center justify-between px-4 py-2.5 bg-muted/50 cursor-pointer hover:bg-muted/80"
                        onClick={() => setExpandedSurchargeCarrier(prev => prev === parseInt(carrierId) ? null : parseInt(carrierId))}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <span className="font-semibold text-sm">{carrier.name}</span>
                          {carrier.service && <span className="text-xs text-muted-foreground">— {carrier.service}</span>}
                          {(() => {
                            const groupCount = Object.keys(groups).length;
                            const assignedCountries = surchargeCarrierCountries[carrierId] || [];
                            return (
                              <div className="flex items-center gap-1.5 ml-2">
                                <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-1.5 py-0 text-[10px] font-medium">
                                  {carrier.rules.length} rule{carrier.rules.length !== 1 ? 's' : ''}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
                                  {groupCount} group{groupCount !== 1 ? 's' : ''}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-muted px-1.5 py-0 text-[10px] font-medium text-muted-foreground" title={assignedCountries.join(', ')}>
                                  {assignedCountries.length > 0 ? assignedCountries.join(', ') : 'No countries assigned'}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openNewSurchargeRule(parseInt(carrierId)); }}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add
                        </Button>
                      </div>

                      {isExpanded && (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[120px]">Group</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs">Condition</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[50px]">Op</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[80px]">Threshold</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[100px]">Outcome</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[80px]">Amount</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs">Countries</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs">Label</th>
                              <th className="px-3 py-1.5 text-left font-medium text-xs w-[70px]">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(groups).map(([groupName, groupRules], gi) => (
                              <React.Fragment key={groupName}>
                                {groupRules.map((rule, ri) => (
                                  <tr key={rule.id} className={`border-b border-border hover:bg-muted/20 ${gi % 2 === 1 ? 'bg-muted/10' : ''}`}>
                                    {ri === 0 ? (
                                      <td className="px-3 py-1.5 font-medium text-xs" rowSpan={groupRules.length}>
                                        <span className="inline-flex items-center rounded-md bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium">
                                          {groupName}
                                        </span>
                                        {groupRules.length > 1 && (
                                          <span className="block text-[10px] text-muted-foreground mt-0.5">OR logic</span>
                                        )}
                                      </td>
                                    ) : null}
                                    <td className="px-3 py-1.5 text-xs">{CONDITION_OPTIONS.find(o => o.value === rule.condition)?.label || rule.condition}</td>
                                    <td className="px-3 py-1.5 text-xs font-mono">
                                      {rule.condition === 'always'
                                        ? <span className="text-muted-foreground">—</span>
                                        : (OPERATOR_OPTIONS.find(o => o.value === rule.operator)?.label || rule.operator)}
                                    </td>
                                    <td className="px-3 py-1.5 text-xs font-mono">
                                      {rule.condition === 'always'
                                        ? <span className="text-muted-foreground">{rule.outcome === 'flat_shipment' ? 'every shipment' : 'every parcel'}</span>
                                        : rule.threshold}
                                    </td>
                                    <td className="px-3 py-1.5 text-xs">
                                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        rule.outcome === 'refused' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                          : rule.outcome === 'per_kg' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                          : rule.outcome === 'flat_shipment' ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'
                                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                      }`} title={rule.outcome === 'flat_shipment' ? 'Once per consignment on this carrier, however many parcels or pallets' : undefined}>
                                        {rule.outcome === 'refused' ? 'Refused' : rule.outcome === 'per_kg' ? '€/kg' : rule.outcome === 'flat_shipment' ? 'Flat / shipment' : 'Flat'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-xs font-mono">{rule.outcome === 'refused' ? '—' : `€${rule.amount}`}</td>
                                    <td className="px-3 py-1.5 text-xs">
                                      {rule.country_codes
                                        ? rule.country_codes.split(',').map(c => c.trim()).filter(Boolean).map((code, i) => (
                                            <span key={i} className="inline-flex items-center gap-0.5 mr-1.5">
                                              {getCountryFlag(code)} {code}
                                            </span>
                                          ))
                                        : <span className="text-muted-foreground italic">All</span>
                                      }
                                    </td>
                                    <td className="px-3 py-1.5 text-xs text-muted-foreground">
                                      {rule.label}
                                      {rule.outcome === 'per_kg' && rule.weight_basis === 'billable' && (
                                        <span className="ml-1.5 inline-flex items-center rounded bg-blue-100 dark:bg-blue-900/30 px-1 py-0 text-[9px] text-blue-700 dark:text-blue-300" title="Per-kg on billable weight = max(real, volumetric)">
                                          billable
                                        </span>
                                      )}
                                      {rule.window_status && (
                                        <span
                                          title={`Active window: ${rule.valid_from || '—'} to ${rule.valid_until || '—'}`}
                                          className={`ml-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0 text-[9px] font-semibold ${
                                            rule.window_status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : rule.window_status === 'scheduled' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                            : 'bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                          }`}>
                                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                          {rule.window_status}
                                          <span className="font-mono opacity-70">
                                            {rule.valid_from ? rule.valid_from.slice(5) : '…'}→{rule.valid_until ? rule.valid_until.slice(5) : '…'}
                                          </span>
                                        </span>
                                      )}
                                      {(() => {
                                        const a = rule.applies_to || 'parcel';
                                        const meta = {
                                          parcel: ['parcel', 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', 'Evaluated for PARCELS only'],
                                          pallet: ['pallet', 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', 'Evaluated for PALLETS only'],
                                          both:   ['both',   'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300', 'Evaluated for parcels AND pallets'],
                                        }[a] || ['?', 'bg-muted text-muted-foreground', a];
                                        return (
                                          <span className={`ml-1.5 inline-flex items-center rounded px-1 py-0 text-[9px] font-semibold ${meta[1]}`} title={meta[2]}>
                                            {meta[0]}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <div className="flex items-center gap-0.5">
                                        <button className="rounded p-1 hover:bg-accent" onClick={() => { setEditingSurcharge(rule); setSurchargeDialogOpen(true); }}>
                                          <Pencil className="h-3 w-3" />
                                        </button>
                                        <button className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600" onClick={() => handleSurchargeDelete(rule.id)}>
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Country Edit Modal */}
      {/* Edit carrier assignment modal */}
      <Modal
        open={editRateModalOpen}
        onClose={() => setEditRateModalOpen(false)}
        title="Edit carrier assignment"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditRateModalOpen(false)}>Cancel</Button>
            <Button onClick={confirmEditRate} disabled={!editRateCarrierId || (editRateRow && parseInt(editRateCarrierId) === editRateRow.carrier_id)}>Save</Button>
          </>
        }
      >
        {editRateRow && (
          <>
            <div>
              <label className="text-sm font-medium">Country</label>
              <p className="mt-1 text-sm">{getCountryFlag(editRateRow.iso2_code)} {editRateRow.country_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Rate type</label>
              <p className="mt-1 text-sm capitalize">{editRateRow.rateType}</p>
            </div>
            <div>
              <label className="text-sm font-medium">Carrier</label>
              <select
                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={editRateCarrierId}
                onChange={(e) => setEditRateCarrierId(e.target.value)}
              >
                <option value="">Select a carrier...</option>
                {carriers.filter(c => c.is_active).map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.service ? ` — ${c.service}` : ''}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </Modal>

      {/* Add carrier rates modal */}
      <Modal
        open={addRateModalOpen}
        onClose={() => setAddRateModalOpen(false)}
        title={`Add ${addRateType} rates`}
        footer={
          <>
            <Button variant="outline" onClick={() => setAddRateModalOpen(false)}>Cancel</Button>
            <Button onClick={confirmAddRate} disabled={!addRateCountryId || !addRateCarrierId}>Add</Button>
          </>
        }
      >
        <div>
          <label className="text-sm font-medium">Country</label>
          <select
            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={addRateCountryId}
            onChange={(e) => setAddRateCountryId(e.target.value)}
          >
            <option value="">Select a country...</option>
            {countries.filter(c => c.is_active).sort((a, b) => a.name.localeCompare(b.name)).map(c => (
              <option key={c.id} value={c.id}>{getCountryFlag(c.iso2_code)} {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Carrier</label>
          <select
            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={addRateCarrierId}
            onChange={(e) => setAddRateCarrierId(e.target.value)}
          >
            <option value="">Select a carrier...</option>
            {carriers.filter(c => c.is_active).map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.service ? ` — ${c.service}` : ''}</option>
            ))}
          </select>
        </div>
        {addRateCountryId && addRateCarrierId && (() => {
          const rateMap = { weight: weightRates, volumetric: volumetricRates, coli: coliRates, pallet: palletRates };
          const existing = (rateMap[addRateType] || []).find(cr => cr.country_id === parseInt(addRateCountryId) && cr.carrier_id === parseInt(addRateCarrierId));
          if (existing) return <p className="text-xs text-amber-600 font-medium">This country + carrier combination already has {addRateType} rates.</p>;
          return null;
        })()}
      </Modal>

      <Modal
        open={countryDialogOpen}
        onClose={() => setCountryDialogOpen(false)}
        title={editingCountry?.id ? 'Edit Country' : 'Add Country'}
        footer={
          <>
            <Button variant="outline" onClick={() => setCountryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCountrySave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div>
          <Label>ISO2 Code</Label>
          <Input
            value={editingCountry?.iso2_code || ''}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, iso2_code: e.target.value.toUpperCase() } : null)}
            maxLength={2}
            placeholder="e.g. BE, NL, US"
          />
        </div>
        <div>
          <Label>Country Name</Label>
          <Input
            value={editingCountry?.name || ''}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, name: e.target.value } : null)}
          />
        </div>
        <div>
          <Label>VAT Rate (decimal, e.g. 0.21 = 21%)</Label>
          <Input
            type="number"
            step="0.01"
            value={editingCountry?.vat_rate || 0}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, vat_rate: parseFloat(e.target.value) || 0 } : null)}
          />
        </div>
        <div>
          <Label>Franco B2B (free shipping)</Label>
          <Input
            type="number"
            value={editingCountry?.franco_threshold || ''}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, franco_threshold: parseFloat(e.target.value) || null } : null)}
            placeholder="Leave empty for none"
          />
        </div>
        <div>
          <Label>Franco B2C (free shipping)</Label>
          <Input
            type="number"
            value={editingCountry?.franco_b2c || ''}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, franco_b2c: parseFloat(e.target.value) || null } : null)}
            placeholder="Leave empty for none"
          />
        </div>
        <div>
          <Label>Rate Type</Label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={editingCountry?.rate_type || 'weight'}
            onChange={(e) => setEditingCountry(prev => prev ? { ...prev, rate_type: e.target.value } : null)}
          >
            <option value="weight">Weight-based (EU)</option>
            <option value="volumetric">Volumetric</option>
            <option value="coli">Per-Parcel</option>
          </select>
        </div>
        {editingCountry?.rate_type === 'coli' && (
          <div>
            <Label>Per Additional Package Cost</Label>
            <Input
              type="number"
              step="0.01"
              value={editingCountry?.coli_additional_cost || ''}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, coli_additional_cost: parseFloat(e.target.value) || null } : null)}
              placeholder="e.g. 5.24"
            />
          </div>
        )}
        {editingCountry?.rate_type === 'volumetric' && (
          <div>
            <Label>Per Additional kg Cost</Label>
            <Input
              type="number"
              step="0.01"
              value={editingCountry?.vol_per_additional_kg || ''}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, vol_per_additional_kg: parseFloat(e.target.value) || null } : null)}
              placeholder="For extrapolation beyond max tier"
            />
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingCountry?.is_eu || false}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, is_eu: e.target.checked } : null)}
              className="rounded border-border"
            />
            EU Country
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingCountry?.charge_vat_b2c ?? true}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, charge_vat_b2c: e.target.checked } : null)}
              className="rounded border-border"
            />
            Charge VAT B2C
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingCountry?.charge_vat_b2b || false}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, charge_vat_b2b: e.target.checked } : null)}
              className="rounded border-border"
            />
            Charge VAT B2B
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingCountry?.is_active || false}
              onChange={(e) => setEditingCountry(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
              className="rounded border-border"
            />
            Active
          </label>
        </div>
      </Modal>

      {/* Country rate analysis modal — line charts of rates per carrier */}
      {(() => {
        const country = countries.find(c => c.id === countryDetailId);
        const RT_META = {
          weight:     { label: 'Real weight', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
          volumetric: { label: 'Volumetric',  cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
          coli:       { label: 'Per parcel',  cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
        };
        const buildSeries = (source, cid) => (source || [])
          .filter(r => r.country_id === cid && r.carrier_id != null)
          .map(r => ({
            carrierId: r.carrier_id,
            label: r.carrier_name || `Carrier ${r.carrier_id}`,
            color: colorForCarrier(r.carrier_id),
            data: Object.entries(r.rates || {})
              .map(([k, v]) => ({ x: Number(k), y: Number(v.cost) }))
              .filter(d => Number.isFinite(d.x) && Number.isFinite(d.y) && d.y > 0)
              .sort((a, b) => a.x - b.x),
          }))
          .filter(s => s.data.length)
          .sort((a, b) => a.label.localeCompare(b.label));
        const block = (rt, source, xUnit) => {
          const series = country ? buildSeries(source, country.id) : [];
          const m = RT_META[rt];
          return (
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>{m.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {series.length === 0 ? 'no rates' : `${series.length} carrier${series.length === 1 ? '' : 's'}`}
                  </span>
                </div>
                {series.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    {series.map(s => (
                      <span key={s.carrierId} className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-muted-foreground">{s.label}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <RateLineChart series={series} xUnit={xUnit} />
            </div>
          );
        };
        return (
          <Modal
            open={countryDetailId !== null}
            onClose={() => setCountryDetailId(null)}
            size="5xl"
            title={country
              ? <span className="inline-flex items-center gap-2">
                  <span>{getCountryFlag(country.iso2_code)}</span>
                  <span>{country.name}</span>
                  <span className="text-sm font-normal text-muted-foreground">({country.iso2_code})</span>
                  <span className="text-xs font-normal text-muted-foreground">— coverage &amp; rates</span>
                </span>
              : 'Rate analysis'}
          >
            <div className="space-y-3">
              {country && (
                <div className="rounded-lg border border-border p-4">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Checkout coverage &amp; franco</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {[
                      ['Carrier service', country.carrier_service, 'Live on the Shippy carrier service at checkout'],
                      ['Pickup Point', country.pickup, 'GLS ParcelShop (Pickup Point) offered at checkout'],
                      ['Parcel Locker', country.locker, 'GLS Parcel Locker offered at checkout'],
                      ['Multi-carrier', country.multi_carrier, 'Extra "via <carrier>" alternative rates at checkout'],
                      ['Split shipment', country.partial_shipping, "Ship-what's-in-stock offered"],
                    ].map(([label, on, tip]) => (
                      <span key={label} title={tip}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${on
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                        {on ? '✓' : '✗'} {label}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      ['Franco B2B', country.franco_threshold ? `€${country.franco_threshold}` : '—'],
                      ['Franco B2C', country.franco_b2c ? `€${country.franco_b2c}` : '—'],
                      ['Franco gift', country.franco_gift != null ? `€${country.franco_gift}` : '—'],
                      ['VAT', `${(country.vat_rate * 100).toFixed(0)}% · B2C ${country.charge_vat_b2c ? '✓' : '✗'}`],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div className="text-[11px] text-muted-foreground">{label}</div>
                        <div className="text-sm font-semibold tabular-nums">{value}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Checkout features are config-driven (read-only here). Franco gift = partial-franco discount absorbed on franco orders, applied only when shipping ≤ 10% of order value.
                  </p>
                </div>
              )}
              {block('weight', weightRates, 'kg')}
              {block('volumetric', volumetricRates, 'kg')}
              {block('coli', coliRates, 'pkg')}
              <p className="text-[11px] text-muted-foreground text-center">Hover a point to see the exact rate. Each line is one carrier. Pallet rates are shown in the Pallet tab.</p>
            </div>
          </Modal>
        );
      })()}

      {/* Bin Edit Modal */}
      <Modal
        open={binDialogOpen}
        onClose={() => setBinDialogOpen(false)}
        title={editingBin?.id ? 'Edit Bin' : 'Add Bin'}
        footer={
          <>
            <Button variant="outline" onClick={() => setBinDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBinSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div>
          <Label>Bin Name</Label>
          <Input
            value={editingBin?.name || ''}
            onChange={(e) => setEditingBin(prev => prev ? { ...prev, name: e.target.value } : null)}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>Width (cm)</Label>
            <Input type="number" value={editingBin?.width || 0} onChange={(e) => setEditingBin(prev => prev ? { ...prev, width: parseFloat(e.target.value) || 0 } : null)} />
          </div>
          <div>
            <Label>Height (cm)</Label>
            <Input type="number" value={editingBin?.height || 0} onChange={(e) => setEditingBin(prev => prev ? { ...prev, height: parseFloat(e.target.value) || 0 } : null)} />
          </div>
          <div>
            <Label>Depth (cm)</Label>
            <Input type="number" value={editingBin?.depth || 0} onChange={(e) => setEditingBin(prev => prev ? { ...prev, depth: parseFloat(e.target.value) || 0 } : null)} />
          </div>
        </div>
        <div>
          <Label>Max Weight (kg)</Label>
          <Input type="number" value={editingBin?.max_weight || 30} onChange={(e) => setEditingBin(prev => prev ? { ...prev, max_weight: parseFloat(e.target.value) || 30 } : null)} />
        </div>
        <div>
          <Label>Packaging Cost</Label>
          <Input type="number" step="0.01" value={editingBin?.packaging_cost || 0} onChange={(e) => setEditingBin(prev => prev ? { ...prev, packaging_cost: parseFloat(e.target.value) || 0 } : null)} />
        </div>
        <div>
          <Label>Sort Order</Label>
          <Input type="number" value={editingBin?.sort_order || 0} onChange={(e) => setEditingBin(prev => prev ? { ...prev, sort_order: parseInt(e.target.value) || 0 } : null)} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editingBin?.is_active || false}
            onChange={(e) => setEditingBin(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
            className="rounded border-border"
          />
          Active
        </label>
      </Modal>

      {/* Zone Edit Modal */}
      <Modal
        open={zoneDialogOpen}
        onClose={() => setZoneDialogOpen(false)}
        title={editingZone?.id ? 'Edit Zone' : 'Add Zone'}
        footer={
          <>
            <Button variant="outline" onClick={() => setZoneDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleZoneSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div>
          <Label>Zone Name</Label>
          <Input
            value={editingZone?.name || ''}
            onChange={(e) => setEditingZone(prev => prev ? { ...prev, name: e.target.value } : null)}
            placeholder="e.g. Balearic Islands, Azores, Sicily"
          />
        </div>
        <div>
          <Label>Country</Label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={editingZone?.country_id || ''}
            onChange={(e) => setEditingZone(prev => prev ? { ...prev, country_id: Number(e.target.value) } : null)}
          >
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {getCountryFlag(country.iso2_code)} {country.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Postal Code Patterns</Label>
          <Input
            value={editingZone?.postal_code_patterns || ''}
            onChange={(e) => setEditingZone(prev => prev ? { ...prev, postal_code_patterns: e.target.value } : null)}
            placeholder="Comma-separated prefixes (e.g. 07,35,38)"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>VAT Rate</Label>
            <Input
              type="number"
              step="0.01"
              value={editingZone?.vat_rate ?? ''}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, vat_rate: e.target.value ? parseFloat(e.target.value) : null } : null)}
              placeholder="Use country rate"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Franco B2B</Label>
            <Input
              type="number"
              value={editingZone?.franco_threshold ?? ''}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, franco_threshold: e.target.value ? parseFloat(e.target.value) : null } : null)}
              placeholder="Use country"
            />
          </div>
          <div>
            <Label>Franco B2C</Label>
            <Input
              type="number"
              value={editingZone?.franco_b2c ?? ''}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, franco_b2c: e.target.value ? parseFloat(e.target.value) : null } : null)}
              placeholder="Use country"
            />
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <textarea
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={editingZone?.notes || ''}
            onChange={(e) => setEditingZone(prev => prev ? { ...prev, notes: e.target.value } : null)}
            placeholder="Internal notes about this zone"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingZone?.is_eu ?? true}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, is_eu: e.target.checked } : null)}
              className="rounded border-border"
            />
            {editingZone?.is_eu ? 'EU (Weight-based rates)' : 'Non-EU (Volumetric rates)'}
          </label>
          <label className="flex items-center gap-2 text-sm" title="On: this zone's rows override only the carriers they list; the country's other carriers still serve these postal codes at their country rates (France departement tiers). Off: self-contained zone that replaces the country rates entirely (islands: a mainland rate never falls through).">
            <input
              type="checkbox"
              checked={editingZone?.inherit_country_carriers || false}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, inherit_country_carriers: e.target.checked } : null)}
              className="rounded border-border"
            />
            {editingZone?.inherit_country_carriers ? 'Override (country carriers still apply)' : 'Self-contained (replaces country rates)'}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editingZone?.is_active || false}
              onChange={(e) => setEditingZone(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
              className="rounded border-border"
            />
            Active
          </label>
        </div>
      </Modal>

      {/* Carrier Edit Modal */}
      <Modal
        open={carrierDialogOpen}
        onClose={() => setCarrierDialogOpen(false)}
        title={editingCarrier?.id ? 'Edit Carrier' : 'Add Carrier'}
        footer={
          <>
            <Button variant="outline" onClick={() => setCarrierDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCarrierSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div>
          <Label>Carrier Name</Label>
          <Input
            value={editingCarrier?.name || ''}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, name: e.target.value } : null)}
            placeholder="e.g. bpost, DPD, GLS, PostNL"
          />
        </div>
        <div>
          <Label>Service</Label>
          <Input
            value={editingCarrier?.service || ''}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, service: e.target.value } : null)}
            placeholder="e.g. Parcel, Express, Standard (optional)"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editingCarrier?.is_active || false}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
            className="rounded border-border"
          />
          Active
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editingCarrier?.fuel_on_surcharges || false}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, fuel_on_surcharges: e.target.checked } : null)}
            className="rounded border-border"
          />
          <span>
            Fuel % on surcharges
            <span className="block text-xs text-muted-foreground">This carrier bills its fuel surcharge on top of handling/oversize surcharges too (UPS-style), not only on the base rate.</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="number" step="0.01" min="0" max="20"
            value={editingCarrier?.green_surcharge_pct !== undefined && editingCarrier?.green_surcharge_pct !== null ? +(editingCarrier.green_surcharge_pct * 100).toFixed(2) : 0}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, green_surcharge_pct: (parseFloat(e.target.value) || 0) / 100 } : null)}
            className="w-20 rounded border border-border bg-background px-2 py-1 text-sm"
          />
          <span>
            Green surcharge % of the transport charge
            <span className="block text-xs text-muted-foreground">Added after fuel and not fuel-surcharged: DHL Express GoGreen Plus (carbon reduced), about 1.7% on the Express Worldwide air products. 0 for every other carrier.</span>
          </span>
        </label>
        <div>
          <Label>Odoo mapping name</Label>
          <Input
            value={editingCarrier?.odoo_mapping || ''}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, odoo_mapping: e.target.value } : null)}
            placeholder="e.g. GLS FlexDelivery"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">Odoo delivery method name, exactly as shown in Odoo (e.g. GLS FlexDelivery). Leave blank if not mapped.</p>
        </div>
        <div>
          <Label>Odoo mapping name for pickups</Label>
          <Input
            value={editingCarrier?.odoo_mapping_pickup || ''}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, odoo_mapping_pickup: e.target.value } : null)}
            placeholder="e.g. GLS ShopDelivery"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">Odoo delivery method for a pickup-point delivery on this carrier&rsquo;s network. Only matters for carriers with pickup rates (GLS, bpost). A country can override it on the Pickup tab (Belgium: GLS Belgium ShopDelivery). Leave blank if not mapped.</p>
        </div>
        <div>
          <Label>Pallet minimum chargeable weight (kg)</Label>
          <Input
            type="number"
            value={editingCarrier?.pallet_min_chargeable_kg ?? ''}
            onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, pallet_min_chargeable_kg: e.target.value === '' ? null : e.target.value } : null)}
            placeholder="e.g. 250"
          />
          <p className="mt-1 text-xs text-muted-foreground">A pallet lighter than this (real and volumetric) is billed at this weight on the pallet card. DHL EuroPlus: 250. Leave blank for no minimum.</p>
        </div>
        <div>
          <Label>Largest pallet taken (cm)</Label>
          <div className="grid grid-cols-3 gap-2">
            {[
              ['pallet_max_base_length_cm', 'Base length', '120'],
              ['pallet_max_base_width_cm', 'Base width', '100'],
              ['pallet_max_height_cm', 'Height', '220'],
            ].map(([field, label, ph]) => (
              <div key={field}>
                <Input
                  type="number"
                  value={editingCarrier?.[field] ?? ''}
                  onChange={(e) => setEditingCarrier(prev => prev ? { ...prev, [field]: e.target.value === '' ? null : e.target.value } : null)}
                  placeholder={`e.g. ${ph}`}
                />
                <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">A pallet with a bigger base (either way round) or a taller load is refused on every format of this carrier, at checkout, in the simulator and on the B2B endpoint. GLS BusinessFreight: 120 &times; 100, height 220. Leave blank for no limit.</p>
        </div>
      </Modal>

      {/* Surcharge Rule Edit Modal */}
      <Modal
        open={surchargeDialogOpen}
        onClose={() => setSurchargeDialogOpen(false)}
        title={editingSurcharge?.id ? 'Edit Surcharge Rule' : 'Add Surcharge Rule'}
        footer={
          <>
            <Button variant="outline" onClick={() => setSurchargeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSurchargeSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div>
          <Label>Carrier</Label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={editingSurcharge?.carrier_id || ''}
            onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, carrier_id: parseInt(e.target.value) } : null)}
          >
            <option value="">Select carrier...</option>
            {carriers.filter(c => c.is_active).map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.service ? ` — ${c.service}` : ''}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Condition</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={editingSurcharge?.condition || 'longest_side'}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, condition: e.target.value } : null)}
            >
              {CONDITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className={editingSurcharge?.condition === 'always' ? 'opacity-40' : ''}>Operator</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={editingSurcharge?.operator || 'gt'}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, operator: e.target.value } : null)}
            >
              {OPERATOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={editingSurcharge?.condition === 'always' ? 'opacity-40' : ''}>Threshold</Label>
            <Input
              type="number"
              step="0.1"
              value={editingSurcharge?.threshold ?? 0}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, threshold: parseFloat(e.target.value) || 0 } : null)}
            />
          </div>
          <div>
            <Label>Outcome</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={editingSurcharge?.outcome || 'flat'}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, outcome: e.target.value } : null)}
            >
              {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        {editingSurcharge?.outcome !== 'refused' && (
          <div>
            <Label>Amount (€{editingSurcharge?.outcome === 'per_kg' ? '/kg' : editingSurcharge?.outcome === 'flat_shipment' ? ' per shipment' : ''})</Label>
            <Input
              type="number"
              step="0.01"
              value={editingSurcharge?.amount ?? 0}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, amount: parseFloat(e.target.value) || 0 } : null)}
            />
          </div>
        )}
        {editingSurcharge?.outcome === 'per_kg' && (
          <div>
            <Label>Per-kg weight basis</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={editingSurcharge?.weight_basis || 'real'}
              onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, weight_basis: e.target.value } : null)}
            >
              <option value="real">Real weight</option>
              <option value="billable">Billable — max(real, volumetric)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Carrier demand/peak surcharges bill chargeable weight — use billable.</p>
          </div>
        )}
        <div>
          <Label>Surcharge Group</Label>
          <Input
            value={editingSurcharge?.surcharge_group || ''}
            onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, surcharge_group: e.target.value } : null)}
            placeholder="e.g. oversize, weight_limit, girth"
          />
          <p className="text-xs text-muted-foreground mt-1">Rules in the same group use OR logic. Different groups stack.</p>
        </div>
        <div>
          <Label>Why / source</Label>
          <textarea
            className="flex w-full min-h-[64px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={editingSurcharge?.note || ''}
            onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, note: e.target.value } : null)}
            placeholder="Invoice number, carrier notice, who decided and when. Shown on the Pricing Rules page."
          />
        </div>
        <div>
          <Label>Countries (ISO2, comma-separated)</Label>
          <Input
            value={editingSurcharge?.country_codes || ''}
            onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, country_codes: e.target.value.toUpperCase() } : null)}
            placeholder="e.g. FR,BE — leave empty for all"
          />
        </div>
        <div>
          <Label>Label</Label>
          <Input
            value={editingSurcharge?.label || ''}
            onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, label: e.target.value } : null)}
            placeholder="e.g. L>120cm, >30kg"
          />
        </div>
        <div>
          <Label>Temporary window <span className="font-normal text-muted-foreground">(optional — for peak / demand surcharges)</span></Label>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div>
              <span className="text-[11px] text-muted-foreground block mb-0.5">Valid from</span>
              <Input
                type="date"
                value={editingSurcharge?.valid_from || ''}
                onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, valid_from: e.target.value || null } : null)}
              />
            </div>
            <div>
              <span className="text-[11px] text-muted-foreground block mb-0.5">Valid until</span>
              <Input
                type="date"
                value={editingSurcharge?.valid_until || ''}
                onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, valid_until: e.target.value || null } : null)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Leave empty for always-on. Set dates for a temporary surcharge that auto-activates and expires (e.g. DHL demand 1 Oct → 5 Feb) — the rule only applies while today is in the window.</p>
        </div>
        <div className="pt-1">
          <Label className="text-sm">Applies to</Label>
          <div className="inline-flex rounded-lg border border-border overflow-hidden mt-1.5">
            {[['parcel', 'Parcels only'], ['pallet', 'Pallets only'], ['both', 'Both']].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setEditingSurcharge(prev => prev ? { ...prev, applies_to: v } : null)}
                className={`px-3 py-1.5 text-xs font-medium ${(editingSurcharge?.applies_to || 'parcel') === v ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Most rules belong to one side. A parcel network rule (girth cap, the 31.5 kg per-parcel limit) would refuse
            every pallet ever built, and a pallet piece charge means nothing on a parcel. Pick <strong>Both</strong> only
            when the carrier really bills it on either.
          </p>
        </div>
        <label className="flex items-start gap-2 pt-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!editingSurcharge?.fuel_exempt}
                 onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, fuel_exempt: e.target.checked } : null)} />
          <span>Not fuelled
            <span className="block text-xs text-muted-foreground">Stays a flat amount even when this carrier grosses its surcharges with fuel (UPS). The UPS international processing fee (EUR 2.35, US) is billed that way; residential and additional handling are fuelled.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 pt-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!editingSurcharge?.additive}
                 onChange={(e) => setEditingSurcharge(prev => prev ? { ...prev, additive: e.target.checked } : null)} />
          <span>Additive to the generic rules
            <span className="block text-xs text-muted-foreground">A rule with countries normally replaces the carrier's country-less rules in those countries. Tick this for a fee that comes on top of them (UPS US processing fee), so residential, demand and size rules keep applying.</span>
          </span>
        </label>
      </Modal>

      <PalletLaneCoveragePanel laneKey={palletLaneKey} onClose={() => setPalletLaneKey(null)}
                               Modal={Modal} RateLineChart={RateLineChart} />
    </div>
  );
};

export default ShippingRates;
